import {
  FormEvent,
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  type User,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import oprimeLogoUrl from "../../web/oprime-favicon-512.png";
import {
  Navigate,
  NavLink,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import {
  Activity,
  ArrowLeft,
  ArrowDownToLine,
  Ban,
  BarChart3,
  CircleAlert,
  CircleDollarSign,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Copy,
  Database,
  FileDown,
  FileText,
  Filter,
  Eye,
  Gift,
  HeartPulse,
  Image,
  LayoutDashboard,
  Link2,
  Loader2,
  LogOut,
  Mail,
  Megaphone,
  Menu,
  MessageSquare,
  Moon,
  Network,
  Percent,
  Pencil,
  PauseCircle,
  PlayCircle,
  Radio,
  Send,
  ReceiptText,
  RefreshCw,
  RotateCw,
  Search,
  Settings2,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  SortAsc,
  Smartphone,
  Sun,
  Target,
  UserCog,
  UserPlus,
  Users,
  UploadCloud,
  WalletCards,
  Zap,
  X,
  XCircle,
} from "lucide-react";
import { auth, db, functions, storage } from "./firebase";
import type { AdminUser, DashboardData, Transaction } from "./types";
import airtelLogo from "../../assets/airtel.png";
import gloLogo from "../../assets/glo.png";
import mtnLogo from "../../assets/mtn.png";
import nineMobileLogo from "../../assets/t2.jpg";
import abaLogo from "../../assets/aba.png";
import ekoLogo from "../../assets/eko.png";
import ikejaLogo from "../../assets/ikeja.png";

const nav = [
  {
    path: "/overview",
    key: "overview",
    label: "Overview",
    icon: LayoutDashboard,
  },
  {
    path: "/transactions",
    key: "transactions",
    label: "Transactions",
    icon: ReceiptText,
  },
  {
    path: "/pending",
    key: "pendingTransactions",
    label: "Pending Transactions",
    icon: Clock3,
  },
  { path: "/deposits", key: "deposits", label: "Deposits", icon: WalletCards },
  { path: "/watchlist", key: "watchlist", label: "Watchlist", icon: Ban },
  { path: "/users", key: "users", label: "Users", icon: Users },
  { path: "/services", key: "services", label: "Services", icon: Settings2 },
  {
    path: "/data-plans",
    key: "dataPlans",
    label: "Data Plans",
    icon: Smartphone,
  },
  { path: "/funding", key: "funding", label: "Funding", icon: Database },
  { path: "/kyc", key: "kyc", label: "KYC Review", icon: ShieldCheck },
  { path: "/aml", key: "aml", label: "AML Review", icon: Shield },
  { path: "/disputes", key: "disputes", label: "Disputes", icon: Mail },
  { path: "/broadcasts", key: "broadcasts", label: "Broadcasts", icon: Radio },
  { path: "/cashback", key: "cashback", label: "Cashback", icon: Percent },
  { path: "/referrals", key: "referrals", label: "Referrals", icon: UserPlus },
  { path: "/promos", key: "promos", label: "Promo Codes", icon: Gift },
  { path: "/banners", key: "banners", label: "Home Banners", icon: Image },
  { path: "/campaigns", key: "campaigns", label: "Campaigns", icon: Network },
  { path: "/activity", key: "activity", label: "Activity", icon: Activity },
  { path: "/reports", key: "reports", label: "Reports", icon: FileDown },
  { path: "/health", key: "health", label: "System Health", icon: HeartPulse },
  {
    path: "/admins",
    key: "admins",
    label: "Admins",
    icon: UserCog,
    adminOnly: true,
  },
];

const FLUSHBAR_EVENT = "oprime-admin-flushbar";
const CONFIRM_EVENT = "oprime-admin-confirm";

function adminActionProgressLabel(
  name: string,
  payload: Record<string, unknown>,
) {
  if (name === "adminSetUserSuspended") {
    return payload.suspended ? "Suspending user…" : "Restoring user access…";
  }
  if (name === "adminSetUserWatchlisted") {
    return payload.watchlisted
      ? "Adding user to watchlist…"
      : "Removing user from watchlist…";
  }
  if (name === "adminSendBroadcast") return "Sending notification…";
  if (name === "adminAdjustUserWallet") return "Updating wallet…";
  return "Processing admin action…";
}

const AdminPermissionContext = createContext<{
  canAdjustWallet: boolean;
  canViewTransactionProviders: boolean;
  adminDirectory: Record<string, unknown>[];
}>({ canAdjustWallet: false, canViewTransactionProviders: false, adminDirectory: [] });

type ConfirmOptions = {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "danger" | "warning" | "primary";
};

function confirmAction(options: ConfirmOptions) {
  return new Promise<boolean>((resolve) => {
    window.dispatchEvent(new CustomEvent(CONFIRM_EVENT, { detail: { ...options, resolve } }));
  });
}

function showFlushbar(message: string, error = false) {
  window.dispatchEvent(
    new CustomEvent(FLUSHBAR_EVENT, { detail: { message, error } }),
  );
}

async function copyToClipboard(value: string, label: string) {
  if (!value.trim()) return;
  try {
    await navigator.clipboard.writeText(value);
    showFlushbar(`${label} copied`);
  } catch {
    showFlushbar(`Could not copy ${label.toLowerCase()}`, true);
  }
}

const money = new Intl.NumberFormat("en-NG", {
  style: "currency",
  currency: "NGN",
});
const date = new Intl.DateTimeFormat("en-NG", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});
const depositDate = new Intl.DateTimeFormat("en-NG", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});
const naira = (kobo = 0) => money.format(kobo / 100);
const personName = (value?: string) => {
  const name = String(value || "").trim().toLocaleLowerCase("en-NG");
  if (!name) return "—";
  return name.replace(/(^|[\s'-])\p{L}/gu, (letter) =>
    letter.toLocaleUpperCase("en-NG"),
  );
};

const depositBankName = (value?: string) => {
  const bank = String(value || "").trim().replace(/_/g, " ");
  if (!bank) return "—";
  return bank.toLocaleLowerCase("en-NG").replace(/(^|\s)\p{L}/gu, (letter) =>
    letter.toLocaleUpperCase("en-NG"),
  );
};

const depositSourceName = (tx: Transaction): string => {
  const direct = String(tx.senderBank || "").trim();
  if (direct) return direct;
  const providerResponse = tx.providerResponse as Record<string, unknown> | undefined;
  const sender = providerResponse?.sender as Record<string, unknown> | undefined;
  const senderBank = String(sender?.bank || sender?.bankName || sender?.bank_name || "").trim();
  if (senderBank) return senderBank;
  const wanted = new Set(["senderbank", "senderbankname", "sourcebank", "originatorbank", "originatingbank"]);
  const seen = new Set<unknown>();
  const findBank = (value: unknown): string => {
    if (!value || typeof value !== "object" || seen.has(value)) return "";
    seen.add(value);
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
      if (wanted.has(key.replace(/[^a-z0-9]/gi, "").toLowerCase()) && typeof child === "string" && child.trim()) {
        return child.trim();
      }
    }
    for (const child of Object.values(value as Record<string, unknown>)) {
      const found = findBank(child);
      if (found) return found;
    }
    return "";
  };
  return findBank(tx.providerResponse) || String(tx.bankName || tx.provider || tx.channel || "").trim();
};

const depositNaira = (value?: number | null) => Number(value ?? 0) / 100;

const formatDate = (val?: any): string => {
  if (!val) return "—";
  let t = 0;
  if (typeof val === "number") t = val;
  else if (typeof val === "string") t = Date.parse(val) || 0;
  else if (typeof val === "object") {
    if (typeof val.toMillis === "function") t = val.toMillis();
    else if (typeof val.seconds === "number") t = val.seconds * 1000;
  }
  if (!t) return "—";
  const d = new Date(t);
  const dayStr = d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  const timeStr = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  return `${dayStr} • ${timeStr}`;
};

const isWalletAdjustmentTransaction = (tx: Transaction) => {
  const type = String(tx.type || "").trim().toLowerCase();
  const title = String(tx.title || "").trim().toLowerCase();
  const action = String(tx.adjustmentAction || "").trim().toLowerCase();
  return (
    [
      "wallet_adjustment",
      "admin_wallet_adjustment",
      "admin_credit",
      "admin_debit",
      "admin_wallet_credit",
      "admin_wallet_debit",
    ].includes(type) ||
    title.includes("admin wallet credit") ||
    title.includes("admin wallet debit") ||
    (Boolean(tx.adminUid || tx.adminEmail || tx.adjustmentId) &&
      ["credit", "debit", "wallet_credit", "wallet_debit"].includes(action))
  );
};

const isDepositTransaction = (tx: Transaction) => {
  if (isWalletAdjustmentTransaction(tx)) return false;
  const type = String(tx.type || "").trim().toLowerCase();
  const title = String(tx.title || "").trim().toLowerCase();
  return (
    type === "fund" ||
    type === "funding" ||
    type === "deposit" ||
    type === "wallet_funding" ||
    title === "wallet funding" ||
    title.includes("account deposit") ||
    title.includes("opay deposit") ||
    title.includes("deposit")
  );
};

const decodedAvatarUrls = new Set<string>();

function suggestedAvatarId(name?: string) {
  const first = String(name || "").trim().toLowerCase().split(/\s+/)[0] || "";
  const female = new Set(["ada", "amara", "amaka", "blessing", "chioma", "chidinma", "esther", "favour", "faith", "grace", "ifeoma", "joy", "mercy", "ngozi", "precious", "queen", "rita", "ruth", "victoria", "zainab", "aisha", "mary", "jennifer", "angelina", "delicia", "oma", "temilade", "tolulope", "yetunde"]);
  const male = new Set(["ayomide", "chinedu", "chukwuemeka", "daniel", "david", "dominic", "emmanuel", "ernest", "francis", "james", "john", "joseph", "kelvin", "michael", "nathan", "olamide", "olumide", "paul", "peter", "samuel", "segun", "solomon", "stephen", "tunde", "victor", "wisdom"]);
  const isFemale = female.has(first) || (!male.has(first) && (first.endsWith("a") || first.endsWith("e")));
  const pool = isFemale ? ["f1", "f2", "f3", "f4"] : ["m1", "m2", "m3", "m4"];
  const hash = Array.from(String(name || "User")).reduce((sum, char) => sum + (char.codePointAt(0) || 0), 0);
  return pool[hash % pool.length];
}

function userAvatarSrc(photoUrl?: string, avatarId?: string, name?: string) {
  const photo = String(photoUrl || "").trim();
  if (photo) return photo;
  const safeId = /^(m|f)[1-4]$/.test(String(avatarId || ""))
    ? String(avatarId)
    : suggestedAvatarId(name);
  return `/avatars/avatar_${safeId}.png`;
}

function normalizedPhone(value?: string) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("234") && digits.length === 13) return `0${digits.slice(3)}`;
  return digits;
}

function buildUserMap(users?: AdminUser[]) {
  const map = new Map<string, AdminUser>();
  const add = (key: unknown, user: AdminUser) => {
    const value = String(key || "").trim();
    if (value) map.set(value.toLowerCase(), user);
  };
  (users || []).forEach((user) => {
    add(user.uid, user);
    add(user.userId, user);
    add(user.customerNo, user);
    add(user.email, user);
    add(user.phone, user);
    add(normalizedPhone(user.phone), user);
  });
  return map;
}

function currentUserForTransaction(userMap: Map<string, AdminUser>, row: Transaction) {
  const keys = [
    row.uid,
    row.userId,
    row.userEmail,
    row.userPhone,
    normalizedPhone(row.userPhone),
  ];
  for (const key of keys) {
    const value = String(key || "").trim().toLowerCase();
    if (value && userMap.has(value)) return userMap.get(value);
  }
  return undefined;
}

function LoadedAvatar({ src, alt = "", fallback }: { src?: string; alt?: string; fallback: ReactNode }) {
  const [loaded, setLoaded] = useState(() => !!src && decodedAvatarUrls.has(src));
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setLoaded(!!src && decodedAvatarUrls.has(src));
    setFailed(false);
  }, [src]);
  return (
    <>
      {(!src || failed) && fallback}
      {src && !failed && !loaded && <span className="avatar-loading-skeleton" aria-hidden="true" />}
      {src && !failed && (
        <img
          className={loaded ? "avatar-image-ready" : "avatar-image-loading"}
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          onLoad={() => {
            decodedAvatarUrls.add(src);
            setLoaded(true);
          }}
          onError={() => setFailed(true)}
        />
      )}
    </>
  );
}

function App() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => onAuthStateChanged(auth, setUser), []);
  if (user === undefined) return <Splash />;
  return <>{user ? <AdminApp user={user} /> : <Login />}<ConfirmationHost /></>;
}

function ConfirmationHost() {
  const [request, setRequest] = useState<(ConfirmOptions & { resolve: (value: boolean) => void }) | null>(null);
  useEffect(() => {
    const listener = (event: Event) => setRequest((event as CustomEvent).detail);
    window.addEventListener(CONFIRM_EVENT, listener);
    return () => window.removeEventListener(CONFIRM_EVENT, listener);
  }, []);
  useEffect(() => {
    if (!request) return;
    const listener = (event: KeyboardEvent) => {
      if (event.key === "Escape") { request.resolve(false); setRequest(null); }
    };
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, [request]);
  if (!request) return null;
  const close = (answer: boolean) => { request.resolve(answer); setRequest(null); };
  const tone = request.tone || "warning";
  return <div className="confirm-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) close(false); }}>
    <section className={`confirm-dialog ${tone}`} role="alertdialog" aria-modal="true" aria-labelledby="confirm-title" aria-describedby="confirm-message">
      <button className="confirm-close" aria-label="Close" onClick={() => close(false)}><X /></button>
      <span className="confirm-icon">{tone === "danger" ? <CircleAlert /> : tone === "primary" ? <CheckCircle2 /> : <Shield />}</span>
      <div><small>{tone === "danger" ? "Sensitive action" : tone === "primary" ? "Confirmation" : "Please confirm"}</small><h2 id="confirm-title">{request.title}</h2><p id="confirm-message">{request.message}</p></div>
      <footer><button onClick={() => close(false)}>{request.cancelLabel || "Cancel"}</button><button className="confirm-submit" autoFocus onClick={() => close(true)}>{tone === "danger" ? <XCircle /> : <Check />}{request.confirmLabel || "Confirm"}</button></footer>
    </section>
  </div>;
}

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to sign in");
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="login-page">
      <form className="login-card" onSubmit={submit}>
        <Brand />
        <h1>Admin Console</h1>
        <p>Sign in with your authorized OPrime admin account.</p>
        <label>
          Email
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>
        <label>
          Password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        {error && <div className="error">{error}</div>}
        <button className="primary" disabled={busy}>
          {busy ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </main>
  );
}

const dashboardCacheKey = (uid: string) => `oprime-admin-dashboard:${uid}`;
const dashboardBootCacheKey = (uid: string) => `oprime-admin-boot:${uid}`;

const terminalTransactionStatuses = new Set([
  "success",
  "successful",
  "completed",
  "delivered",
  "failed",
  "failure",
  "error",
  "rejected",
  "refunded",
  "reversed",
  "declined",
  "cancelled",
  "canceled",
]);

function isPendingTransactionRecord(row: Transaction) {
  const status = String(row.status || "").trim().toLowerCase();
  const reviewStatus = String((row as any).adminReviewStatus || "")
    .trim()
    .toLowerCase();
  if (terminalTransactionStatuses.has(status) || status === "processing") {
    return false;
  }
  return (
    status === "pending" ||
    status === "review" ||
    status === "in_review" ||
    status === "manual_review" ||
    reviewStatus === "pending" ||
    (row as any).requiresAdminApproval === true
  );
}

function transactionKey(row: Transaction) {
  return `${String(row.uid || "")}/${String(row.id || row.txId || "")}`;
}

function mergeTransaction(existing: Transaction | undefined, incoming: Transaction) {
  if (!existing) return incoming;
  const existingStatus = String(existing.status || "").toLowerCase();
  const incomingStatus = String(incoming.status || "").toLowerCase();
  const existingIsTerminal = terminalTransactionStatuses.has(existingStatus);
  const incomingIsTerminal = terminalTransactionStatuses.has(incomingStatus);
  const existingUpdatedAt = Number(existing.updatedAt || existing.completedAt || existing.createdAt || 0);
  const incomingUpdatedAt = Number(incoming.updatedAt || incoming.completedAt || incoming.createdAt || 0);

  // Provider callbacks move transactions from pending/processing to a final
  // state. A slower dashboard response must never move that row backwards.
  if (existingIsTerminal && !incomingIsTerminal) return { ...incoming, ...existing };
  if (incomingIsTerminal && !existingIsTerminal) return { ...existing, ...incoming };
  return incomingUpdatedAt >= existingUpdatedAt
    ? { ...existing, ...incoming }
    : { ...incoming, ...existing };
}

function mergeTransactions(current: Transaction[], incoming: Transaction[]) {
  const byKey = new Map(current.map((row) => [transactionKey(row), row]));
  incoming.forEach((row) => {
    const key = transactionKey(row);
    byKey.set(key, mergeTransaction(byKey.get(key), row));
  });
  return Array.from(byKey.values()).sort(
    (a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0),
  );
}

function readDashboardCache(uid: string): DashboardData | null {
  try {
    const cached =
      sessionStorage.getItem(dashboardCacheKey(uid)) ||
      localStorage.getItem(dashboardBootCacheKey(uid));
    return cached ? (JSON.parse(cached) as DashboardData) : null;
  } catch {
    return null;
  }
}

function writeDashboardCache(uid: string, data: DashboardData) {
  const key = dashboardCacheKey(uid);
  try {
    sessionStorage.setItem(key, JSON.stringify(data));
  } catch {
    // Keep a smaller, immediately useful snapshot if the browser storage quota
    // cannot hold the complete administrative response.
    try {
      sessionStorage.setItem(
        key,
        JSON.stringify({
          ...data,
          transactions: (data.transactions || []).slice(0, 1000),
          auditLogs: (data.auditLogs || []).slice(0, 500),
          webhookEvents: (data.webhookEvents || []).slice(0, 100),
          cashbackEntries: (data.cashbackEntries || []).slice(0, 100),
          referralEntries: (data.referralEntries || []).slice(0, 100),
          referralWithdrawals: (data.referralWithdrawals || []).slice(0, 100),
          promoRedemptions: (data.promoRedemptions || []).slice(0, 100),
        }),
      );
    } catch {
      // The live request still works when storage is unavailable or disabled.
    }
  }
  // A compact persistent snapshot makes a fresh tab immediately data-ready.
  // Live Firestore listeners and adminDashboard reconcile it in the background.
  try {
    localStorage.setItem(
      dashboardBootCacheKey(uid),
      JSON.stringify({
        ...data,
        users: (data.users || []).slice(0, 200),
        transactions: (data.transactions || []).slice(0, 120),
        auditLogs: (data.auditLogs || []).slice(0, 60),
        webhookEvents: (data.webhookEvents || []).slice(0, 30),
        cashbackEntries: (data.cashbackEntries || []).slice(0, 30),
        referralEntries: (data.referralEntries || []).slice(0, 30),
        referralWithdrawals: (data.referralWithdrawals || []).slice(0, 30),
        promoRedemptions: (data.promoRedemptions || []).slice(0, 30),
      }),
    );
  } catch {
    // Storage can be unavailable in private browsing; live loading still works.
  }
}

async function preloadDashboardImages(data: DashboardData) {
  const urls = Array.from(new Set([
    ...(data.users || []).map((item) => String(item.photoUrl || "").trim()),
    ...(data.transactions || []).map((item) => String(item.photoUrl || "").trim()),
  ].filter(Boolean))).slice(0, 100);
  if (!urls.length) return;
  const ready = Promise.allSettled(urls.map((url) => new Promise<void>((resolve) => {
    if (decodedAvatarUrls.has(url)) return resolve();
    const image = new window.Image();
    image.decoding = "async";
    image.fetchPriority = "high";
    image.onload = () => {
      decodedAvatarUrls.add(url);
      void image.decode?.().catch(() => undefined).finally(resolve);
    };
    image.onerror = () => resolve();
    image.src = url;
    if (image.complete && image.naturalWidth > 0) {
      decodedAvatarUrls.add(url);
      resolve();
    }
  })));
  await Promise.race([ready, new Promise<void>((resolve) => window.setTimeout(resolve, 2500))]);
}

function AdminApp({ user }: { user: User }) {
  const [dark, setDark] = useState(
    () => localStorage.getItem("oprime-admin-theme") === "dark",
  );
  const [mobileNav, setMobileNav] = useState(false);
  const [data, setData] = useState<DashboardData | null>(() =>
    readDashboardCache(user.uid),
  );
  const initialImageWarmup = useRef(!data);
  const [busy, setBusy] = useState(true);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [toast, setToast] = useState<{
    message: string;
    error?: boolean;
  } | null>(null);
  const location = useLocation();
  useEffect(() => {
    if (data) void preloadDashboardImages(data);
  }, []);
  const load = useCallback(async (searchQuery?: string) => {
    setBusy(true);
    setError("");
    try {
      let result: Awaited<ReturnType<ReturnType<typeof httpsCallable<Record<string, unknown>, DashboardData>>>> | null = null;
      let lastError: unknown;
      // A cold dashboard request can occasionally exceed the callable deadline.
      // Retry once with a smaller working set before exposing an error screen.
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const call = httpsCallable<Record<string, unknown>, DashboardData>(
            functions,
            "adminDashboard",
            { timeout: attempt === 0 ? 25000 : 45000 }
          );
          result = await call({
            limitUsers: attempt === 0 ? 180 : 80,
            // Exact searches still query the backend and realtime listeners
            // append or update new records as they arrive.
            limitTransactions: attempt === 0 ? 240 : 100,
            ...(searchQuery ? { search: searchQuery, txSearch: searchQuery } : {}),
          });
          break;
        } catch (requestError) {
          lastError = requestError;
          const message = requestError instanceof Error ? requestError.message : String(requestError || "");
          if (attempt === 1 || !/deadline|timeout|unavailable/i.test(message)) throw requestError;
        }
      }
      if (!result) throw lastError || new Error("Could not load admin data");
      if (!searchQuery) writeDashboardCache(user.uid, result.data);
      warmUserProfileCache(result.data);
      if (initialImageWarmup.current) {
        initialImageWarmup.current = false;
        await preloadDashboardImages(result.data);
      }
      setData((prev) => {
        if (!prev) return result.data;
        const transactions = mergeTransactions(
          prev.transactions || [],
          result.data.transactions || [],
        );
        if (!searchQuery) return { ...result.data, transactions };
        const userMap = new Map((prev.users || []).map((u) => [u.uid || (u as any).id, u]));
        (result.data.users || []).forEach((u) => userMap.set(u.uid || (u as any).id, u));
        return {
          ...result.data,
          transactions,
          users: Array.from(userMap.values()),
        };
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load admin data");
    } finally {
      setBusy(false);
    }
  }, [user.uid]);
  const runAction = useCallback(
    async (
      name: string,
      payload: Record<string, unknown>,
      success: string,
      refreshDashboard = true,
    ) => {
      setActionInProgress(adminActionProgressLabel(name, payload));
      setError("");
      try {
        const token = await auth.currentUser?.getIdToken();
        const call = httpsCallable<
          Record<string, unknown>,
          Record<string, unknown>
        >(functions, name, { timeout: 120000 });
        await call({ ...payload, ...(token ? { authToken: token } : {}) });

        // Resolve transaction actions in the current UI immediately. The live
        // Firestore listener remains authoritative and reconciles the complete
        // server record afterwards.
        if (
          name === "adminResolveAirtimeReview" ||
          name === "adminRefundProcessingTransaction" ||
          name === "adminResolveReferralWithdrawal"
        ) {
          const action = String(payload.action || "").toLowerCase();
          const resolvedStatus =
            name === "adminRefundProcessingTransaction" || action === "reject" || payload.decision === "rejected"
              ? "refunded"
              : "completed";
          const payloadUid = String(payload.uid || "");
          const payloadId = String(payload.id || "");
          const payloadTxId = String(payload.txId || "");
          const payloadRequestId = String(payload.providerRequestId || "");
          setData((current) => {
            if (!current) return current;
            let changed = false;
            const transactions = (current.transactions || []).map((row) => {
              const matchesUid = !payloadUid || String(row.uid || "") === payloadUid;
              const matchesTransaction =
                (payloadId && String(row.id || "") === payloadId) ||
                (payloadTxId && String(row.txId || "") === payloadTxId) ||
                (payloadRequestId &&
                  String(row.providerRequestId || "") === payloadRequestId);
              if (!matchesUid || !matchesTransaction) return row;
              changed = changed || isPendingTransactionRecord(row);
              return {
                ...row,
                status: resolvedStatus,
                adminReviewStatus: action === "reject" ? "rejected" : "approved",
                requiresAdminApproval: false,
                updatedAt: Date.now(),
                ...(resolvedStatus === "completed" ? { completedAt: Date.now() } : {}),
              } as Transaction;
            });
            if (!changed) return { ...current, transactions };
            return {
              ...current,
              transactions,
              stats: {
                ...current.stats,
                pendingTransactions: Math.max(
                  0,
                  Number(current.stats?.pendingTransactions || 0) - 1,
                ),
              },
            };
          });
        }
        setToast({ message: success });
        // Firestore listeners update the visible rows immediately. A full
        // dashboard reconciliation can happen in the background and must not
        // keep an action button waiting after the server has accepted it.
        if (refreshDashboard) {
          window.setTimeout(() => void load(), 250);
        }
        return true;
      } catch (e) {
        const message =
          e instanceof Error ? e.message : "The admin action failed";
        setError(message);
        setToast({ message, error: true });
        return false;
      } finally {
        setActionInProgress(null);
      }
    },
    [load],
  );
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    let refreshTimer: number | undefined;
    const scheduleRefresh = () => {
      if (refreshTimer) window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => void load(), 500);
    };
    const liveTransactions = query(
      collectionGroup(db, "items"),
      orderBy("createdAt", "desc"),
      limit(500),
    );
    const subscriptions = [
      onSnapshot(collection(db, "users"), (snapshot) => {
        setData((current) => {
          if (!current) return current;
          const byId = new Map((current.users || []).map((item) => [String(item.uid || (item as any).id), item]));
          snapshot.docChanges().forEach((change) => {
            if (change.type === "removed") { byId.delete(change.doc.id); return; }
            const raw = change.doc.data();
            const previous = byId.get(change.doc.id);
            byId.set(change.doc.id, {
              ...previous,
              ...raw,
              uid: change.doc.id,
              name: raw.name || raw.displayName || previous?.name || "",
              email: raw.email || previous?.email || "",
              phone: raw.phone || raw.phoneNumber || previous?.phone || "",
              // An explicitly empty photo means the user removed it. Do not revive
              // a stale photo from dashboard/cache data after that removal.
              photoUrl: Object.prototype.hasOwnProperty.call(raw, "photoUrl")
                ? String(raw.photoUrl || "")
                : String(raw.profilePhotoUrl || raw.avatarUrl || previous?.photoUrl || ""),
              avatarId: Object.prototype.hasOwnProperty.call(raw, "avatarId")
                ? String(raw.avatarId || "")
                : String(previous?.avatarId || ""),
            } as AdminUser);
          });
          const next = { ...current, users: [...byId.values()] };
          warmUserProfileCache(next);
          return next;
        });
      }, () => undefined),
      onSnapshot(collection(db, "wallets"), (snapshot) => {
        setData((current) => {
          if (!current) return current;
          const changes = new Map(snapshot.docChanges().filter((change) => change.type !== "removed").map((change) => [change.doc.id, change.doc.data()]));
          if (!changes.size) return current;
          return { ...current, users: current.users.map((item) => {
            const wallet = changes.get(String(item.uid || ""));
            return wallet ? { ...item, balance: Number(wallet.balance ?? item.balance ?? 0) } : item;
          }) };
        });
      }, () => undefined),
      onSnapshot(
        liveTransactions,
        (snapshot) => {
          setData((current) => {
            if (!current) return current;
            const byKey = new Map(
              (current.transactions || []).map((row) => [`${row.uid || ""}/${row.id}`, row]),
            );
            snapshot.docChanges().forEach((change) => {
              // Several unrelated features also use a subcollection named
              // `items` (for example beneficiaries). A collection-group
              // listener sees all of them, so accept only real transaction
              // paths: transactions/{uid}/items/{transactionId}.
              if (change.doc.ref.parent.parent?.parent.id !== "transactions") return;
              const uid = change.doc.ref.parent.parent?.id || String(change.doc.data().uid || "");
              const key = `${uid}/${change.doc.id}`;
              if (change.type === "removed") {
                byKey.delete(key);
                return;
              }
              const raw = change.doc.data();
              const knownUser = current.users.find((item) => item.uid === uid);
              const createdAt = raw.createdAt?.toMillis?.() ?? Number(raw.createdAt || Date.now());
              const incoming = {
                ...byKey.get(key),
                ...raw,
                id: change.doc.id,
                txId: raw.txId || change.doc.id,
                uid,
                type: String(raw.type || "").toLowerCase(),
                status: String(raw.status || "pending").toLowerCase(),
                amount: Number(raw.amount ?? raw.total ?? 0),
                total: Number(raw.total ?? raw.amount ?? 0),
                userName: knownUser?.name || raw.userName || "",
                userEmail: knownUser?.email || raw.userEmail || "",
                userPhone: knownUser?.phone || raw.userPhone || "",
                // The live users document is authoritative. An empty current
                // photo means the user intentionally returned to an avatar;
                // never revive the image captured by an older transaction.
                photoUrl: knownUser
                  ? String(knownUser.photoUrl || "")
                  : String(raw.photoUrl || ""),
                avatarId: knownUser
                  ? String(knownUser.avatarId || "")
                  : String(raw.avatarId || ""),
                createdAt,
                updatedAt: raw.updatedAt?.toMillis?.() ?? raw.completedAt?.toMillis?.() ?? createdAt,
                completedAt: raw.completedAt?.toMillis?.() ?? undefined,
              } as Transaction;
              byKey.set(key, mergeTransaction(byKey.get(key), incoming));
            });
            return {
              ...current,
              transactions: Array.from(byKey.values()).sort(
                (a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0),
              ),
            };
          });
        },
        (snapshotError) => {
          console.error("Live transaction sync failed", snapshotError);
          setToast({ message: "Live transaction sync disconnected. Reconnecting…", error: true });
        },
      ),
      onSnapshot(
        query(collection(db, "admin_audit_logs"), orderBy("createdAt", "desc"), limit(150)),
        (snapshot) => {
          setData((current) => {
            if (!current) return current;
            const auditById = new Map((current.auditLogs || []).map((row) => [String(row.id), row]));
            snapshot.docChanges().forEach((change) => {
              if (change.type === "removed") {
                auditById.delete(change.doc.id);
                return;
              }
              const raw = change.doc.data();
              auditById.set(change.doc.id, {
                ...auditById.get(change.doc.id),
                ...raw,
                id: change.doc.id,
                amount: Number(raw.amount || 0),
                createdAt: raw.createdAt?.toMillis?.() ?? Number(raw.createdAt || Date.now()),
              });
            });
            return { ...current, auditLogs: [...auditById.values()].sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0)) };
          });
        },
        () => undefined,
      ),
    ];
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") scheduleRefresh();
    };
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      subscriptions.forEach((unsubscribe) => unsubscribe());
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      if (refreshTimer) window.clearTimeout(refreshTimer);
    };
  }, [load]);
  useEffect(() => {
    document.documentElement.dataset.theme = dark ? "dark" : "light";
    localStorage.setItem("oprime-admin-theme", dark ? "dark" : "light");
  }, [dark]);
  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 3000);
    return () => window.clearTimeout(timer);
  }, [toast]);
  useEffect(() => {
    const handleFlushbar = (event: Event) => {
      const detail = (event as CustomEvent<{ message: string; error?: boolean }>)
        .detail;
      if (detail?.message) setToast(detail);
    };
    window.addEventListener(FLUSHBAR_EVENT, handleFlushbar);
    return () => window.removeEventListener(FLUSHBAR_EVENT, handleFlushbar);
  }, []);
  const allowed = useMemo(
    () => new Set(data?.adminAccess?.sections ?? nav.map((item) => item.key)),
    [data],
  );
  const visibleNav = nav.filter(
    (item) =>
      item.key === "overview" ||
      item.key === "pendingTransactions" ||
      item.path === "/pending" ||
      allowed.has(item.key) ||
      (item.key === "pendingTransactions" && allowed.has("transactions")) ||
      (item.adminOnly && data?.adminAccess?.canManageAdmins),
  );
  const pendingTransactionCount = (data?.transactions || []).filter(
    (row) =>
      !isWalletAdjustmentTransaction(row) &&
      !isDepositTransaction(row) &&
      isPendingTransactionRecord(row),
  ).length;
  const watchlistCount = (data?.users || []).filter(
    (listedUser) => listedUser.watchlisted === true,
  ).length;
  const currentAdmin = data?.adminAccessList?.find(
    (item) => String(item.email || "").toLowerCase() === String(user.email || "").toLowerCase(),
  );
  const currentAdminName = String(currentAdmin?.name || user.displayName || "Admin");

  // Keep the initial secure fetch behind the branded splash. Once the shell is
  // visible, every dashboard route receives the same already-loaded snapshot.
  if (busy && !data) return <Splash />;

  return (
    <div className="app-shell">
      {actionInProgress && (
        <div className="admin-action-progress" role="status" aria-live="polite">
          <Loader2 className="spin" />
          <span>
            <strong>{actionInProgress}</strong>
            <small>Please wait while the change is saved.</small>
          </span>
        </div>
      )}
      {toast && (
        <div className={`flushbar ${toast.error ? "flushbar-error" : ""}`}>
          {toast.error ? <CircleAlert /> : <CheckCircle2 />}
          <span>{toast.message}</span>
        </div>
      )}
      <aside className={mobileNav ? "sidebar open" : "sidebar"}>
        <button
          className="close-nav icon-button"
          onClick={() => setMobileNav(false)}
        >
          <X />
        </button>
        <Brand />
        <nav>
          {visibleNav.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setMobileNav(false)}
            >
              <item.icon />
              <span>{item.label}</span>
              {item.key === "pendingTransactions" && pendingTransactionCount > 0 && (
                <strong
                  className="nav-count-badge"
                  aria-label={`${pendingTransactionCount} pending transactions`}
                  title={`${pendingTransactionCount} pending transactions`}
                >
                  {pendingTransactionCount > 99 ? "99+" : pendingTransactionCount}
                </strong>
              )}
              {item.key === "watchlist" && watchlistCount > 0 && (
                <strong
                  className="nav-count-badge"
                  aria-label={`${watchlistCount} watchlisted users`}
                  title={`${watchlistCount} watchlisted users`}
                >
                  {watchlistCount > 99 ? "99+" : watchlistCount}
                </strong>
              )}
              <ChevronRight className="nav-chevron" />
            </NavLink>
          ))}
        </nav>
        <button
          className="signout"
          onClick={() => {
            sessionStorage.removeItem(dashboardCacheKey(user.uid));
            localStorage.removeItem(dashboardBootCacheKey(user.uid));
            void signOut(auth);
          }}
        >
          <LogOut /> Sign Out
        </button>
      </aside>
      {mobileNav && (
        <button
          className="mobile-nav-scrim"
          aria-label="Close navigation"
          onClick={() => setMobileNav(false)}
        />
      )}
      <main className="workspace">
        <header>
          <button
            className="menu icon-button"
            onClick={() => setMobileNav(true)}
          >
            <Menu />
          </button>
          <div>
            <h1>
              {nav.find((item) => item.path === location.pathname)?.label ??
                (location.pathname === "/profile" ? "My Profile" : "Overview")}
            </h1>
            <p>Monitor operations, customers and live transactions</p>
          </div>
          <div className="header-actions">
            <span className="live">
              <i /> LIVE SYNC
            </span>
            <button className="icon-button" onClick={() => void load()} title="Refresh">
              <RefreshCw className={busy ? "spin" : ""} />
            </button>
            <button className="icon-button" onClick={() => setDark((v) => !v)}>
              {dark ? <Sun /> : <Moon />}
            </button>
            <NavLink className="admin-profile-trigger" to="/profile" title="Open admin profile">
              <span className="avatar">{initials(currentAdminName)}</span>
              <span><strong>{currentAdminName}</strong><small>{data?.adminAccess?.role || "Admin"}</small></span>
            </NavLink>
          </div>
        </header>
        <section className="content">
          {error && (
            <div className="error banner">
              {error}
              <button onClick={() => void load()}>Retry</button>
            </div>
          )}
          {busy && !data ? (
            <Loading />
          ) : (
            data && (
              <AdminPermissionContext.Provider
                value={{
                  canAdjustWallet:
                    data.adminAccess.role === "superadmin" ||
                    data.adminAccess.canAdjustWallet === true,
                  canViewTransactionProviders:
                    data.adminAccess.role === "superadmin" ||
                    data.adminAccess.canViewTransactionProviders === true,
                  adminDirectory: data.adminAccessList || [],
                }}
              >
              <Routes>
                <Route
                  path="/overview"
                  element={<Overview data={data} runAction={runAction} />}
                />
                <Route
                  path="/transactions"
                  element={
                    <Transactions
                      rows={data.transactions}
                      users={data.users}
                      auditLogs={data.auditLogs}
                      mode="transactions"
                      runAction={runAction}
                      onSearch={(q) => void load(q)}
                    />
                  }
                />
                <Route
                  path="/pending"
                  element={
                    <Transactions
                      rows={data.transactions}
                      users={data.users}
                      auditLogs={data.auditLogs}
                      mode="pending"
                      runAction={runAction}
                      onSearch={(q) => void load(q)}
                    />
                  }
                />
                <Route
                  path="/deposits"
                  element={
                    <DepositsPage
                      rows={data.transactions}
                      users={data.users}
                      totalDeposits={data.stats.totalDeposits}
                      runAction={runAction}
                      onSearch={(q) => void load(q)}
                    />
                  }
                />
                <Route
                  path="/users"
                  element={
                    <UsersPage
                      users={data.users}
                      transactions={data.transactions}
                      runAction={runAction}
                    />
                  }
                />
                <Route
                  path="/watchlist"
                  element={allowed.has("watchlist")
                    ? <WatchlistPage users={data.users} runAction={runAction} />
                    : <Navigate to="/overview" replace />}
                />
                <Route
                  path="/services"
                  element={<ServicesPage data={data} />}
                />
                <Route path="/data-plans" element={<DataPlansManager />} />
                <Route path="/funding" element={<FundingPage data={data} />} />
                <Route
                  path="/kyc"
                  element={
                    <KycReviewPage
                      rows={data.kycSubmissions}
                      runAction={runAction}
                    />
                  }
                />
                <Route
                  path="/aml"
                  element={
                    <AmlReviewPage
                      reviews={data.amlReviews}
                      settings={data.amlSettings}
                      auditLogs={data.auditLogs}
                      users={data.users}
                      runAction={runAction}
                    />
                  }
                />
                <Route
                  path="/disputes"
                  element={
                    <DisputesPage
                      rows={data.disputes}
                      users={data.users}
                      runAction={runAction}
                    />
                  }
                />
                <Route
                  path="/broadcasts"
                  element={
                    <BroadcastPage
                      rows={data.broadcasts}
                      runAction={runAction}
                    />
                  }
                />
                <Route
                  path="/cashback"
                  element={
                    <CashbackPage
                      settings={data.cashbackSettings}
                      rows={data.cashbackEntries}
                      runAction={runAction}
                    />
                  }
                />
                <Route
                  path="/referrals"
                  element={
                    <ReferralsPage
                      settings={data.referralSettings}
                      entries={data.referralEntries}
                      withdrawals={data.referralWithdrawals}
                      runAction={runAction}
                    />
                  }
                />
                <Route
                  path="/promos"
                  element={
                    <PromosPage
                      rows={data.promoCodes}
                      redemptions={data.promoRedemptions}
                      totalSpendKobo={Number(data.stats?.totalPromoSpendKobo || 0)}
                      runAction={runAction}
                    />
                  }
                />
                <Route
                  path="/banners"
                  element={<HomeBannersPage />}
                />
                <Route
                  path="/campaigns"
                  element={<CampaignsPage />}
                />
                <Route
                  path="/activity"
                  element={
                    <ActivityPage
                      rows={data.auditLogs}
                      admins={data.adminAccessList}
                      broadcasts={data.broadcasts}
                      disputes={data.disputes}
                      users={data.users}
                      runAction={runAction}
                    />
                  }
                />
                <Route path="/reports" element={<ReportsPage data={data} />} />
                <Route
                  path="/health"
                  element={
                    <HealthPage
                      providers={data.providersHealth}
                      events={data.webhookEvents}
                      runAction={runAction}
                    />
                  }
                />
                <Route
                  path="/admins"
                  element={
                    <AdminsPage
                      admins={data.adminAccessList}
                      invites={data.adminInvites}
                      runAction={runAction}
                    />
                  }
                />
                <Route
                  path="/profile"
                  element={<AdminProfilePage user={user} access={data.adminAccess} admin={currentAdmin} />}
                />
                <Route path="*" element={<Navigate to="/overview" replace />} />
              </Routes>
              </AdminPermissionContext.Provider>
            )
          )}
        </section>
      </main>
    </div>
  );
}

function Brand() {
  return (
    <div className="brand">
      <div className="brand-mark"><img src={oprimeLogoUrl} alt="OPrime" /></div>
      <div>
        <strong>OPrime</strong>
        <small>ADMIN CONSOLE</small>
      </div>
    </div>
  );
}

function AdminProfilePage({
  user,
  access,
  admin,
}: {
  user: User;
  access: DashboardData["adminAccess"];
  admin?: Record<string, unknown>;
}) {
  const [step, setStep] = useState<"request" | "verify">("request");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const email = String(user.email || admin?.email || "");
  const name = String(admin?.name || user.displayName || "Admin");
  const sections = Array.isArray(access.sections) ? access.sections : [];
  const passwordChecks = [
    [newPassword.length >= 8, "At least 8 characters"],
    [/[A-Z]/.test(newPassword), "One uppercase letter"],
    [/[a-z]/.test(newPassword), "One lowercase letter"],
    [/\d/.test(newPassword), "One number"],
  ] as const;
  const passwordReady = passwordChecks.every(([valid]) => valid) && newPassword === confirmPassword;

  useEffect(() => {
    if (!cooldown) return;
    const timer = window.setInterval(() => setCooldown((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  const sendCode = async () => {
    if (!email || cooldown) return;
    setBusy(true); setError("");
    try {
      const call = httpsCallable(functions, "requestPasswordResetOtp", { timeout: 30000 });
      await call({ email });
      setStep("verify"); setCooldown(60);
      showFlushbar(`Verification code sent to ${email}`);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not send the verification code");
    } finally { setBusy(false); }
  };

  const changePassword = async (event: FormEvent) => {
    event.preventDefault();
    if (code.replace(/\D/g, "").length !== 6) return setError("Enter the complete 6-digit verification code.");
    if (!passwordReady) return setError("Use a stronger password and make sure both passwords match.");
    setBusy(true); setError("");
    try {
      const call = httpsCallable(functions, "resetPasswordWithEmailOtp", { timeout: 30000 });
      await call({ email, code: code.replace(/\D/g, ""), newPassword });
      showFlushbar("Password changed. Sign in again with your new password.");
      sessionStorage.removeItem(dashboardCacheKey(user.uid));
      window.setTimeout(() => void signOut(auth), 900);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not change the password");
    } finally { setBusy(false); }
  };

  return <div className="admin-profile-page">
    <section className="admin-profile-hero">
      <span>{initials(name)}</span>
      <div><small>ADMIN PROFILE</small><h2>{name}</h2><p>{email}</p><div><b><CheckCircle2 /> Active account</b><b><Shield /> {access.role || "admin"}</b>{access.canManageTransactions && <b><Zap /> Transaction actions</b>}</div></div>
      <NavLink to="/overview"><ArrowLeft /> Back to dashboard</NavLink>
    </section>

    <div className="admin-profile-grid">
      <section className="admin-profile-card account-card">
        <header><span><UserCog /></span><div><h2>Account details</h2><p>Your identity and console access.</p></div></header>
        <div className="admin-account-details">
          <div><small>Full name</small><strong>{name}</strong></div>
          <div><small>Email address</small><strong>{email}</strong><em><Check /> Verified</em></div>
          <div><small>Firebase UID</small><strong>{user.uid}</strong></div>
          <div><small>Role</small><strong>{access.role || "admin"}</strong></div>
        </div>
        <div className="admin-access-summary"><div><strong>Section access</strong><small>{sections.length} console section{sections.length === 1 ? "" : "s"} assigned</small></div><div>{sections.length ? sections.map((key) => <span key={key}>{nav.find((item) => item.key === key)?.label || humanize(key)}</span>) : <p>No individual section restrictions.</p>}</div></div>
      </section>

      <section className="admin-profile-card password-card">
        <header><span><ShieldCheck /></span><div><h2>Change password</h2><p>Protected by an email verification code.</p></div></header>
        {step === "request" ? <div className="password-request">
          <span><Mail /></span><h3>Verify it’s really you</h3><p>We’ll send a six-digit OTP to <strong>{email}</strong>. It expires after 10 minutes and can only be used once.</p>
          <button disabled={busy || !email} onClick={() => void sendCode()}>{busy ? <Loader2 className="spin" /> : <Send />} Send verification code</button>
        </div> : <form className="password-change-form" onSubmit={changePassword}>
          <div className="otp-heading"><span><Mail /></span><div><strong>Code sent to your email</strong><small>Enter it below with your new password.</small></div></div>
          <label><span>Verification code</span><div className="admin-otp-input"><input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" maxLength={6} /></div></label>
          <label><span>New password</span><input type="password" autoComplete="new-password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} placeholder="Enter a strong new password" /></label>
          <label><span>Confirm new password</span><input type="password" autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} placeholder="Repeat your new password" /></label>
          <div className="password-rules">{passwordChecks.map(([valid, label]) => <span className={valid ? "valid" : ""} key={label}>{valid ? <CheckCircle2 /> : <CircleAlert />}{label}</span>)}<span className={confirmPassword && newPassword === confirmPassword ? "valid" : ""}>{confirmPassword && newPassword === confirmPassword ? <CheckCircle2 /> : <CircleAlert />}Passwords match</span></div>
          {error && <p className="profile-form-error"><CircleAlert />{error}</p>}
          <footer><button type="button" disabled={busy || cooldown > 0} onClick={() => void sendCode()}><RotateCw />{cooldown ? `Resend in ${cooldown}s` : "Resend code"}</button><button className="save-password" disabled={busy || !passwordReady || code.length !== 6}>{busy ? <Loader2 className="spin" /> : <ShieldCheck />} Change password</button></footer>
        </form>}
        {step === "request" && error && <p className="profile-form-error request-error"><CircleAlert />{error}</p>}
      </section>
    </div>

    <section className="admin-security-note"><Shield /><div><strong>Account security</strong><p>OPrime will never ask you to share an OTP or password. Changing your password signs this browser out so you can authenticate again securely.</p></div></section>
  </div>;
}
function Splash() {
  return (
    <main className="splash">
      <Brand />
      <span className="loader" />
    </main>
  );
}
function Loading() {
  return (
    <div className="loading">
      <span className="loader" />
      <p>Loading secure admin data…</p>
    </div>
  );
}

function Overview({
  data,
  runAction,
}: {
  data: DashboardData;
  runAction: RunAction;
}) {
  const s = data.stats;
  const cards = [
    ["Users", s.users, Users],
    ["Suspended", s.suspendedUsers, Ban],
    ["Transactions", s.transactions, Activity],
    ["Success", s.successfulTransactions, ShieldCheck],
    ["Failed", s.failedTransactions, CircleAlert],
    ["Pending", s.pendingTransactions, Clock3],
    ["Held", s.heldDeposits, Shield],
    ["Pending KYC", s.pendingKyc, ShieldCheck],
    ["Open Disputes", s.openDisputes, Mail],
    ["Volume", naira(s.volumeKobo), CircleDollarSign],
    ["Wallet Float", naira(s.walletFloatKobo), WalletCards],
  ] as const;
  const recentTransactions = data.transactions
    .filter(
      (row) =>
        !isDepositTransaction(row) &&
        !isWalletAdjustmentTransaction(row) &&
        row.status !== "pending",
    )
    .slice(0, 5);
  const recentDeposits = data.transactions
    .filter(isDepositTransaction)
    .slice(0, 5);
  return (
    <>
      <section className="command-center">
        <span className="command-icon">
          <LayoutDashboard />
        </span>
        <div>
          <h2>OPrime Command Center</h2>
          <p>
            Monitor customers, deposits, disputes, product health and live
            transactions from one place.
          </p>
        </div>
        <div className="command-watermark" aria-hidden="true" />
      </section>
      <div className="metric-grid">
        {cards.map(([label, value, Icon]) => (
          <article className="metric" key={label}>
            <span>
              <Icon />
            </span>
            <div>
              <small>{label}</small>
              <strong>{value ?? 0}</strong>
            </div>
          </article>
        ))}
      </div>
      <OverviewActivityTable
        title="Recent Transactions"
        rows={recentTransactions}
        users={data.users}
        auditLogs={data.auditLogs}
        runAction={runAction}
      />
      <OverviewActivityTable
        title="Recent Deposits"
        rows={recentDeposits}
        users={data.users}
        auditLogs={data.auditLogs}
        runAction={runAction}
        deposits
      />
    </>
  );
}

function OverviewActivityTable({
  title,
  rows,
  users,
  auditLogs,
  runAction,
  deposits = false,
}: {
  title: string;
  rows: Transaction[];
  users: AdminUser[];
  auditLogs: Record<string, unknown>[];
  runAction: RunAction;
  deposits?: boolean;
}) {
  const [selected, setSelected] = useState<Transaction | null>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const userMap = useMemo(() => buildUserMap(users), [users]);
  return (
    <section className="overview-activity panel">
      <div className="activity-heading">
        <h2>{title}</h2>
        <span className="activity-live">
          <i /> Live Sync
        </span>
      </div>
      <div className="activity-scroll">
        <div className="activity-grid activity-header">
          <span>Transactions ↕</span>
          <span>User ↕</span>
          <span>Status ↕</span>
          <span>Amount ↕</span>
          <span>Action</span>
        </div>
        {rows.length === 0 ? (
          <div className="activity-empty">
            No recent {deposits ? "deposits" : "transactions"} found.
          </div>
        ) : (
          rows.map((row) => {
            const matchedUser = currentUserForTransaction(userMap, row);
            const userPhoto = userAvatarSrc(
              matchedUser ? matchedUser.photoUrl : row.photoUrl,
              matchedUser ? matchedUser.avatarId : row.avatarId,
              matchedUser?.name || row.userName,
            );
            const success = [
              "success",
              "successful",
              "completed",
              "delivered",
            ].includes((row.status ?? "").toLowerCase());
            const failed = [
              "failed",
              "failure",
              "rejected",
              "cancelled",
            ].includes((row.status ?? "").toLowerCase());
            const displayStatus = success
              ? "Completed"
              : failed
                ? "Failed"
                : row.status || "Pending";
            return (
              <div
                className="activity-grid activity-row"
                key={`${title}-${row.uid}-${row.id}`}
              >
                <div className="activity-service">
                  <span
                    className={`service-badge ${deposits ? "deposit" : ""} ${
                      !deposits && isVirtualNumberTransaction(row) ? "virtual-country-flag" : ""
                    }`}
                  >
                    {deposits ? (
                      <WalletCards />
                    ) : serviceLogo(row) ? (
                      <img
                        src={serviceLogo(row) ?? undefined}
                        alt={`${serviceInitial(row)} logo`}
                      />
                    ) : (
                      serviceInitial(row)
                    )}
                  </span>
                  <div>
                    <strong>
                      {deposits
                        ? "Wallet Funding"
                        : row.title || serviceName(row)}
                    </strong>
                    <small>
                      {deposits
                        ? row.senderAccountNumber || "—"
                        : row.recipient || "—"}
                    </small>
                  </div>
                </div>
                <button
                  className="activity-user activity-user-button"
                  onClick={() => matchedUser && setSelectedUser(matchedUser)}
                  disabled={!matchedUser}
                >
                  <span className="user-badge">
                    <LoadedAvatar
                      src={userPhoto}
                      alt={`${row.userName || "User"} profile`}
                      fallback={initials(row.userName)}
                    />
                  </span>
                  <div>
                    <strong>{row.userName || "Unknown User"}</strong>
                    <small>{row.userPhone || row.userEmail || "—"}</small>
                  </div>
                </button>
                <div>
                  <span
                    className={`activity-status ${success ? "completed" : failed ? "failed" : "pending"}`}
                  >
                    {success ? (
                      <CheckCircle2 />
                    ) : failed ? (
                      <XCircle />
                    ) : (
                      <Clock3 />
                    )}
                    {displayStatus}
                  </span>
                </div>
                <strong className={failed ? "failed-amount" : ""}>
                  {naira(row.total ?? row.amount)}
                </strong>
                <button
                  className="view-details"
                  onClick={() => setSelected(row)}
                >
                  <Eye /> View Details
                </button>
              </div>
            );
          })
        )}
      </div>
      {selected && (
        <TransactionPreview
          row={selected}
          deposits={deposits}
          auditLogs={auditLogs}
          runAction={runAction}
          onClose={() => setSelected(null)}
        />
      )}
      {selectedUser && (
        <UserProfileModal
          initialUser={selectedUser}
          runAction={runAction}
          onClose={() => setSelectedUser(null)}
        />
      )}
    </section>
  );
}

function initials(name?: string) {
  return (name || "U")
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function serviceInitial(row: Transaction) {
  const value =
    `${row.provider || ""} ${row.title || row.type || ""} ${row.network || ""} ${row.disco || ""} ${row.serviceID || ""}`.toLowerCase();
  if (value.includes("mtn")) return "MTN";
  if (value.includes("glo")) return "glo";
  if (value.includes("airtel")) return "airtel";
  if (value.includes("9mobile")) return "9mobile";
  return (row.title || row.type || "TX").slice(0, 2).toUpperCase();
}

function virtualCountryIso(row: Transaction) {
  const direct = String(
    row.countryIso || row.countryCode || row.country || "",
  ).trim().toLowerCase();
  if (/^[a-z]{2}$/.test(direct)) return direct;
  const value = `${row.countryName || ""} ${row.country || ""} ${row.title || ""}`.toLowerCase();
  if (value.includes("usa") || value.includes("united states")) return "us";
  if (value.includes("canada")) return "ca";
  if (value.includes("nigeria")) return "ng";
  if (value.includes("united kingdom") || /\buk\b/.test(value)) return "gb";
  if (value.includes("australia")) return "au";
  return "";
}

function serviceLogo(row: Transaction) {
  const value =
    `${row.provider || ""} ${row.title || row.type || ""} ${row.network || ""} ${row.disco || ""} ${row.serviceID || ""}`.toLowerCase();
  if (value.includes("mtn")) return mtnLogo;
  if (value.includes("glo")) return gloLogo;
  if (value.includes("airtel")) return airtelLogo;
  if (value.includes("9mobile") || value.includes("t2")) return nineMobileLogo;
  if (isVirtualNumberTransaction(row)) {
    const iso = virtualCountryIso(row);
    return iso ? `https://flagcdn.com/w80/${iso}.png` : null;
  }
  if (value.includes("aba") || value.includes("abapower")) return abaLogo;
  if (value.includes("ikeja") || value.includes("ikedc")) return ikejaLogo;
  if (value.includes("eko") || value.includes("ekedc")) return ekoLogo;
  return null;
}

function isReferralCommission(row: Transaction) {
  return String(row.type || "").toLowerCase() === "referral_withdrawal";
}

function nestedTransactionValue(
  source: unknown,
  names: string[],
): string | number | undefined {
  const wanted = new Set(names.map((name) => name.toLowerCase().replace(/[^a-z0-9]/g, "")));
  const seen = new Set<object>();
  const visit = (value: unknown): string | number | undefined => {
    if (!value || typeof value !== "object" || seen.has(value as object)) return undefined;
    seen.add(value as object);
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
      const normalized = key.toLowerCase().replace(/[^a-z0-9]/g, "");
      if (wanted.has(normalized) && (typeof child === "string" || typeof child === "number")) {
        if (String(child).trim()) return child;
      }
    }
    for (const child of Object.values(value as Record<string, unknown>)) {
      const found = visit(child);
      if (found !== undefined) return found;
    }
    return undefined;
  };
  return visit(source);
}

function isElectricityTransaction(row: Transaction) {
  const value = `${row.type || ""} ${row.title || ""}`.toLowerCase();
  return value.includes("electric") || value.includes("power");
}

function isVirtualNumberTransaction(row: Transaction) {
  const value = `${row.type || ""} ${row.title || ""}`.toLowerCase();
  return value.includes("virtual_number") || value.includes("virtual number") || value.includes("usa number");
}

function virtualSmsCode(row: Transaction) {
  const directSms =
    typeof row.sms === "string" || typeof row.sms === "number"
      ? String(row.sms).trim()
      : "";
  return (
    row.smsCode ||
    row.verificationCode ||
    directSms ||
    nestedTransactionValue(row.sms, [
      "code",
      "smsCode",
      "sms_code",
      "otp",
      "pin",
      "verificationCode",
      "verification_code",
    ]) ||
    nestedTransactionValue(row.providerResponse, [
      "smsCode",
      "sms_code",
      "otp",
      "pin",
      "verificationCode",
      "verification_code",
    ])
  );
}

function transactionRecipient(row: Transaction) {
  if (isElectricityTransaction(row)) {
    return row.meterNumber || nestedTransactionValue(row.providerResponse, ["meterNumber", "meter_number", "billersCode"]) || row.recipient || "—";
  }
  return row.recipient || "—";
}

function serviceName(row: Transaction) {
  const exactName = String(row.title || "").trim();
  if (exactName) return exactName;
  const type = (row.type || "Transaction").replaceAll("_", " ");
  return type.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function transactionAuditLabel(row: Transaction, log: Record<string, unknown>) {
  const action = String(log.action || "").trim();
  const normalized = action.toLowerCase();
  const exactService = serviceName(row);

  // Derive the service from the transaction itself. This also repairs the
  // display of legacy audits that were incorrectly saved as virtual-number
  // refunds by the old shared refund function.
  if (normalized.includes("declin") && normalized.includes("refund")) {
    return `${exactService} Declined And Refunded`;
  }
  if (normalized.includes("approv")) {
    return `${exactService} Approved`;
  }
  if (normalized.includes("reject")) {
    return `${exactService} Rejected`;
  }

  return (action || "Transaction action")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function TransactionPreview({
  row,
  deposits = false,
  auditLogs,
  runAction,
  onActionConfirm,
  onClose,
}: {
  row: Transaction;
  deposits?: boolean;
  auditLogs: Record<string, unknown>[];
  runAction?: RunAction;
  onActionConfirm?: (row: Transaction, action: "approve" | "reject") => void;
  onClose: () => void;
}) {
  const { adminDirectory } = useContext(AdminPermissionContext);
  const [requerying, setRequerying] = useState(false);
  const [acting, setActing] = useState<"approve" | "reject" | null>(null);
  const [localConfirmAction, setLocalConfirmAction] = useState<"approve" | "reject" | null>(null);
  const transactionId = String(row.txId || row.id);
  const success = ["success", "successful", "completed", "delivered"].includes(
    (row.status || "").toLowerCase(),
  );
  const failed = ["failed", "rejected", "cancelled"].includes(
    (row.status || "").toLowerCase(),
  );
  const normalizedStatus = String(row.status || "pending").trim().toLowerCase();
  const processing = normalizedStatus === "processing";
  const isAmbiguousPayPoint =
    String(row.provider || "").toLowerCase() === "paypoint" &&
    !success &&
    !failed;
  const isAdminPending = normalizedStatus === "pending";
  const isAirtime =
    (row.type || "").toLowerCase() === "airtime" ||
    (row.title || "").toLowerCase().includes("airtime");
  const receiptBalanceAfter =
    isAirtime &&
    row.cashbackCredited === true &&
    row.balanceAfterIncludesCashback !== true
      ? Number(row.balanceAfter ?? 0) + Number(row.cashbackAmount ?? 0)
      : row.balanceAfter;
  const isElectricity = isElectricityTransaction(row);
  const isVirtualNumber = isVirtualNumberTransaction(row);
  const deliveredVirtualCode = virtualSmsCode(row);
  const electricityToken =
    row.token || nestedTransactionValue(row.providerResponse, ["token", "purchasedCode", "purchased_code"]);
  const electricityUnits =
    row.units ?? nestedTransactionValue(row.providerResponse, ["units", "unit", "purchasedUnits"]);
  const electricityAddress =
    row.address || row.customerAddress || nestedTransactionValue(row.providerResponse, ["address", "customerAddress", "customer_address"]);
  const logs = auditLogs.filter((log) => {
    const action = String(log.action || "").toLowerCase();
    const isDecision =
      action.includes("approv") ||
      action.includes("reject") ||
      action.includes("declin") ||
      action.includes("refund");
    return (
      isDecision &&
      (String(log.txId || "") === transactionId ||
        String(log.txDocId || "") === row.id)
    );
  });
  const copy = (value: unknown) =>
    void copyToClipboard(String(value || ""), "Transaction detail");
  const requery = async () => {
    if (!runAction || requerying) return;
    setRequerying(true);
    try {
      await runAction(
        "adminRequeryTransaction",
        {
          uid: row.uid,
          id: row.id,
          txId: row.txId,
          providerRequestId: row.providerRequestId,
        },
        "Transaction status refreshed",
        false,
      );
    } finally {
      setRequerying(false);
    }
  };

  const requestAction = async (action: "approve" | "reject") => {
    if (acting) return;

    // TransactionTable supplies the branded confirmation modal. Other pages
    // also reuse this preview, so they need a self-contained fallback instead
    // of silently doing nothing when no callback was supplied.
    if (onActionConfirm) {
      onClose();
      onActionConfirm(row, action);
      return;
    }

    if (!runAction) {
      window.alert("This transaction action is unavailable. Refresh the page and try again.");
      return;
    }

    setLocalConfirmAction(action);
  };

  const confirmLocalAction = async () => {
    const action = localConfirmAction;
    if (!action || !runAction || acting) return;

    setActing(action);
    try {
      const referralWithdrawal = String(row.type || "").toLowerCase() === "referral_withdrawal";
      const completed = await runAction(
        referralWithdrawal
          ? "adminResolveReferralWithdrawal"
          : action === "reject"
            ? "adminRefundProcessingTransaction"
            : "adminResolveAirtimeReview",
        referralWithdrawal
          ? { id: row.withdrawalId || row.id, decision: action === "reject" ? "rejected" : "paid", note: action === "reject" ? "Declined and returned to referral balance" : "Approved for bank payout", action }
          : {
              action,
              uid: row.uid,
              id: row.id,
              txId: row.txId,
              providerRequestId: row.providerRequestId,
              reason: action === "reject" ? "Declined and refunded by admin" : "Approved by admin",
            },
        action === "reject"
          ? "Transaction rejected & refunded"
          : "Transaction approved successfully",
      );
      if (completed) {
        setLocalConfirmAction(null);
        onClose();
      }
    } finally {
      setActing(null);
    }
  };

  if (deposits) {
    return (
      <div className="modal-backdrop transaction-summary-backdrop" role="presentation" onMouseDown={onClose}>
        <div
          className="transaction-preview transaction-summary"
          role="dialog"
          aria-modal="true"
          aria-label="Deposit receipt"
          onMouseDown={(event) => event.stopPropagation()}
        >
          <div className="summary-head">
            <div>
              <h2>Transaction Summary</h2>
              <span className="summary-live">⚡ Live</span>
            </div>
            <button onClick={onClose}>Close</button>
          </div>
          <div className="summary-body">
            <div className="summary-status">
              <strong>Transaction Status</strong>
              <span
                className={`activity-status ${success ? "completed" : failed ? "failed" : "pending"}`}
              >
                {success ? <CheckCircle2 /> : failed ? <XCircle /> : <Clock3 />}
                {success
                  ? "Completed"
                  : failed
                    ? "Failed"
                    : row.status || "Pending"}
              </span>
            </div>
            <div className="summary-grid">
              <SummaryField
                label="Wallet Holder"
                value={row.walletHolderName || row.userName || "Unknown User"}
              />
              <SummaryField
                label="Customer ID"
                value={row.userId || row.uid || "—"}
                copy={() => copy(row.userId || row.uid)}
              />
              <SummaryField
                label="Email Address"
                value={row.userEmail || "—"}
                copy={() => copy(row.userEmail)}
              />
              <SummaryField
                label="Deposit ID"
                value={row.depositId || row.txId || row.id}
                copy={() => copy(row.depositId || row.txId || row.id)}
              />
              <SummaryField label="Transaction Title" value="Wallet Funding" />
              <SummaryField
                label="Recipient"
                value={row.sourceAccountNumber || row.senderAccountNumber || "—"}
              />
              <SummaryField
                label="Source Account Name"
                value={personName(row.sourceAccountName || row.senderName)}
              />
              <SummaryField label="Bal Before" value={naira(row.balanceBefore)} />
              <SummaryField
                label="Amount"
                value={naira(row.total ?? row.amount)}
              />
              <SummaryField label="Bal After" value={naira(row.balanceAfter)} />
            </div>
            <div className="summary-grid summary-grid-two">
              <SummaryField
                label="Transaction Time"
                value={row.createdAt ? depositDate.format(row.createdAt) : "—"}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <>
      <div className="modal-backdrop transaction-summary-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="transaction-preview transaction-summary"
        role="dialog"
        aria-modal="true"
        aria-label="Transaction details"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="summary-head">
          <div>
            <h2>Transaction Summary</h2>
            <span className="summary-live">⚡ Live</span>
          </div>
          <button onClick={onClose}>Close</button>
        </div>
        <div className="summary-body">
          <div className="summary-status">
            <strong>Transaction Status</strong>
            <span
              className={`activity-status ${success ? "completed" : failed ? "failed" : processing ? "processing" : "pending"}`}
            >
              {success ? <CheckCircle2 /> : failed ? <XCircle /> : <Clock3 />}
              {success
                ? "Completed"
                : failed
                  ? "Failed"
                  : processing ? "Processing" : row.status || "Pending"}
            </span>
          </div>
          <div className="summary-grid">
            <SummaryField
              label="Wallet Holder"
              value={row.walletHolderName || row.userName || "Unknown User"}
            />
            <SummaryField
              label="Customer ID"
              value={row.userId || row.uid || "—"}
              copy={() => copy(row.userId || row.uid)}
            />
            <SummaryField
              label="Email Address"
              value={row.userEmail || "—"}
              copy={() => copy(row.userEmail)}
            />
            <SummaryField
              label="Transaction ID"
              value={transactionId}
              copy={() => copy(transactionId)}
            />
            <SummaryField
              label="Transaction Title"
              value={row.title || serviceName(row)}
            />
            <SummaryField label="Recipient" value={transactionRecipient(row)} />
            <SummaryField label="Bal Before" value={naira(row.balanceBefore)} />
            <SummaryField
              label="Amount"
              value={naira(row.total ?? row.amount)}
            />
            <SummaryField label="Bal After" value={naira(receiptBalanceAfter)} />
          </div>
          <div className="summary-grid summary-grid-two">
            {!isAirtime && !isElectricity && !isVirtualNumber && (
              <SummaryField
                label="Package"
                value={row.package || row.planName || row.planCode || "—"}
              />
            )}
            {isVirtualNumber && (
              <SummaryField
                label="SMS Code"
                value={deliveredVirtualCode || "—"}
                copy={deliveredVirtualCode ? () => copy(deliveredVirtualCode) : undefined}
              />
            )}
            <SummaryField
              label="Transaction Time"
              value={row.createdAt ? date.format(row.createdAt) : "—"}
            />
          </div>
          {isElectricity && (electricityToken || electricityUnits || electricityAddress) && (
            <div className="summary-grid summary-electricity-details">
              {electricityToken && (
                <SummaryField
                  label="Token"
                  value={electricityToken}
                  copy={() => copy(electricityToken)}
                />
              )}
              {electricityUnits !== undefined && electricityUnits !== "" && (
                <SummaryField label="Units" value={electricityUnits} />
              )}
              {electricityAddress && (
                <SummaryField label="Address" value={electricityAddress} />
              )}
            </div>
          )}
          <section className="provider-response">
            <h3>Provider Responses</h3>
            {row.combinedPlan && row.combinedProviderResponses?.length ? (
              <div className="combined-response-list">
                {row.combinedProviderResponses.map((leg, index) => (
                  <article key={`${leg.requestId || index}`}>
                    <header><strong>{leg.order || index + 1}. {leg.name || "Component plan"}</strong><span className={`activity-status ${leg.status === "success" ? "completed" : leg.status === "failed" ? "failed" : "processing"}`}>{leg.status || "pending"}</span></header>
                    <p><b>{String(leg.provider || "provider").toUpperCase()}</b> · {leg.message || "No response message"}</p>
                    <small>Reference: {leg.providerReference || leg.requestId || "—"}</small>
                    <details><summary>Raw API response</summary><pre>{JSON.stringify(leg.response ?? {}, null, 2)}</pre></details>
                  </article>
                ))}
              </div>
            ) : <p>{row.providerResponseMessage || "No provider response recorded."}</p>}
          </section>
          <button
            className="requery-summary"
            onClick={() => void requery()}
            disabled={requerying}
            aria-busy={requerying}
          >
            {requerying ? <Loader2 className="spin" /> : <RefreshCw />}
            {requerying ? "Requerying transaction…" : "Requery transaction"}
          </button>

          {(!success && !failed) && (
            <div className="pending-preview-actions" style={{ display: "flex", gap: 12, marginTop: 16 }}>
              <button
                className="btn-approve"
                style={{ flex: 1, padding: "12px 16px", borderRadius: 10, background: "#10b981", color: "#fff", border: "none", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                onClick={() => void requestAction("approve")}
                disabled={acting !== null}
              >
                {acting === "approve" ? <Loader2 className="spin" /> : <Check className="w-4 h-4" />}
                {acting === "approve"
                  ? isAmbiguousPayPoint ? "Confirming…" : "Approving…"
                  : isAmbiguousPayPoint ? "Confirm Delivered" : "Approve & Complete"}
              </button>
              <button
                className="btn-decline"
                style={{ flex: 1, padding: "12px 16px", borderRadius: 10, background: "#ef4444", color: "#fff", border: "none", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                onClick={() => void requestAction("reject")}
                disabled={acting !== null}
              >
                {acting === "reject" ? <Loader2 className="spin" /> : <X className="w-4 h-4" />}
                {acting === "reject" ? "Refunding…" : "Decline & Refund"}
              </button>
            </div>
          )}
          <section className="summary-audits">
            <h3>Audit Logs</h3>
            {logs.length ? (
              logs.map((log, index) => {
                const admin = adminDirectory.find((item) =>
                  (log.adminUid && String(item.uid || "") === String(log.adminUid)) ||
                  (log.adminEmail && String(item.email || "").toLowerCase() === String(log.adminEmail).toLowerCase())
                );
                const storedName = String(log.adminName || "").trim();
                const adminName = String(
                  admin?.name ||
                  (!storedName.includes("@") ? storedName : "") ||
                  "Admin",
                );
                return (
                <article key={String(log.id || index)}>
                  <div>
                    <strong>
                      {transactionAuditLabel(row, log)}
                    </strong>
                    <time>
                      {log.createdAt ? date.format(Number(log.createdAt)) : "—"}
                    </time>
                  </div>
                  <b>
                    Admin: {adminName}
                  </b>
                  <p>Reason: {String(log.reason || "No reason provided")}</p>
                </article>
                );
              })
            ) : (
              <p className="no-audits">
                No audit actions recorded for this transaction.
              </p>
            )}
          </section>
        </div>
      </div>
      </div>
      <ConfirmTransactionModal
        target={localConfirmAction ? { row, action: localConfirmAction } : null}
        onClose={() => {
          if (!acting) setLocalConfirmAction(null);
        }}
        onConfirm={() => void confirmLocalAction()}
        loading={acting !== null}
      />
    </>
  );
}

function SummaryField({
  label,
  value,
  copy,
}: {
  label: string;
  value: ReactNode;
  copy?: () => void;
}) {
  return (
    <div className="summary-field">
      <span>{label}</span>
      <strong>
        {value}
        {copy && (
          <button onClick={copy} aria-label={`Copy ${label}`}>
            <Copy />
          </button>
        )}
      </strong>
    </div>
  );
}

function formatKycStatus(status?: string) {
  const normalized = String(status || "not_submitted")
    .trim()
    .toLowerCase();
  if (["not_submitted", "not submitted", "none", ""].includes(normalized)) {
    return "KYC Not Submitted";
  }
  return `KYC ${normalized
    .replaceAll("_", " ")
.replace(/\b\w/g, (letter) => letter.toUpperCase())}`;
}

type RunAction = (
  name: string,
  payload: Record<string, unknown>,
  success: string,
  refreshDashboard?: boolean,
) => Promise<boolean>;

function Transactions({
  rows,
  users,
  auditLogs,
  mode,
  runAction,
  onSearch,
}: {
  rows: Transaction[];
  users?: AdminUser[];
  auditLogs: Record<string, unknown>[];
  mode: string;
  runAction?: RunAction;
  onSearch?: (term: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [serviceType, setServiceType] = useState("all");

  const matchServiceType = (row: Transaction, typeFilter: string) => {
    if (typeFilter === "all") return true;
    const type = String(row.type || "").trim().toLowerCase();
    const title = String(row.title || "").trim().toLowerCase();
    const planName = String(row.planName || "").trim().toLowerCase();
    const pkg = String(row.package || "").trim().toLowerCase();
    const fullText = `${type} ${title} ${planName} ${pkg}`.toLowerCase();

    switch (typeFilter) {
      case "airtime":
        return fullText.includes("airtime") || type === "vtu" || fullText.includes("recharge");
      case "data":
        return fullText.includes("data") || fullText.includes("gb") || fullText.includes("mb");
      case "tv":
        return (
          fullText.includes("tv") ||
          fullText.includes("cable") ||
          fullText.includes("dstv") ||
          fullText.includes("gotv") ||
          fullText.includes("startimes") ||
          fullText.includes("showmax")
        );
      case "electricity":
        return (
          fullText.includes("electric") ||
          fullText.includes("power") ||
          fullText.includes("meter") ||
          fullText.includes("nepa") ||
          fullText.includes("ikedc") ||
          fullText.includes("ekedc") ||
          fullText.includes("ibedc") ||
          fullText.includes("aedc")
        );
      case "education":
        return (
          fullText.includes("edu") ||
          fullText.includes("waec") ||
          fullText.includes("jamb") ||
          fullText.includes("neco") ||
          fullText.includes("result")
        );
      case "virtual_number":
        return (
          fullText.includes("virtual") ||
          fullText.includes("sms") ||
          fullText.includes("temp num") ||
          fullText.includes("otp")
        );
      default:
        return fullText.includes(typeFilter);
    }
  };

  useEffect(() => {
    const q = query.trim();
    if (!q) return;
    const timer = setTimeout(() => {
      onSearch?.(q);
    }, 450);
    return () => clearTimeout(timer);
  }, [query, onSearch]);

  const userMap = useMemo(() => buildUserMap(users), [users]);

  const filtered = useMemo(() => {
    const base = rows.filter((row) => {
      const transactionType = String(row.type || "").trim().toLowerCase();
      if (isWalletAdjustmentTransaction(row)) {
        return false;
      }
      const isDep = isDepositTransaction(row);
      if (mode === "transactions" && isDep) return false;
      if (mode === "transactions" && isPendingTransactionRecord(row)) return false;
      if (mode === "deposits" && !isDep) return false;
      if (mode === "pending" && isDep) return false;
      if (mode === "pending" && !isPendingTransactionRecord(row)) return false;

      if (mode !== "pending" && status !== "all" && row.status !== status) return false;
      if (!matchServiceType(row, serviceType)) return false;
      return true;
    });

    const q = query.trim().toLowerCase();
    if (!q) return base;

    const matchesTxId = (row: Transaction) => {
      const id = String(row.id || "").toLowerCase();
      const txId = String(row.txId || "").toLowerCase();
      const depId = String(row.depositId || "").toLowerCase();
      const reqId = String(row.providerRequestId || "").toLowerCase();
      return (
        id === q ||
        txId === q ||
        depId === q ||
        reqId === q ||
        (q.length >= 3 && (id.startsWith(q) || txId.startsWith(q) || depId.startsWith(q)))
      );
    };

    const hasTxIdMatch = base.some(matchesTxId);
    if (hasTxIdMatch) {
      return base.filter(matchesTxId);
    }

    return base.filter((row) => {
      const userObj = currentUserForTransaction(userMap, row);

      const userName = userObj?.name || row.userName || "";
      const userEmail = userObj?.email || row.userEmail || "";
      const userPhone = userObj?.phone || row.userPhone || "";
      const recipient = transactionRecipient(row);
      const txId = row.txId || row.id || row.depositId || "";
      const title = row.title || "";
      const provider = row.provider || "";

      const haystack =
        `${txId} ${row.id} ${row.depositId || ""} ${userName} ${userEmail} ${userPhone} ${recipient} ${title} ${provider}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, mode, query, status, serviceType, userMap]);

  const cardTitle =
    mode === "deposits"
      ? "All Deposits"
      : mode === "pending"
      ? "Pending Transactions"
      : "All Transactions";

  return (
    <div className="pending-page-wrap">
      {mode === "pending" && (
        <div className="pending-summary-card">
          <h3>Pending Transactions</h3>
          <p>{filtered.length} awaiting resolution</p>
          <div className="search">
            <Search />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Exact transaction ID, phone or email"
            />
          </div>
        </div>
      )}

      <div className="panel tx-page-panel">
        <div className="activity-heading">
          <h2>{cardTitle}</h2>
          <span className="activity-live">
            <i /> Live Sync
          </span>
        </div>
        {mode !== "pending" && (
          <div className="toolbar">
            <select
              value={serviceType}
              onChange={(e) => setServiceType(e.target.value)}
              title="Filter by service type"
            >
              <option value="all">all</option>
              <option value="airtime">airtime</option>
              <option value="data">data</option>
              <option value="tv">tv</option>
              <option value="electricity">electricity</option>
              <option value="education">education</option>
              <option value="virtual_number">virtual number</option>
            </select>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="all">All statuses</option>
              <option value="success">Successful</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="failed">Failed</option>
            </select>
            <div className="search">
              <Search />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Exact transaction ID, phone or email"
              />
            </div>
          </div>
        )}
        <TransactionTable
          rows={filtered}
          users={users}
          auditLogs={auditLogs}
          mode={mode}
          runAction={runAction}
        />
      </div>
    </div>
  );
}

function DepositReceiptModal({
  row,
  userMap,
  onClose,
}: {
  row: Transaction | null;
  userMap: Map<string, AdminUser>;
  onClose: () => void;
}) {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  if (!row) return null;

  const userObj = currentUserForTransaction(userMap, row);

  const userName = personName(userObj?.name || row.walletHolderName || row.userName);
  const customerId = userObj?.customerNo || userObj?.userId || row.userId || row.uid || "—";
  const userEmail = row.userEmail || userObj?.email || "—";
  const title = row.title || "Wallet Funding";
  const recipient = row.recipient || row.sourceAccountNumber || row.senderAccountNumber || row.userPhone || "—";
  const sourceName = personName(row.sourceAccountName || row.senderName);

  const balBefore = row.balanceBefore ?? row.walletBalanceBefore;
  const balAfter = row.balanceAfter ?? row.walletBalanceAfter;
  const amt = row.total ?? row.amount ?? 0;

  const formatK = (n?: number | null): string => {
    if (n === undefined || n === null) return "—";
    const amountInNaira = depositNaira(n);
    if (amountInNaira >= 1000) {
      const k = (amountInNaira / 1000).toFixed(2).replace(/\.?0+$/, "");
      return `₦ ${k}K`;
    }
    return `₦ ${amountInNaira.toFixed(2)}`;
  };

  const isSuccess = ["success", "successful", "completed", "delivered"].includes(
    String(row.status || "").toLowerCase()
  );
  const isFailed = ["failed", "error", "rejected"].includes(
    String(row.status || "").toLowerCase()
  );

  const copyText = (text: string, label: string) => {
    if (text === "—") return;
    void navigator.clipboard.writeText(text);
    setCopiedField(label);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div className="receipt-modal-overlay" onClick={onClose}>
      <div className="receipt-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="receipt-modal-header">
          <div className="receipt-header-title">
            <h3>Transaction Summary</h3>
            <span className="receipt-live-badge">⚡ Live</span>
          </div>
          <button className="receipt-close-btn" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="receipt-modal-body">
          <div className="receipt-status-box">
            <span className="receipt-status-label">Transaction Status</span>
            <span
              className={`receipt-status-badge ${
                isSuccess ? "completed" : isFailed ? "failed" : "pending"
              }`}
            >
              {isSuccess ? "✔ Completed" : isFailed ? "⊗ Failed" : "⏳ Pending"}
            </span>
          </div>

          <div className="receipt-grid">
            <div className="receipt-grid-cell">
              <span className="receipt-label">Wallet Holder</span>
              <strong className="receipt-value">{userName}</strong>
            </div>

            <div className="receipt-grid-cell">
              <span className="receipt-label">Customer ID</span>
              <strong className="receipt-value copyable">
                {customerId}{" "}
                <button
                  className="copy-icon-btn"
                  onClick={() => copyText(String(customerId), "customerId")}
                  title="Copy Customer ID"
                >
                  <Copy size={13} />
                </button>
                {copiedField === "customerId" && <span className="copied-tip">Copied</span>}
              </strong>
            </div>

            <div className="receipt-grid-cell">
              <span className="receipt-label">Email Address</span>
              <strong className="receipt-value copyable">
                {userEmail}{" "}
                <button
                  className="copy-icon-btn"
                  onClick={() => copyText(userEmail, "userEmail")}
                  title="Copy Email"
                >
                  <Copy size={13} />
                </button>
                {copiedField === "userEmail" && <span className="copied-tip">Copied</span>}
              </strong>
            </div>

            <div className="receipt-grid-cell">
              <span className="receipt-label">Transaction Title</span>
              <strong className="receipt-value">{title}</strong>
            </div>

            <div className="receipt-grid-cell">
              <span className="receipt-label">Recipient</span>
              <strong className="receipt-value">{recipient}</strong>
            </div>

            <div className="receipt-grid-cell">
              <span className="receipt-label">Source Account Name</span>
              <strong className="receipt-value">{sourceName}</strong>
            </div>

            <div className="receipt-section-divider" />

            <div className="receipt-grid-cell">
              <span className="receipt-label">Bal Before</span>
              <strong className="receipt-value">{formatK(balBefore)}</strong>
            </div>

            <div className="receipt-grid-cell">
              <span className="receipt-label">Amount</span>
              <strong className="receipt-value">{formatK(amt)}</strong>
            </div>

            <div className="receipt-grid-cell">
              <span className="receipt-label">Bal After</span>
              <strong className="receipt-value">{formatK(balAfter)}</strong>
            </div>

            <div className="receipt-section-divider" />

            <div className="receipt-grid-cell full-width">
              <span className="receipt-label">Transaction Time</span>
              <strong className="receipt-value">{formatDate(row.createdAt)}</strong>
            </div>
          </div>

          <div className="receipt-provider-box">
            <h4>Provider Responses</h4>
            <div className="provider-response-json">
              {row.providerResponseMessage || "—"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RequeryDepositModal({
  isOpen,
  onClose,
  runAction,
}: {
  isOpen: boolean;
  onClose: () => void;
  runAction?: RunAction;
}) {
  const [depositIdInput, setDepositIdInput] = useState("");
  const [provider, setProvider] = useState("monnify");
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!depositIdInput.trim()) return;
    setLoading(true);
    try {
      await runAction?.(
        "adminRequeryTransaction",
        { txId: depositIdInput.trim(), provider },
        "Deposit requery submitted"
      );
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="confirm-modal-overlay" onClick={() => !loading && onClose()}>
      <div className="confirm-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="confirm-modal-header">
          <div className="confirm-icon-circle approve">
            <RotateCw />
          </div>
          <div>
            <h3>Requery Deposit</h3>
            <p>Enter Deposit Reference / ID from deposits counter to verify status</p>
          </div>
        </div>

        <div style={{ margin: "16px 0", display: "flex", flexDirection: "column", gap: "12px" }}>
          <div>
            <label style={{ fontSize: "13px", fontWeight: 600, color: "var(--text-color, #475569)", display: "block", marginBottom: 4 }}>
              Deposit ID / Reference
            </label>
            <input
              type="text"
              value={depositIdInput}
              onChange={(e) => setDepositIdInput(e.target.value)}
              placeholder="e.g. 8, 7, 6 or 21865166"
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                fontSize: "14px",
                outline: "none"
              }}
            />
          </div>

          <div>
            <label style={{ fontSize: "13px", fontWeight: 600, color: "var(--text-color, #475569)", display: "block", marginBottom: 4 }}>
              Gateway / Provider
            </label>
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                fontSize: "14px",
                outline: "none"
              }}
            >
              <option value="monnify">Monnify</option>
              <option value="paystack">Paystack</option>
              <option value="palmpay">PalmPay</option>
              <option value="opay">OPay</option>
            </select>
          </div>
        </div>

        <div className="confirm-modal-actions">
          <button className="confirm-btn-cancel" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            className="confirm-btn-submit approve"
            onClick={handleSubmit}
            disabled={loading || !depositIdInput.trim()}
          >
            {loading ? "Requerying..." : "Submit Requery"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DepositsPage({
  rows,
  users,
  totalDeposits,
  runAction,
  onSearch,
}: {
  rows: Transaction[];
  users?: AdminUser[];
  totalDeposits?: number;
  runAction?: RunAction;
  onSearch?: (q: string) => void;
}) {
  const [statusFilter, setStatusFilter] = useState("all");
  const [topSearch, setTopSearch] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [selectedDep, setSelectedDep] = useState<Transaction | null>(null);
  const [showRequeryModal, setShowRequeryModal] = useState(false);
  const [sortField, setSortField] = useState("createdAt");
  const [sortAsc, setSortAsc] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const userMap = useMemo(() => buildUserMap(users), [users]);

  const depositRows = useMemo(() => {
    return rows.filter((r) => isDepositTransaction(r));
  }, [rows]);

  const depositSearchText = (row: Transaction): string => {
    const userObj = currentUserForTransaction(userMap, row);
    return [
      row.id,
      row.txId,
      row.transactionId,
      row.reference,
      row.providerRef,
      row.providerRequestId,
      row.depositId,
      row.depositNo,
      row.userEmail,
      userObj?.email,
      row.userPhone,
      userObj?.phone,
      row.recipient,
      row.sourceAccountNumber,
      row.senderAccountNumber,
    ]
      .map((value) => String(value || "").toLowerCase())
      .join(" ");
  };

  const filteredDeposits = useMemo(() => {
    return depositRows.filter((row) => {
      if (statusFilter !== "all") {
        const st = String(row.status || "").toLowerCase();
        if (statusFilter === "completed" || statusFilter === "success") {
          if (!["success", "successful", "completed", "delivered"].includes(st)) return false;
        } else if (statusFilter === "failed") {
          if (!["failed", "error", "rejected"].includes(st)) return false;
        } else if (statusFilter === "pending") {
          if (!["pending", "processing", "review", "in_review"].includes(st)) return false;
        }
      }

      const tq = topSearch.trim().toLowerCase();
      if (tq && !depositSearchText(row).includes(tq)) return false;

      const q = tableSearch.trim().toLowerCase();
      if (q && !depositSearchText(row).includes(q)) return false;

      return true;
    });
  }, [depositRows, statusFilter, topSearch, tableSearch, userMap]);

  const parseTxTimestamp = (tx: Transaction): number => {
    const c: any = tx.createdAt;
    if (!c) return 0;
    if (typeof c === "number") return c;
    if (typeof c === "string") {
      const parsed = Date.parse(c);
      if (!isNaN(parsed)) return parsed;
    }
    if (typeof c === "object") {
      if (typeof c.toMillis === "function") return c.toMillis();
      if (typeof c.seconds === "number") return c.seconds * 1000;
      if (typeof c._seconds === "number") return c._seconds * 1000;
    }
    return 0;
  };

  const getDepositCounterId = (row: Transaction): string => {
    if (row.depositId !== undefined && row.depositId !== null && String(row.depositId).trim() !== "") {
      return String(row.depositId);
    }
    if (row.depositNo !== undefined && row.depositNo !== null && String(row.depositNo).trim() !== "") {
      return String(row.depositNo);
    }
    return "—";
  };

  const sortedDeposits = useMemo(() => {
    return [...filteredDeposits].sort((a, b) => {
      let valA: any = 0;
      let valB: any = 0;

      if (sortField === "createdAt") {
        valA = parseTxTimestamp(a);
        valB = parseTxTimestamp(b);
      } else if (sortField === "depositId") {
        valA = Number(a.depositId ?? a.depositNo ?? 0);
        valB = Number(b.depositId ?? b.depositNo ?? 0);
      } else if (sortField === "userName") {
        valA = (a.userName || a.userEmail || "").toLowerCase();
        valB = (b.userName || b.userEmail || "").toLowerCase();
      } else if (sortField === "amount") {
        valA = a.total ?? a.amount ?? 0;
        valB = b.total ?? b.amount ?? 0;
      }

      if (valA < valB) return sortAsc ? -1 : 1;
      if (valA > valB) return sortAsc ? 1 : -1;
      return 0;
    });
  }, [filteredDeposits, sortField, sortAsc]);

  const totalPages = Math.max(1, Math.ceil(sortedDeposits.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageStart = sortedDeposits.length === 0 ? 0 : safePage * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, sortedDeposits.length);
  const pageRows = sortedDeposits.slice(pageStart, pageEnd);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(false);
    }
    setPage(0);
  };

  const renderSortIndicator = (field: string) => {
    if (sortField !== field) return <span style={{ opacity: 0.4, marginLeft: 4 }}>⇅</span>;
    return <span style={{ marginLeft: 4 }}>{sortAsc ? "↑" : "↓"}</span>;
  };

  return (
    <div className="deposits-page-wrap">
      <DepositReceiptModal
        row={selectedDep}
        userMap={userMap}
        onClose={() => setSelectedDep(null)}
      />

      <RequeryDepositModal
        isOpen={showRequeryModal}
        onClose={() => setShowRequeryModal(false)}
        runAction={runAction}
      />

      {/* Top Card */}
      <div className="deposits-top-card">
        <div className="deposits-top-header">
          <h3>Deposits</h3>
          <p>
            {depositRows.length} loaded
            {Number(totalDeposits || 0) > depositRows.length
              ? ` of ${Number(totalDeposits).toLocaleString("en-NG")} total`
              : ""}
          </p>
        </div>

        <div className="deposits-top-filters">
          <div className="deposits-status-filter">
            <span>Status</span>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              <option value="all">all</option>
              <option value="completed">completed</option>
              <option value="pending">pending</option>
              <option value="failed">failed</option>
            </select>
          </div>

          <div className="deposits-top-search">
            <Search size={16} />
            <input
              type="text"
              value={topSearch}
              onChange={(e) => {
                setTopSearch(e.target.value);
                onSearch?.(e.target.value);
              }}
              placeholder="Search reference, deposit ID, email or phone"
            />
          </div>
        </div>
      </div>

      {/* All Deposits Card */}
      <div className="deposits-table-card">
        <div className="deposits-card-header">
          <div className="deposits-card-title">All Deposits</div>
        </div>

        <div className="deposits-toolbar-row">
          <div className="deposits-toolbar-left">
            <button className="dep-btn-filter" onClick={() => setStatusFilter("all")}>
              <SlidersHorizontal size={14} /> Filters
            </button>
            <button className="dep-btn-requery" onClick={() => setShowRequeryModal(true)}>
              <RotateCw size={14} /> Requery Deposit
            </button>
            <button className="dep-btn-pdf" onClick={() => window.print()}>
              <FileText size={14} /> Export PDF
            </button>
            <div className="dep-page-nav">
              <label className="dep-page-size">
                <span>Rows</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(0);
                  }}
                  aria-label="Rows per page"
                >
                  {[10, 25, 50, 100].map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </label>
              <span className="dep-page-count">
                {sortedDeposits.length ? `${pageStart + 1}–${pageEnd}` : "0"} of {sortedDeposits.length}
              </span>
              <button
                className="dep-page-btn"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={safePage === 0}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                className="dep-page-btn"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={safePage >= totalPages - 1}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          <div className="deposits-toolbar-right">
            <div className="deposits-table-search">
              <Search size={16} />
              <input
                type="text"
                value={tableSearch}
                onChange={(e) => {
                  setTableSearch(e.target.value);
                  setPage(0);
                  onSearch?.(e.target.value);
                }}
                placeholder="Search reference, deposit ID, email or phone"
              />
            </div>
            <button className="dep-btn-export" onClick={() => alert("Exporting deposits data...")}>
              <FileDown size={14} /> Export Data
            </button>
          </div>
        </div>

        <div className="table-wrap">
          <table className="dep-table-custom">
            <thead>
              <tr>
                <th onClick={() => handleSort("depositId")} style={{ cursor: "pointer" }}>
                  ID {renderSortIndicator("depositId")}
                </th>
                <th onClick={() => handleSort("userName")} style={{ cursor: "pointer" }}>
                  User {renderSortIndicator("userName")}
                </th>
                <th>Bank</th>
                <th onClick={() => handleSort("amount")} style={{ cursor: "pointer" }}>
                  Amount {renderSortIndicator("amount")}
                </th>
                <th>Source Acct Name</th>
                <th>Source Acct No</th>
                <th onClick={() => handleSort("createdAt")} style={{ cursor: "pointer" }}>
                  Time {renderSortIndicator("createdAt")}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row) => {
                const depCounterId = getDepositCounterId(row);
                const userObj = currentUserForTransaction(userMap, row);

                const userName = personName(userObj?.name || row.walletHolderName || row.userName) || "User";
                const userEmail = userObj?.email || row.userEmail || "—";
                const userPhoto = userAvatarSrc(
                  userObj ? userObj.photoUrl : row.photoUrl,
                  userObj ? userObj.avatarId : row.avatarId,
                  userObj?.name || row.userName,
                );
                const bank = depositBankName(depositSourceName(row));
                const amt = row.total ?? row.amount ?? 0;
                const srcName = personName(row.sourceAccountName || row.senderName);
                const srcNo = row.sourceAccountNumber || row.senderAccountNumber || "—";

                return (
                  <tr key={`${row.uid}-${row.id}`}>
                    <td className="dep-id-cell">{depCounterId}</td>
                    <td>
                      <div className="dep-user-info">
                        <span className="dep-user-avatar">
                          <LoadedAvatar src={userPhoto} fallback={initials(userName)} />
                        </span>
                        <div className="dep-user-text">
                          <strong>{userName}</strong>
                          <small>{userEmail}</small>
                        </div>
                      </div>
                    </td>
                    <td className="dep-bank-badge">{bank}</td>
                    <td className="dep-amount">₦{depositNaira(amt).toFixed(2)}</td>
                    <td className="dep-source-acct">{srcName}</td>
                    <td>
                      <span className="dep-acct-no">
                        {srcNo}{" "}
                        {srcNo !== "—" && (
                          <button
                            className="dep-copy-icon"
                            onClick={() => void copyToClipboard(srcNo, "Account number")}
                            title="Copy Account Number"
                          >
                            <Copy size={13} />
                          </button>
                        )}
                      </span>
                    </td>
                    <td>{formatDate(row.createdAt)}</td>
                    <td>
                      <button className="dep-eye-btn" onClick={() => setSelectedDep(row)} title="View Receipt">
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ConfirmTransactionModal({
  target,
  onClose,
  onConfirm,
  loading,
}: {
  target: { row: Transaction; action: "approve" | "reject" } | null;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
}) {
  if (!target) return null;
  const { row, action } = target;
  const isApprove = action === "approve";

  const rawTitle = String(row.title || "").trim();
  const type = String(row.type || "").toLowerCase();
  const isReferralWithdrawal = type === "referral_withdrawal";
  const source = `${row.network || ""} ${row.provider || ""} ${rawTitle} ${type}`.toLowerCase();
  let network = "";
  if (source.includes("mtn")) network = "MTN";
  else if (source.includes("airtel")) network = "Airtel";
  else if (source.includes("glo")) network = "Glo";
  else if (source.includes("9mobile") || source.includes("etisalat")) network = "9mobile";

  let title = rawTitle || row.type || "Transaction";
  if (type.includes("data") || rawTitle.toLowerCase().includes("data")) {
    title = `${network ? network + " " : ""}Data`;
  } else if (type.includes("airtime") || rawTitle.toLowerCase().includes("airtime")) {
    title = `${network ? network + " " : ""}Airtime`;
  }

  const recipient = row.recipient || row.userPhone || row.sourceAccountNumber || "—";
  const user = row.userName || "User";
  const userPhone = row.userPhone || row.userEmail || "";
  const amt = row.total ?? row.amount ?? 0;

  return (
    <div className="confirm-modal-overlay" onClick={() => !loading && onClose()}>
      <div className="confirm-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="confirm-modal-header">
          <div className={`confirm-icon-circle ${isApprove ? "approve" : "reject"}`}>
            {isApprove ? <CheckCircle2 /> : <XCircle />}
          </div>
          <div>
            <h3>{isApprove ? (isReferralWithdrawal ? "Approve Bank Payout?" : "Approve Transaction?") : "Decline & Refund Transaction?"}</h3>
            <p>Please confirm your intent before proceeding</p>
          </div>
        </div>

        <div className="confirm-details-box">
          <div className="confirm-detail-row">
            <span>Service</span>
            <strong>{title}</strong>
          </div>
          <div className="confirm-detail-row">
            <span>Recipient</span>
            <strong>{recipient}</strong>
          </div>
          <div className="confirm-detail-row">
            <span>Customer</span>
            <strong>{user} {userPhone ? `(${userPhone})` : ""}</strong>
          </div>
          <div className="confirm-detail-row">
            <span>Amount</span>
            <strong style={{ fontSize: "16px", color: isApprove ? "#10b981" : "#ef4444" }}>
              {naira(amt)}
            </strong>
          </div>
        </div>

        <div className={`confirm-notice-banner ${isApprove ? "approve" : "reject"}`}>
          {isApprove ? (
            <>{isReferralWithdrawal ? "This will submit the verified bank payout. The receipt will update automatically when accepted." : "Are you sure you want to approve this transaction and mark it as completed?"}</>
          ) : (
            <>
              Are you sure you want to decline this transaction? <strong>{naira(amt)}</strong> will be automatically refunded back to {user}'s {isReferralWithdrawal ? "referral balance" : "wallet balance"}.
            </>
          )}
        </div>

        <div className="confirm-modal-actions">
          <button className="confirm-btn-cancel" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            className={`confirm-btn-submit ${isApprove ? "approve" : "reject"}`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" style={{ width: 16, height: 16 }} /> Processing...
              </>
            ) : isApprove ? (
              <>Approve Transaction</>
            ) : (
              <>Decline & Refund</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function TransactionTable({
  rows,
  users,
  auditLogs,
  mode,
  runAction,
}: {
  rows: Transaction[];
  users?: AdminUser[];
  auditLogs: Record<string, unknown>[];
  mode: string;
  runAction?: RunAction;
}) {
  const { canViewTransactionProviders } = useContext(AdminPermissionContext);
  const [selected, setSelected] = useState<Transaction | null>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<{ row: Transaction; action: "approve" | "reject" } | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);

  const handleConfirmAction = async () => {
    if (!confirmTarget) return;
    const { row, action } = confirmTarget;
    const targetId = String(row.id || row.txId || "");
    setConfirmLoading(true);
    setProcessingId(targetId);
    try {
      const referralWithdrawal = String(row.type || "").toLowerCase() === "referral_withdrawal";
      const completed = await runAction?.(
        referralWithdrawal
          ? "adminResolveReferralWithdrawal"
          : action === "reject" ? "adminRefundProcessingTransaction" : "adminResolveAirtimeReview",
        referralWithdrawal
          ? { id: row.withdrawalId || row.id, decision: action === "reject" ? "rejected" : "paid", note: action === "reject" ? "Declined and returned to referral balance" : "Approved for bank payout", action }
          : {
              action,
              uid: row.uid,
              id: row.id,
              txId: row.txId,
              providerRequestId: row.providerRequestId,
              reason: action === "reject" ? "Declined and refunded by admin" : "Approved by admin",
            },
        action === "approve" ? "Approval processed — live status refreshed" : "Transaction rejected & refunded"
      );
      if (!completed) return;
      setSelected(null);
      setConfirmTarget(null);
    } finally {
      setConfirmLoading(false);
      setProcessingId(null);
    }
  };
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortAsc, setSortAsc] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(25);
  const [page, setPage] = useState<number>(0);

  const userMap = useMemo(() => buildUserMap(users), [users]);

  const getTxTitle = (tx: Transaction): string => {
    const rawTitle = String(tx.title || "").trim();
    const type = String(tx.type || "").toLowerCase();
    const source = `${tx.network || ""} ${tx.provider || ""} ${rawTitle} ${type}`.toLowerCase();

    let network = "";
    if (source.includes("mtn")) network = "MTN";
    else if (source.includes("airtel")) network = "Airtel";
    else if (source.includes("glo")) network = "Glo";
    else if (source.includes("9mobile") || source.includes("etisalat")) network = "9mobile";

    if (type.includes("data") || rawTitle.toLowerCase().includes("data")) {
      return `${network ? network + " " : ""}Data`;
    }
    if (type.includes("airtime") || rawTitle.toLowerCase().includes("airtime")) {
      return `${network ? network + " " : ""}Airtime`;
    }
    if (type.includes("virtual") || rawTitle.toLowerCase().includes("virtual")) {
      return "Virtual Number";
    }
    if (type === "fund" || type.includes("deposit") || type === "funding") {
      const bank = tx.bankName || tx.senderName || "";
      return bank ? `${bank} Deposit` : "Wallet Funding";
    }
    return rawTitle || tx.type || "Transaction";
  };

  const getRecipient = (tx: Transaction): string => {
    if (isElectricityTransaction(tx)) return String(transactionRecipient(tx));
    return tx.recipient || tx.userPhone || tx.sourceAccountNumber || tx.senderAccountNumber || "—";
  };

  const parseTxTimestamp = (tx: Transaction): number => {
    const c: any = tx.createdAt;
    if (!c) {
      const idNum = Number(tx.txId || tx.id);
      if (!isNaN(idNum) && idNum > 1000000000) return idNum;
      return 0;
    }
    if (typeof c === "number") return c;
    if (typeof c === "string") {
      const parsed = Date.parse(c);
      if (!isNaN(parsed)) return parsed;
      const num = Number(c);
      if (!isNaN(num)) return num;
    }
    if (typeof c === "object") {
      if (typeof c.toMillis === "function") return c.toMillis();
      if (typeof c.seconds === "number") return c.seconds * 1000;
      if (typeof c._seconds === "number") return c._seconds * 1000;
    }
    return 0;
  };

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      if (sortField === "createdAt") {
        const timeA = parseTxTimestamp(a);
        const timeB = parseTxTimestamp(b);
        if (timeA !== timeB) {
          return sortAsc ? timeA - timeB : timeB - timeA;
        }
      }

      let valA: any = a[sortField as keyof Transaction];
      let valB: any = b[sortField as keyof Transaction];

      if (sortField === "title") {
        valA = getTxTitle(a);
        valB = getTxTitle(b);
      } else if (sortField === "userName") {
        valA = a.userName || a.userEmail || "";
        valB = b.userName || b.userEmail || "";
      } else if (sortField === "amount") {
        valA = a.total ?? a.amount ?? 0;
        valB = b.total ?? b.amount ?? 0;
      } else if (sortField === "status") {
        valA = a.status || "";
        valB = b.status || "";
      }

      if (valA < valB) return sortAsc ? -1 : 1;
      if (valA > valB) return sortAsc ? 1 : -1;
      return 0;
    });
  }, [rows, sortField, sortAsc]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageStart = sortedRows.length === 0 ? 0 : safePage * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, sortedRows.length);
  const pageRows = useMemo(() => {
    return sortedRows.slice(pageStart, pageEnd);
  }, [sortedRows, pageStart, pageEnd]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
    setPage(0);
  };

  const renderSortIndicator = (field: string) => {
    if (sortField !== field) return <span style={{ opacity: 0.4, marginLeft: 4 }}>⇅</span>;
    return <span style={{ marginLeft: 4 }}>{sortAsc ? "↑" : "↓"}</span>;
  };

  const providerLabel = (value: unknown) => {
    const raw = String(value || "").trim();
    if (!raw) return "—";
    const key = raw.toLowerCase().replace(/[^a-z0-9]/g, "");
    const names: Record<string, string> = {
      lizzysub: "Lizzysub",
      paypoint: "Paypoint",
      vtpass: "VTPass",
      datareloaded: "DataReloaded",
      gladtidings: "GladTidings",
      clubkonnect: "ClubKonnect",
      flutterwave: "Flutterwave",
    };
    return names[key] || raw.replace(/\b\w/g, (letter) => letter.toUpperCase());
  };

  return (
    <div className="table-wrap">
      <ConfirmTransactionModal
        target={confirmTarget}
        onClose={() => setConfirmTarget(null)}
        onConfirm={handleConfirmAction}
        loading={confirmLoading}
      />
      <table className="tx-table-custom">
        <thead>
          <tr>
            <th onClick={() => handleSort("title")} style={{ cursor: "pointer" }}>
              Transactions {renderSortIndicator("title")}
            </th>
            <th onClick={() => handleSort("userName")} style={{ cursor: "pointer" }}>
              User {renderSortIndicator("userName")}
            </th>
            <th onClick={() => handleSort("status")} style={{ cursor: "pointer" }}>
              Status {renderSortIndicator("status")}
            </th>
            <th onClick={() => handleSort("amount")} style={{ cursor: "pointer" }}>
              Amount {renderSortIndicator("amount")}
            </th>
            {canViewTransactionProviders && <th>Provider</th>}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {pageRows.map((row) => {
            const logo = serviceLogo(row);
            const title = getTxTitle(row);
            const recipient = getRecipient(row);

            const userObj = currentUserForTransaction(userMap, row);

            const userPhoto = userAvatarSrc(
              userObj ? userObj.photoUrl : row.photoUrl,
              userObj ? userObj.avatarId : row.avatarId,
              userObj?.name || row.userName,
            );
            const userName = userObj?.name || row.userName || "User";
            const userPhone = userObj?.phone || row.userPhone || userObj?.email || row.userEmail || "—";
            const openProfileUser: AdminUser | null =
              userObj ||
              (row.uid
                ? {
                    uid: row.uid,
                    userId: row.userId,
                    name: row.userName,
                    email: row.userEmail,
                    phone: row.userPhone,
                    photoUrl: row.photoUrl,
                    avatarId: row.avatarId,
                  }
                : null);
            const amt = row.total ?? row.amount ?? 0;
            const isFailed = ["failed", "error", "rejected"].includes(
              String(row.status || "").toLowerCase()
            );
            const isSuccess = ["success", "successful", "completed", "delivered"].includes(
              String(row.status || "").toLowerCase()
            );
            const normalizedStatus = String(row.status || "pending").trim().toLowerCase();
            const isProcessing = normalizedStatus === "processing";
            const isAdminPending = normalizedStatus === "pending";
            const isRowProcessing = processingId === String(row.id || row.txId || "");

            return (
              <tr key={`${row.uid}-${row.id}`}>
                <td className="tx-cell-service">
                  <div className="tx-service-info">
                    <span
                      className={`tx-logo-badge ${
                        isVirtualNumberTransaction(row) ? "virtual-country-flag" : ""
                      }`}
                    >
                      {logo ? (
                        <img src={logo} alt="" />
                      ) : isReferralCommission(row) ? (
                        <span className="tx-logo-fallback referral-commission-logo"><Gift /></span>
                      ) : (
                        <span className="tx-logo-fallback">
                          {title.slice(0, 2).toUpperCase()}
                        </span>
                      )}
                    </span>
                    <div>
                      <strong>{title}</strong>
                      <small>{recipient}</small>
                    </div>
                  </div>
                </td>
                <td className="tx-cell-user">
                  <button
                    type="button"
                    className="tx-user-info tx-user-profile-trigger"
                    disabled={!openProfileUser}
                    onClick={() => openProfileUser && setSelectedUser(openProfileUser)}
                    aria-label={`Open ${userName}'s profile`}
                  >
                    <span className="tx-user-avatar">
                      <LoadedAvatar src={userPhoto} fallback={initials(userName)} />
                    </span>
                    <div>
                      <strong>{userName}</strong>
                      <small>{userPhone}</small>
                    </div>
                  </button>
                </td>
                <td>
                  <span
                    className={`tx-status-badge ${
                      isSuccess
                        ? "completed"
                        : isFailed
                        ? "failed"
                        : isProcessing ? "processing" : "pending"
                    }`}
                  >
                    {isSuccess ? "Completed" : isFailed ? "Failed" : isProcessing ? "Processing" : "Pending"}
                  </span>
                </td>
                <td className="tx-cell-amount">
                  <strong className={isFailed ? "tx-amount-failed" : ""}>
                    {naira(amt)}
                  </strong>
                </td>
                {canViewTransactionProviders && (
                  <td className="tx-cell-provider"><strong>{providerLabel(row.provider)}</strong></td>
                )}
                <td className="tx-cell-actions">
                  {mode === "pending" && isAdminPending ? (
                    isRowProcessing ? (
                      <div className="tx-action-processing">
                        <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} />
                        <span>Processing...</span>
                      </div>
                    ) : (
                      <div className="pending-actions-wrap">
                        <button
                          className="tx-action-btn green"
                          title="Approve Transaction"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmTarget({ row, action: "approve" });
                          }}
                        >
                          <Check />
                        </button>
                        <button
                          className="tx-action-btn red"
                          title="Decline & Refund Transaction"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmTarget({ row, action: "reject" });
                          }}
                        >
                          <X />
                        </button>
                        <button
                          className="tx-action-btn eye"
                          title="View Details & Receipt"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelected(row);
                          }}
                        >
                          <Eye />
                        </button>
                      </div>
                    )
                  ) : (
                    <button
                      className="view-more-btn"
                      onClick={() => setSelected(row)}
                      aria-label="View Details"
                    >
                      <Eye /> View More
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!sortedRows.length && (
        <div className="empty" style={{ padding: 32, textAlign: "center", color: "var(--muted)" }}>
          No transactions found
        </div>
      )}
      {sortedRows.length > 0 && (
        <div className="table-pagination">
          <div className="table-pagination-info">
            Showing {sortedRows.length === 0 ? 0 : pageStart + 1}–{pageEnd} of {sortedRows.length} transactions
          </div>
          <div className="table-pagination-controls">
            <div className="table-pagination-size">
              <span>Rows per page:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(0);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <div className="table-pagination-pages">
              <span>
                Page {safePage + 1} of {totalPages}
              </span>
              <button
                className="table-pagination-btn"
                disabled={safePage === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft />
              </button>
              <button
                className="table-pagination-btn"
                disabled={safePage >= totalPages - 1}
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                aria-label="Next page"
              >
                <ChevronRight />
              </button>
            </div>
          </div>
        </div>
      )}
      {selected && (
        <TransactionPreview
          row={selected}
          auditLogs={auditLogs}
          runAction={runAction}
          onActionConfirm={(r, a) => setConfirmTarget({ row: r, action: a })}
          onClose={() => setSelected(null)}
        />
      )}
      {selectedUser && runAction && (
        <UserProfileModal
          initialUser={selectedUser}
          runAction={runAction}
          onClose={() => setSelectedUser(null)}
        />
      )}
    </div>
  );
}

function ProfileTransactions({
  rows,
  user,
  auditLogs,
  runAction,
}: {
  rows: Transaction[];
  user: AdminUser;
  auditLogs: Record<string, unknown>[];
  runAction: RunAction;
}) {
  const [selected, setSelected] = useState<Transaction | null>(null);
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [filterQuery, setFilterQuery] = useState("");
  const [historyQuery, setHistoryQuery] = useState("");
  const categories = [
    ["all", "All"],
    ["airtime", "Airtime"],
    ["data", "Data"],
    ["cable", "Cable TV"],
    ["electricity", "Electricity"],
    ["education", "Education"],
    ["virtual", "Virtual Number"],
    ["esim", "eSIM"],
  ] as const;
  const transactionCategory = (row: Transaction) => {
    const value = `${row.type || ""} ${row.title || ""} ${row.serviceID || ""}`.toLowerCase();
    if (/virtual|number|sms/.test(value)) return "virtual";
    if (/airtime|topup/.test(value)) return "airtime";
    if (/\bdata\b|bundle/.test(value)) return "data";
    if (/cable|tv|dstv|gotv|startimes/.test(value)) return "cable";
    if (/electric|power|meter/.test(value)) return "electricity";
    if (/education|exam|waec|neco|jamb/.test(value)) return "education";
    if (/esim/.test(value)) return "esim";
    return "other";
  };
  const filteredRows = useMemo(() => {
    const firstNeedle = filterQuery.trim().toLowerCase();
    const historyNeedle = historyQuery.trim().toLowerCase();
    return rows.filter((row) => {
      const normalizedStatus = String(row.status || "pending").toLowerCase();
      const successful = ["success", "successful", "completed", "delivered"].includes(normalizedStatus);
      const failed = ["failed", "failure", "rejected", "cancelled"].includes(normalizedStatus);
      const statusGroup = successful ? "completed" : failed ? "failed" : normalizedStatus === "processing" ? "processing" : "pending";
      const searchable = `${row.id || ""} ${row.txId || ""} ${row.title || ""} ${row.type || ""} ${transactionRecipient(row)} ${row.status || ""}`.toLowerCase();
      return (category === "all" || transactionCategory(row) === category) &&
        (status === "all" || statusGroup === status) &&
        (!firstNeedle || searchable.includes(firstNeedle)) &&
        (!historyNeedle || searchable.includes(historyNeedle));
    });
  }, [rows, category, status, filterQuery, historyQuery]);
  const exportTransactions = () => {
    const escape = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const lines = [
      ["Transaction ID", "Title", "Recipient", "Status", "Amount", "Time"],
      ...filteredRows.map((row) => [row.txId || row.id, row.title || serviceName(row), transactionRecipient(row), row.status, row.total ?? row.amount ?? 0, row.createdAt ? new Date(row.createdAt).toISOString() : ""]),
    ].map((line) => line.map(escape).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([lines], { type: "text/csv;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${(user.name || "user").replace(/\s+/g, "-").toLowerCase()}-transactions.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="profile-transactions-page">
      <section className="profile-transaction-categories" aria-label="Transaction categories">
        {categories.map(([key, label]) => (
          <button key={key} className={category === key ? "active" : ""} onClick={() => setCategory(key)}>
            {label}
          </button>
        ))}
      </section>
      <section className="profile-transaction-filter-panel">
        <h3>All Transactions</h3>
        <div>
          <label>
            <span>Status</span>
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="all">All statuses</option>
              <option value="completed">Completed</option>
              <option value="processing">Processing</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </label>
          <label className="profile-transaction-search"><Search /><input value={filterQuery} onChange={(event) => setFilterQuery(event.target.value)} placeholder="Search transaction, recipient or ID" /></label>
        </div>
      </section>
      <section className="profile-transactions">
      <div className="profile-transaction-history-toolbar">
        <div><h3>Transaction History</h3><p>{filteredRows.length} transaction{filteredRows.length === 1 ? "" : "s"}</p></div>
        <label className="profile-transaction-search"><Search /><input value={historyQuery} onChange={(event) => setHistoryQuery(event.target.value)} placeholder="Search history" /></label>
        <button onClick={exportTransactions}><FileDown /> Export Data</button>
      </div>
      <div className="profile-transaction-table">
        <div className="profile-transaction-row profile-transaction-head">
          <span>Transactions ↕</span>
          <span>User ↕</span>
          <span>Status ↕</span>
          <span>Amount ↕</span>
          <span>Actions</span>
        </div>
        {filteredRows.length ? (
          filteredRows.map((row) => {
            const normalizedStatus = String(row.status || "").toLowerCase();
            const successful = [
              "success",
              "successful",
              "completed",
              "delivered",
            ].includes(normalizedStatus);
            const failed = [
              "failed",
              "failure",
              "rejected",
              "cancelled",
            ].includes(normalizedStatus);
            const statusLabel = successful
              ? "Completed"
              : failed
                ? "Failed"
                : row.status || "Pending";
            const photo = userAvatarSrc(
              user.photoUrl,
              user.avatarId,
              user.name || row.userName,
            );

            return (
              <div
                className="profile-transaction-row"
                key={`${row.uid}-${row.id}`}
              >
                <div className="profile-transaction-service">
                  <span
                    className={`service-badge ${
                      isVirtualNumberTransaction(row) ? "virtual-country-flag" : ""
                    }`}
                  >
                    {serviceLogo(row) ? (
                      <img
                        src={serviceLogo(row) ?? undefined}
                        alt={`${serviceInitial(row)} logo`}
                      />
                    ) : isReferralCommission(row) ? (
                      <span className="tx-logo-fallback referral-commission-logo"><Gift /></span>
                    ) : (
                      serviceInitial(row)
                    )}
                  </span>
                  <div>
                    <strong>{row.title || serviceName(row)}</strong>
                    <small>{transactionRecipient(row)}</small>
                  </div>
                </div>
                <div className="profile-transaction-user">
                  <span className="user-badge">
                    <LoadedAvatar
                      src={photo}
                      alt={`${user.name || row.userName || "User"} profile`}
                      fallback={initials(user.name || row.userName)}
                    />
                  </span>
                  <div>
                    <strong>{user.name || row.userName || "Unknown User"}</strong>
                    <small>{user.phone || row.userPhone || row.userEmail || "—"}</small>
                  </div>
                </div>
                <span
                  className={`activity-status ${successful ? "completed" : failed ? "failed" : "pending"}`}
                >
                  {successful ? (
                    <CheckCircle2 />
                  ) : failed ? (
                    <XCircle />
                  ) : (
                    <Clock3 />
                  )}
                  {statusLabel}
                </span>
                <strong className={failed ? "failed-amount" : ""}>
                  {naira(row.total ?? row.amount)}
                </strong>
                <button
                  className="view-details"
                  onClick={() => setSelected(row)}
                >
                  <Eye /> View More
                </button>
              </div>
            );
          })
        ) : (
          <div className="empty">No service transactions found</div>
        )}
      </div>
      {selected && (
        <TransactionPreview
          row={selected}
          deposits={false}
          auditLogs={auditLogs}
          runAction={runAction}
          onClose={() => setSelected(null)}
        />
      )}
      </section>
    </div>
  );
}

function UsersPage({
  users,
  transactions,
  runAction,
}: {
  users: AdminUser[];
  transactions: Transaction[];
  runAction: RunAction;
}) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<AdminUser | null>(null);
  const rows = useMemo(() => {
    const numericId = (user: AdminUser) =>
      Number(user.userId || user.customerNo || 0);
    return users
      .filter((user) =>
        `${user.name} ${user.email} ${user.phone} ${user.uid} ${user.userId || ""} ${user.customerNo || ""}`
          .toLowerCase()
          .includes(query.toLowerCase()),
      )
      .sort((a, b) => {
        const createdDifference = Number(b.createdAt || 0) - Number(a.createdAt || 0);
        return createdDifference || numericId(b) - numericId(a);
      });
  }, [users, query]);
  const loadedTransactionCounts = useMemo(() => {
    const counts = new Map<string, number>();
    const excludedTypes = new Set([
      "fund", "funding", "deposit", "wallet_funding",
      "wallet_adjustment", "admin_wallet_adjustment",
    ]);
    transactions.forEach((row) => {
      const type = String(row.type || "").toLowerCase();
      const title = String(row.title || "").toLowerCase();
      const hasDepositId = [row.depositId, row.depositNo].some(
        (value) => value !== undefined && value !== null && String(value).trim() !== "",
      );
      if (
        hasDepositId ||
        excludedTypes.has(type) ||
        title.includes("wallet funding") ||
        title.includes("wallet deposit") ||
        title.includes("fund wallet")
      ) return;
      const uid = String(row.uid || "");
      if (uid) counts.set(uid, (counts.get(uid) || 0) + 1);
    });
    return counts;
  }, [transactions]);
  const joinedBadge = (createdAt?: number) => {
    if (!createdAt) return "Joined —";
    const age = Date.now() - createdAt;
    if (age >= 0 && age <= 30 * 24 * 60 * 60 * 1000) return "New";
    return new Intl.DateTimeFormat("en-NG", {
      month: "short",
      year: "numeric",
    }).format(createdAt);
  };
  return (
    <div className="panel">
      <div className="panel-title">
        <div>
          <h2>Users</h2>
          <p>{rows.length} loaded</p>
        </div>
      </div>
      <div className="toolbar">
        <div className="search">
          <Search />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, phone or ID"
          />
        </div>
      </div>
      <div className="table-wrap">
        <table className="users-table-modern">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Phone</th>
              <th>Transactions</th>
              <th>Balance</th>
              <th>Joined</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((user) => {
              const badge = joinedBadge(user.createdAt);
              const transactionCount =
                user.totalServiceTransactionsCount ??
                user.transactionCount ??
                loadedTransactionCounts.get(user.uid) ??
                0;
              return <tr key={user.uid}>
                <td>
                  <span className="user-id-pill">
                    {user.userId || user.customerNo || "—"}
                  </span>
                </td>
                <td>
                  <button
                    className="user-profile-link"
                    onClick={() => setSelected(user)}
                  >
                    <span className="user-badge">
                      <LoadedAvatar src={userAvatarSrc(user.photoUrl, user.avatarId, user.name)} fallback={initials(user.name)} />
                    </span>
                    <span>
                      <span className="user-name-line">
                        <strong>{user.name || "Unnamed user"}</strong>
                        <em className={badge === "New" ? "new" : "joined"}>
                          {badge}
                        </em>
                      </span>
                      <small>{user.email}</small>
                    </span>
                  </button>
                </td>
                <td>{user.phone || "—"}</td>
                <td className="user-transaction-count">
                  {Number(transactionCount).toLocaleString("en-NG")}
                </td>
                <td>
                  <strong>{naira(user.balance)}</strong>
                </td>
                <td className="user-joined-date">
                  {user.createdAt ? date.format(user.createdAt) : "—"}
                </td>
                <td>
                  <button
                    className="user-view-button"
                    onClick={() => setSelected(user)}
                    aria-label={`Open ${user.name || "user"} profile`}
                    title="View user profile"
                  >
                    <Eye />
                  </button>
                </td>
              </tr>;
            })}
          </tbody>
        </table>
      </div>
      {selected && (
        <UserProfileModal
          initialUser={selected}
          runAction={runAction}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

type UserProfileData = {
  user: AdminUser;
  transactions: Transaction[];
  auditLogs: Record<string, unknown>[];
  amlReviews?: Record<string, unknown>[];
  kycSubmission?: Record<string, unknown> | null;
};

const userProfileCache = new Map<string, UserProfileData>();

function warmUserProfileCache(data: DashboardData) {
  const transactionsByUser = new Map<string, Transaction[]>();
  for (const transaction of data.transactions || []) {
    const uid = String(transaction.uid || "");
    if (!uid) continue;
    const rows = transactionsByUser.get(uid) || [];
    rows.push(transaction);
    transactionsByUser.set(uid, rows);
  }

  const auditsByUser = new Map<string, Record<string, unknown>[]>();
  for (const audit of data.auditLogs || []) {
    const uid = String(audit.uid || "");
    if (!uid) continue;
    const rows = auditsByUser.get(uid) || [];
    rows.push(audit);
    auditsByUser.set(uid, rows);
  }

  const amlByUser = new Map<string, Record<string, unknown>[]>();
  for (const review of data.amlReviews || []) {
    const uid = String(review.uid || "");
    if (!uid) continue;
    const rows = amlByUser.get(uid) || [];
    rows.push(review);
    amlByUser.set(uid, rows);
  }

  const kycByUser = new Map<string, Record<string, unknown>>();
  for (const submission of data.kycSubmissions || []) {
    const uid = String(submission.uid || submission.id || "");
    if (uid) kycByUser.set(uid, submission);
  }

  for (const user of data.users || []) {
    if (!user.uid) continue;
    const cached = userProfileCache.get(user.uid);
    userProfileCache.set(user.uid, {
      user: { ...(cached?.user || {}), ...user },
      transactions: transactionsByUser.get(user.uid) || cached?.transactions || [],
      auditLogs: auditsByUser.get(user.uid) || cached?.auditLogs || [],
      amlReviews: amlByUser.get(user.uid) || cached?.amlReviews || [],
      kycSubmission: kycByUser.get(user.uid) || cached?.kycSubmission || null,
    });
  }
}

function UserProfileModal({
  initialUser,
  runAction,
  onClose,
}: {
  initialUser: AdminUser;
  runAction: RunAction;
  onClose: () => void;
}) {
  const { canAdjustWallet } = useContext(AdminPermissionContext);
  const modalRef = useRef<HTMLDivElement>(null);
  const [profile, setProfile] = useState<UserProfileData>(() =>
    userProfileCache.get(initialUser.uid) || {
      user: initialUser,
      transactions: [],
      auditLogs: [],
    },
  );
  const [tab, setTab] = useState("profile");
  const [dialog, setDialog] = useState<
    | "communication"
    | "push"
    | "manage"
    | "suspend"
    | "watchlist"
    | "wallet"
    | null
  >(null);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [sendPush, setSendPush] = useState(true);
  const [sendInApp, setSendInApp] = useState(true);
  const [reason, setReason] = useState("");
  const [walletAction, setWalletAction] = useState<"credit" | "debit">(
    "credit",
  );
  const [walletAmount, setWalletAmount] = useState("");
  const [walletBusy, setWalletBusy] = useState(false);
  const [profileAction, setProfileAction] = useState<
    "suspend" | "watchlist" | "notification" | null
  >(null);
  const loadProfile = useCallback(async (_showLoading = false) => {
    try {
      const call = httpsCallable<Record<string, unknown>, UserProfileData>(
        functions,
        "adminUserProfile",
      );
      const result = await call({
        uid: initialUser.uid,
        limitTransactions: 500,
      });
      userProfileCache.set(initialUser.uid, result.data);
      setProfile(result.data);
    } catch {
      // Keep showing the dashboard-cached profile and retry on the next sync.
    }
  }, [initialUser.uid]);
  useEffect(() => {
    void loadProfile(false);
  }, [loadProfile]);
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() =>
      modalRef.current?.focus({ preventScroll: true }),
    );
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
    };
  }, []);
  useEffect(() => {
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;
    const refresh = () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => void loadProfile(false), 250);
    };
    const subscriptions = [
      onSnapshot(doc(db, "users", initialUser.uid), refresh, () => undefined),
      onSnapshot(doc(db, "wallets", initialUser.uid), refresh, () => undefined),
      onSnapshot(
        query(
          collection(db, "transactions", initialUser.uid, "items"),
          orderBy("createdAt", "desc"),
          limit(500),
        ),
        refresh,
        () => undefined,
      ),
      onSnapshot(
        query(
          collection(db, "admin_audit_logs"),
          where("uid", "==", initialUser.uid),
          limit(100),
        ),
        refresh,
        () => undefined,
      ),
      onSnapshot(
        doc(db, "kyc_submissions", initialUser.uid),
        refresh,
        () => undefined,
      ),
    ];
    const fallback = window.setInterval(() => void loadProfile(false), 15000);
    return () => {
      subscriptions.forEach((unsubscribe) => unsubscribe());
      window.clearInterval(fallback);
      if (refreshTimer) clearTimeout(refreshTimer);
    };
  }, [initialUser.uid, loadProfile]);
  const user = profile.user || initialUser;
  const transactions = profile.transactions || [];
  const deposits = transactions.filter((row) =>
    ["fund", "funding", "deposit", "wallet_funding"].includes(row.type || ""),
  );
  const serviceTransactions = transactions.filter(
    (row) => {
      const type = String(row.type || "").toLowerCase();
      const title = String(row.title || "").toLowerCase();
      return ![
        "fund",
        "funding",
        "deposit",
        "wallet_funding",
        "wallet_adjustment",
        "admin_wallet_adjustment",
        "admin_credit",
        "admin_debit",
        "admin_wallet_credit",
        "admin_wallet_debit",
      ].includes(type) &&
        !title.includes("admin wallet credit") &&
        !title.includes("admin wallet debit");
    },
  );
  const userAudits = (profile.auditLogs || []).filter((log) => {
    const action = String(log.action || "").toLowerCase();
    const adminIdentity = String(
      log.adminUid || log.adminName || log.adminEmail || "",
    )
      .trim()
      .toLowerCase();
    const isHumanAdmin =
      Boolean(adminIdentity) &&
      !["system", "automatic", "automated", "backend"].includes(
        adminIdentity,
      );
    return (
      isHumanAdmin &&
      (action.startsWith("user_") ||
        action.startsWith("wallet_") ||
        action.startsWith("admin_wallet_"))
    );
  });
  const walletHistoryRows = useMemo(() => {
    const existingIds = new Set(transactions.map((row) => String(row.txId || row.id)));
    const adjustments = (profile.auditLogs || []).flatMap((log) => {
      const action = String(log.action || "").toLowerCase();
      if (!action.includes("wallet_credit") && !action.includes("wallet_debit")) return [];
      const id = String(log.txId || log.id || "");
      if (!id || existingIds.has(id)) return [];
      const credit = action.includes("credit");
      return [{
        id,
        txId: id,
        uid: user.uid,
        userName: user.name,
        userEmail: user.email,
        photoUrl: user.photoUrl,
        type: "admin_wallet_adjustment",
        title: credit ? "Admin Wallet Credit" : "Admin Wallet Debit",
        adjustmentAction: credit ? "credit" : "debit",
        reason: String(log.reason || ""),
        description: String(log.reason || ""),
        status: "success",
        amount: Math.abs(Number(log.amount || 0)),
        total: Math.abs(Number(log.amount || 0)),
        balanceBefore: Number(log.statusBefore),
        balanceAfter: Number(log.statusAfter),
        createdAt: Number(log.createdAt || 0),
      } as Transaction];
    });
    return [...transactions, ...adjustments];
  }, [profile.auditLogs, transactions, user]);
  const suspend = async () => {
    const suspended = !user.deactivated;
    if (!reason.trim() || profileAction) return;
    setProfileAction("suspend");
    try {
      const succeeded = await runAction(
        "adminSetUserSuspended",
        { uid: user.uid, suspended, reason: reason.trim() },
        suspended ? "User suspended" : "User unsuspended",
        false,
      );
      if (succeeded) {
        setDialog(null);
        setReason("");
      }
    } finally {
      setProfileAction(null);
    }
  };
  const watchlist = async () => {
    const watchlisted = !user.watchlisted;
    const actionReason = watchlisted ? reason.trim() : "";
    if ((watchlisted && !actionReason) || profileAction) return;
    setProfileAction("watchlist");
    try {
      const succeeded = await runAction(
        "adminSetUserWatchlisted",
        { uid: user.uid, watchlisted, reason: actionReason },
        watchlisted ? "User added to watchlist" : "User removed from watchlist",
        false,
      );
      if (succeeded) {
        setDialog(null);
        setReason("");
      }
    } finally {
      setProfileAction(null);
    }
  };
  const adjust = async () => {
    const amount = Number(walletAmount);
    if (!amount || !reason.trim() || walletBusy) return;
    setWalletBusy(true);
    try {
      await runAction(
        "adminAdjustUserWallet",
        { uid: user.uid, action: walletAction, amount, reason: reason.trim() },
        walletAction === "credit" ? "Wallet credited" : "Wallet debited",
        false,
      );
      setDialog(null);
      setReason("");
      setWalletAmount("");
      await loadProfile(false);
    } finally {
      setWalletBusy(false);
    }
  };
  const sendMessage = async () => {
    if (
      !title.trim() ||
      !message.trim() ||
      (!sendPush && !sendInApp) ||
      profileAction
    ) {
      return;
    }
    setProfileAction("notification");
    try {
      const succeeded = await runAction(
        "adminSendBroadcast",
        {
          audience: "single",
          targetUid: user.uid,
          targetQuery: user.uid,
          title,
          body: message,
          sendPush,
          inApp: sendInApp,
          actionType: "none",
        },
        "Notification sent",
        false,
      );
      if (succeeded) {
        setDialog(null);
        setTitle("");
        setMessage("");
        setSendPush(true);
        setSendInApp(true);
      }
    } finally {
      setProfileAction(null);
    }
  };
  const tabs = [
    "Profile",
    "Deposits",
    "Transactions",
    "Wallet History",
    "Referral",
    "KYC",
    "AML Review",
  ];
  return (
    <div className="modal-backdrop user-profile-backdrop" onMouseDown={onClose}>
      <div
        ref={modalRef}
        className="user-profile-modal"
        role="dialog"
        aria-modal="true"
        aria-label="User profile"
        tabIndex={-1}
        onMouseDown={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          const target = event.target as HTMLElement;
          if (
            target.matches("input, textarea, select, button") ||
            target.isContentEditable
          ) return;
          const modal = modalRef.current;
          if (!modal) return;
          const directions: Record<string, number> = {
            ArrowDown: 56,
            ArrowUp: -56,
            PageDown: modal.clientHeight * 0.85,
            PageUp: -modal.clientHeight * 0.85,
          };
          if (event.key in directions) {
            event.preventDefault();
            modal.scrollBy({ top: directions[event.key], behavior: "smooth" });
          } else if (event.key === "Home" || event.key === "End") {
            event.preventDefault();
            modal.scrollTo({
              top: event.key === "Home" ? 0 : modal.scrollHeight,
              behavior: "smooth",
            });
          }
        }}
      >
        <header>
          <h2>User Profile</h2>
          <button onClick={onClose}>Close</button>
        </header>
        <nav>
          {tabs.map((item) => (
            <button
              className={tab === item.toLowerCase() ? "active" : ""}
              onClick={() => setTab(item.toLowerCase())}
              key={item}
            >
              {item}
            </button>
          ))}
        </nav>
        <div className="user-profile-content">
            <section className="profile-identity">
              <span className="profile-photo">
                <LoadedAvatar src={userAvatarSrc(user.photoUrl, user.avatarId, user.name)} fallback={initials(user.name)} />
              </span>
              <div>
                <h2>{user.name || "Unnamed user"}</h2>
                <div className="profile-badges">
                  <span>User</span>
                  <span className="good">
                    {user.deactivated ? "Suspended" : "Active"}
                  </span>
                  {user.watchlisted && (
                    <span className="warning">Watchlisted</span>
                  )}
                  <span className="kyc-badge">
                    {formatKycStatus(user.kycStatus)}
                  </span>
                </div>
              </div>
            </section>
            {tab === "profile" && (
              <>
                <div className="profile-stats">
                  <div>
                    <span>Wallet</span>
                    <strong>{naira(user.balance)}</strong>
                  </div>
                  <div>
                    <span>Deposits</span>
                    <strong>
                      {user.totalDepositsCount ?? deposits.length}
                    </strong>
                  </div>
                  <div>
                    <span>Transactions</span>
                    <strong>{serviceTransactions.length}</strong>
                  </div>
                </div>
                <div className="profile-actions">
                  <button onClick={() => setDialog("communication")}>
                    Communication
                  </button>
                  <button onClick={() => setDialog("manage")}>
                    Manage User
                  </button>
                  {canAdjustWallet && (
                    <button className="solid" onClick={() => setDialog("wallet")}>
                      Add / Deduct Wallet
                    </button>
                  )}
                </div>
                {user.watchlisted && (
                  <div className="watchlist-note">
                    <strong>User is on watchlist</strong>
                    <p>{user.watchlistReason || "No reason recorded."}</p>
                  </div>
                )}
                <div className="profile-details">
                  <SummaryField
                    label="User ID"
                    value={user.userId || user.uid}
                    copy={() =>
                      void copyToClipboard(user.userId || user.uid, "User ID")
                    }
                  />
                  <SummaryField
                    label="Email Address"
                    value={user.email || "—"}
                    copy={
                      user.email
                        ? () => void copyToClipboard(user.email!, "Email address")
                        : undefined
                    }
                  />
                  <SummaryField
                    label="Phone Number"
                    value={user.phone || "—"}
                    copy={
                      user.phone
                        ? () => void copyToClipboard(user.phone!, "Phone number")
                        : undefined
                    }
                  />
                  <SummaryField
                    label="Date Joined"
                    value={user.createdAt ? date.format(user.createdAt) : "—"}
                  />
                  <SummaryField
                    label="Last Activity"
                    value={
                      user.lastActivityAt
                        ? date.format(user.lastActivityAt)
                        : "—"
                    }
                  />
                </div>
                <UserAuditLog
                  rows={userAudits}
                  userId={user.userId || user.uid}
                />
              </>
            )}
            {tab === "deposits" && (
              <ProfileDeposits
                rows={deposits}
                auditLogs={profile?.auditLogs || []}
                runAction={runAction}
              />
            )}
            {tab === "transactions" && (
              <ProfileTransactions
                rows={serviceTransactions}
                user={user}
                auditLogs={profile?.auditLogs || []}
                runAction={runAction}
              />
            )}
        {tab === "wallet history" && (
          <ProfileWalletHistory
            rows={walletHistoryRows}
            user={user}
            auditLogs={profile?.auditLogs || []}
            runAction={runAction}
          />
        )}
        {tab === "referral" && <ProfileReferral user={user} />}
        {tab === "kyc" && <ProfileKyc submission={profile?.kycSubmission} />}
{tab === "aml review" && (
  <ProfileAmlReview rows={profile?.amlReviews || []} />
)}
        {![
          "profile",
          "deposits",
          "transactions",
          "wallet history",
          "referral",
          "kyc",
          "aml review",
        ].includes(tab) && (
              <RecordPage
                title={tab.toUpperCase()}
                subtitle={`User ${tab} information`}
                rows={
                  tab === "kyc" && profile?.kycSubmission
                    ? [profile.kycSubmission]
                    : []
                }
              />
            )}
        </div>
        {dialog && (
          <div className="nested-modal">
            <div className="profile-dialog">
              <button
                className="nested-close"
                onClick={() => {
                  setDialog(null);
                  setReason("");
                }}
              >
                <X />
              </button>
              {dialog === "communication" && (
                <>
                  <div className="profile-dialog-heading">
                    <span className="dialog-heading-icon">
                      <Radio />
                    </span>
                    <div>
                      <h2>Communication</h2>
                      <p>{user.name || "User"}</p>
                    </div>
                  </div>
                  <div className="profile-menu">
                    <ProfileMenuItem
                      icon={<Radio />}
                      title="Send push notification"
                      subtitle="Send push & in-app notification to this user"
                      onClick={() => setDialog("push")}
                    />
                    <ProfileMenuItem
                      icon={<Mail />}
                      title="Send email"
                      subtitle={user.email || "No email address"}
                      onClick={() => {
                        if (user.email)
                          window.location.href = `mailto:${user.email}`;
                      }}
                    />
                    <ProfileMenuItem
                      icon={<Copy />}
                      title="Copy email"
                      subtitle={user.email || "No email address"}
                      onClick={() =>
                        user.email &&
                        void copyToClipboard(user.email, "Email address")
                      }
                    />
                    <ProfileMenuItem
                      icon={<Smartphone />}
                      title="Copy phone number"
                      subtitle={user.phone || "No phone number"}
                      onClick={() =>
                        user.phone &&
                        void copyToClipboard(user.phone, "Phone number")
                      }
                    />
                    <ProfileMenuItem
                      icon={<UserCog />}
                      title="Copy user ID"
                      subtitle={user.uid}
                      onClick={() => void copyToClipboard(user.uid, "User ID")}
                    />
                  </div>
                </>
              )}
              {dialog === "push" && (
                <>
                  <button
                    className="dialog-back"
                    onClick={() => setDialog("communication")}
                    aria-label="Back to communication"
                  >
                    <ArrowLeft />
                  </button>
                  <div className="push-dialog-heading">
                    <h2>Send Push Notification</h2>
                    <p>
                      To: {user.name} ({user.email})
                    </p>
                  </div>
                  <label>
                    Notification Title
                    <input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="e.g. Account Notice / Special Update"
                    />
                  </label>
                  <label>
                    Message Content
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={`Type your message to ${user.name}...`}
                    />
                  </label>
                  <h4>Delivery Options</h4>
                  <div className="delivery-options">
                    <button
                      type="button"
                      className={sendPush ? "selected" : ""}
                      aria-pressed={sendPush}
                      disabled={profileAction !== null}
                      onClick={() => setSendPush((selected) => !selected)}
                    >
                      <span className="delivery-check">
                        {sendPush ? <Check /> : null}
                      </span>
                      FCM Push
                    </button>
                    <button
                      type="button"
                      className={sendInApp ? "selected" : ""}
                      aria-pressed={sendInApp}
                      disabled={profileAction !== null}
                      onClick={() => setSendInApp((selected) => !selected)}
                    >
                      <span className="delivery-check">
                        {sendInApp ? <Check /> : null}
                      </span>
                      In-App Inbox
                    </button>
                  </div>
                  {!sendPush && !sendInApp && (
                    <p className="delivery-options-error">
                      Select at least one delivery option.
                    </p>
                  )}
                  <button
                    className="solid send-button"
                    disabled={
                      profileAction !== null ||
                      !title.trim() ||
                      !message.trim() ||
                      (!sendPush && !sendInApp)
                    }
                    onClick={() => void sendMessage()}
                  >
                    {profileAction === "notification" ? (
                      <Loader2 className="spin" />
                    ) : (
                      <Send />
                    )}
                    {profileAction === "notification"
                      ? "Sending…"
                      : "Send Notification"}
                  </button>
                </>
              )}
              {dialog === "manage" && (
                <>
                  <h2>Manage user</h2>
                  <div className="profile-menu manage-menu">
                    <ProfileMenuItem
                      icon={<Ban />}
                      tone="danger"
                      title={user.deactivated ? "Unsuspend user" : "Suspend user"}
                      subtitle={
                        user.deactivated
                          ? "Allow this user to sign in again."
                          : "Block this user from signing in."
                      }
                      onClick={() => setDialog("suspend")}
                    />
                    <ProfileMenuItem
                      icon={<Eye />}
                      tone="warning"
                      title={
                        user.watchlisted
                          ? "Remove from watchlist"
                          : "Add to watchlist"
                      }
                      subtitle={
                        user.watchlisted
                          ? "Stop routing this user through AML review."
                          : "Flag this user for closer transaction review."
                      }
                      onClick={() => setDialog("watchlist")}
                    />
                  </div>
                  <button
                    className="dialog-cancel"
                    onClick={() => setDialog(null)}
                  >
                    Cancel
                  </button>
                </>
              )}
              {(dialog === "suspend" || dialog === "watchlist") && (
                <>
                  <h2>
                    {dialog === "suspend"
                      ? user.deactivated
                        ? "Unsuspend user"
                        : "Suspend user"
                      : user.watchlisted
                        ? "Remove from watchlist"
                        : "Add to watchlist"}
                  </h2>
                  <p>
                    {dialog === "suspend"
                      ? user.deactivated
                        ? `${user.name} will be allowed to sign in again.`
                        : `${user.name} will be blocked from signing in.`
                      : user.watchlisted
                        ? `${user.name} will be removed from closer transaction review.`
                        : `${user.name} will be flagged for closer transaction review.`}
                  </p>
                  {!(dialog === "watchlist" && user.watchlisted) && (
                    <label className="reason-field">
                      Reason
                      <textarea
                        autoFocus
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        placeholder={
                          dialog === "suspend"
                            ? "Enter the reason for this account status change"
                            : "Why is this user being added to the watchlist?"
                        }
                      />
                    </label>
                  )}
                  <div className="dialog-actions">
                    <button
                      className="dialog-cancel"
                      disabled={profileAction !== null}
                      onClick={() => {
                        setReason("");
                        setDialog("manage");
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="solid"
                      disabled={
                        profileAction !== null ||
                        !(dialog === "watchlist" && user.watchlisted) &&
                        !reason.trim()
                      }
                      onClick={() =>
                        void (dialog === "suspend" ? suspend() : watchlist())
                      }
                    >
                      {profileAction ? <Loader2 className="spin" /> : null}
                      {profileAction === "suspend"
                        ? user.deactivated
                          ? "Restoring access…"
                          : "Suspending…"
                        : profileAction === "watchlist"
                          ? user.watchlisted
                            ? "Removing…"
                            : "Adding…"
                          : "Confirm action"}
                    </button>
                  </div>
                </>
              )}
              {dialog === "wallet" && (
                <>
                  <h2>Adjust wallet</h2>
                  <p>
                    {user.name} · Current balance {naira(user.balance)}
                  </p>
                  <div className="wallet-action-toggle">
                    <button
                      className={walletAction === "credit" ? "active" : ""}
                      onClick={() => setWalletAction("credit")}
                    >
                      + Add
                    </button>
                    <button
                      className={walletAction === "debit" ? "active" : ""}
                      onClick={() => setWalletAction("debit")}
                    >
                      − Deduct
                    </button>
                  </div>
                  <label>
                    Amount in Naira
                    <input
                      type="number"
                      min="1"
                      inputMode="decimal"
                      value={walletAmount}
                      onChange={(event) => setWalletAmount(event.target.value)}
                      placeholder="Enter amount"
                    />
                  </label>
                  <label className="reason-field">
                    Reason
                    <textarea
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      placeholder="Explain why this wallet adjustment is required"
                    />
                  </label>
                  <div className="dialog-actions">
                    <button
                      className="dialog-cancel"
                      onClick={() => {
                        setDialog(null);
                        setReason("");
                        setWalletAmount("");
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="solid"
                      disabled={walletBusy || !Number(walletAmount) || !reason.trim()}
                      onClick={() => void adjust()}
                    >
                      {walletBusy
                        ? "Processing…"
                        : walletAction === "credit"
                          ? "Add funds"
                          : "Deduct funds"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ProfileMenuItem({
  icon,
  title,
  subtitle,
  onClick,
  tone = "default",
}: {
  icon: ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
  tone?: "default" | "danger" | "warning";
}) {
  return (
    <button className={`profile-menu-item ${tone}`} onClick={onClick}>
      <span className="profile-menu-icon">{icon}</span>
      <span className="profile-menu-copy">
        <strong>{title}</strong>
        <small>{subtitle}</small>
      </span>
      <ChevronRight />
    </button>
  );
}

function ProfileReferral({ user }: { user: AdminUser }) {
  const referralUser = user as AdminUser & {
    referralWallet?: {
      balance?: number;
      totalEarned?: number;
      totalMovedToWallet?: number;
      pendingWithdrawals?: number;
    };
    referralCode?: string;
    referredByCode?: string;
    referralRewarded?: boolean;
    referralQualifiedAt?: number;
    referralReview?: {
      totalInvited?: number;
      qualifiedCount?: number;
      pendingCount?: number;
      totalReferralRewardsKobo?: number;
      referrals?: Array<Record<string, unknown>>;
    };
  };
  const referralWallet = referralUser.referralWallet || {};
  const referralCode = String(referralUser.referralCode || "").trim();
  const appliedCode = String(referralUser.referredByCode || "").trim();
  const qualified =
    referralUser.referralRewarded || referralUser.referralQualifiedAt
      ? "Qualified"
      : "-";
  const review = referralUser.referralReview || {};
  const referredPeople = review.referrals || [];
  const metrics = [
    ["Referral Balance", naira(Number(referralWallet.balance || 0))],
    ["Total Earned", naira(Number(referralWallet.totalEarned || 0))],
    [
      "Moved to Wallet",
      naira(Number(referralWallet.totalMovedToWallet || 0)),
    ],
    [
      "Pending Withdrawals",
      naira(Number(referralWallet.pendingWithdrawals || 0)),
    ],
  ];

  return (
    <section className="profile-referral-shell">
      <div className="profile-referral-card">
        <header className="profile-referral-header">
          <div className="profile-referral-heading">
            <UserPlus size={25} aria-hidden="true" />
            <h3>Referral Profile</h3>
          </div>
          <span
            className={`profile-referral-state${
              appliedCode ? " is-referred" : ""
            }`}
          >
            <Clock3 size={18} aria-hidden="true" />
            {appliedCode ? "Referred" : "Not Referred"}
          </span>
        </header>

        <div className="profile-referral-metrics">
          {metrics.map(([label, value]) => (
            <div key={label}>
              <span className="profile-referral-label">{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>

        <div className="profile-referral-fields">
          <div>
            <span className="profile-referral-label">Code</span>
            <div className="profile-referral-code">
              <strong>{referralCode || "-"}</strong>
              {referralCode && (
                <button
                  type="button"
                  className="profile-referral-copy"
                  aria-label="Copy referral code"
                  onClick={() =>
                    void copyToClipboard(referralCode, "Referral code")
                  }
                >
                  <Copy size={18} aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
          <div>
            <span className="profile-referral-label">Applied Code</span>
            <strong>{appliedCode || "-"}</strong>
          </div>
          <div>
            <span className="profile-referral-label">Qualified</span>
            <strong>{qualified}</strong>
          </div>
        </div>

        <section className="profile-referral-review">
          <header>
            <div>
              <h4>Referral integrity review</h4>
              <p>Confirm every invite and its qualifying transaction before approving a withdrawal.</p>
            </div>
            <span>{Number(review.qualifiedCount || 0)} of {Number(review.totalInvited || 0)} qualified</span>
          </header>
          <div className="profile-referral-review-stats">
            <div><small>Total invited</small><strong>{Number(review.totalInvited || 0)}</strong></div>
            <div><small>Qualified</small><strong>{Number(review.qualifiedCount || 0)}</strong></div>
            <div><small>Pending</small><strong>{Number(review.pendingCount || 0)}</strong></div>
            <div><small>Rewards earned</small><strong>{naira(Number(review.totalReferralRewardsKobo || 0))}</strong></div>
          </div>
          <div className="profile-referral-review-list">
            {referredPeople.length ? referredPeople.map((entry, index) => {
              const qualifiedReferral = entry.qualified === true;
              return <article key={String(entry.uid || index)}>
                <div className="referral-review-person">
                  <span>{String(entry.name || "OP").split(/\s+/).slice(0, 2).map((part) => part[0] || "").join("").toUpperCase()}</span>
                  <div><strong>{String(entry.name || "OPrime user")}</strong><small>{String(entry.email || entry.phone || entry.uid || "—")}</small><small>Joined {formatDate(entry.joinedAt)}</small></div>
                </div>
                <div><small>Referral code</small><strong>{String(entry.referredByCode || "—")}</strong></div>
                <div><small>Qualifying transaction</small><strong>{String(entry.qualifyingTransactionTitle || "Not completed")}</strong><span>ID {String(entry.qualifyingTransactionId || "—")} · {naira(Number(entry.qualifyingTransactionAmount || 0))}</span></div>
                <span className={`referral-review-badge ${qualifiedReferral ? "qualified" : "pending"}`}>{qualifiedReferral ? <CheckCircle2 /> : <Clock3 />}{qualifiedReferral ? "Qualified" : "Pending"}</span>
              </article>;
            }) : <p className="referral-empty">This user has not referred anyone yet.</p>}
          </div>
        </section>
      </div>
    </section>
  );
}

function ProfileKyc({
  submission,
}: {
  submission?: Record<string, unknown> | null;
}) {
  const resolveValue = (...keys: string[]) => {
    for (const key of keys) {
      const value = submission?.[key];
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        return value;
      }
    }
    return undefined;
  };

  const displayValue = (...keys: string[]) => {
    const value = resolveValue(...keys);
    return value === undefined ? "-" : String(value);
  };

  const displayDate = (...keys: string[]) => {
    const value = resolveValue(...keys);
    if (value === undefined) return "-";

    let millis: number | null = null;
    if (typeof value === "number") {
      millis = value;
    } else if (value instanceof Date) {
      millis = value.getTime();
    } else if (
      typeof value === "object" &&
      value !== null &&
      typeof (value as { toMillis?: unknown }).toMillis === "function"
    ) {
      millis = (value as { toMillis: () => number }).toMillis();
    } else if (
      typeof value === "object" &&
      value !== null &&
      typeof (value as { seconds?: unknown }).seconds === "number"
    ) {
      millis = Number((value as { seconds: number }).seconds) * 1000;
    } else if (typeof value === "string") {
      const parsed = Date.parse(value);
      if (!Number.isNaN(parsed)) millis = parsed;
    }

    if (millis !== null && Number.isFinite(millis)) {
      return new Intl.DateTimeFormat("en-NG", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }).format(new Date(millis));
    }

    return String(value);
  };

  const status = formatKycStatus(
    String(
      resolveValue("status", "kycStatus", "verificationStatus") ||
        "not_submitted",
    ),
  );
  const tier = displayValue("tier", "kycTier", "level");
  const fields = [
    ["Tier", tier === "-" ? "0" : tier],
    ["BVN", displayValue("bvn", "bvnNumber", "bvnNo")],
    ["NIN", displayValue("nin", "ninNumber", "ninNo")],
    [
      "Submitted",
      displayDate("submittedAt", "createdAt", "submittedOn", "submitted_on"),
    ],
    ["Reviewed", displayDate("reviewedAt", "verifiedAt", "approvedAt", "reviewedOn")],
  ];

  return (
    <section className="profile-kyc-shell">
      <div className="profile-kyc-card">
        <div className="profile-kyc-heading">
          <ShieldCheck aria-hidden="true" />
          <h3>{status}</h3>
        </div>

        <div className="profile-kyc-fields">
          {fields.map(([label, value]) => (
            <div className="profile-kyc-field" key={label}>
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProfileAmlReview({
  rows,
}: {
  rows: Record<string, unknown>[];
}) {
  const normalizedStatus = (row: Record<string, unknown>) =>
    String(row.status || row.amlReviewStatus || "")
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_");

  const riskScore = (row: Record<string, unknown>) =>
    Number(row.riskScore || row.risk_score || 0);

  const senderName = (row: Record<string, unknown>) =>
    String(
      row.senderName ||
        row.sourceAccountName ||
        row.accountName ||
        row.sender_name ||
        "",
    ).trim();

  const metrics = [
    {
      label: "Max Risk Score",
      value: Math.max(0, ...rows.map(riskScore)),
    },
    {
      label: "Held Deposits",
      value: rows.filter((row) => normalizedStatus(row) === "held").length,
    },
    {
      label: "Approved Reviews",
      value: rows.filter((row) =>
        ["approved", "auto_approved", "released"].includes(
          normalizedStatus(row),
        ),
      ).length,
    },
    {
      label: "Rejected Reviews",
      value: rows.filter((row) =>
        ["rejected", "declined"].includes(normalizedStatus(row)),
      ).length,
    },
    {
      label: "Sender Names",
      value: new Set(rows.map(senderName).filter(Boolean)).size,
    },
  ];

  return (
    <section className="profile-aml-shell">
      <div className="profile-aml-card">
        <h3 className="profile-aml-heading">AML Risk Profile</h3>
        <div className="profile-aml-fields">
          {metrics.map(({ label, value }) => (
            <div className="profile-aml-field" key={label}>
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProfileDeposits({
  rows,
  auditLogs,
  runAction,
}: {
  rows: Transaction[];
  auditLogs: Record<string, unknown>[];
  runAction: RunAction;
}) {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState<Transaction | null>(null);
  const filtered = rows.filter((row) => {
    const matchesStatus =
      status === "all" || String(row.status || "").toLowerCase() === status;
    const haystack = [
      row.depositId,
      row.txId,
      row.bankName,
      row.senderName,
      row.sourceAccountName,
      row.senderAccountNumber,
      row.sourceAccountNumber,
    ]
      .join(" ")
      .toLowerCase();
    return matchesStatus && haystack.includes(search.trim().toLowerCase());
  });
  const exportRows = () => {
    const headings = [
      "Deposit ID",
      "Bank",
      "Amount",
      "Source Account Name",
      "Source Account Number",
      "Status",
      "Time",
    ];
    const csv = [
      headings,
      ...filtered.map((row) => [
        row.depositId ?? row.txId ?? row.id,
        row.bankName || "Kolomoni",
        row.total ?? row.amount ?? 0,
        row.sourceAccountName || row.senderName || "",
        row.sourceAccountNumber || row.senderAccountNumber || "",
        row.status || "",
        row.createdAt ? new Date(row.createdAt).toISOString() : "",
      ]),
    ]
      .map((line) =>
        line.map((value) => `"${String(value).replaceAll('"', '""')}"`).join(","),
      )
      .join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "user-deposits.csv";
    anchor.click();
    URL.revokeObjectURL(url);
    showFlushbar("Deposit data exported");
  };
  return (
    <section className="profile-deposits">
      <header>
        <div>
          <h2>Deposits</h2>
          <p>{filtered.length} deposit records</p>
        </div>
        <button className="deposit-export" onClick={exportRows}>
          <FileDown /> Export Data
        </button>
      </header>
      <div className="profile-deposit-toolbar">
        <label className="search">
          <Search />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search deposit ID, account or bank"
          />
        </label>
        <select value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="all">All statuses</option>
          <option value="success">Successful</option>
          <option value="completed">Completed</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
        </select>
      </div>
      <div className="profile-deposit-table">
        <div className="profile-deposit-row deposit-head">
          <span>ID</span>
          <span>Bank</span>
          <span>Amount</span>
          <span>Source Account Name</span>
          <span>Source Account No.</span>
          <span>Time</span>
          <span>Receipt</span>
        </div>
        {filtered.map((row) => {
          const accountNumber =
            row.sourceAccountNumber || row.senderAccountNumber || "—";
          return (
            <div className="profile-deposit-row" key={`${row.uid}-${row.id}`}>
              <strong>{row.depositId ?? row.txId ?? row.id}</strong>
              <span>{depositBankName(depositSourceName(row))}</span>
              <span className="deposit-regular">
                {naira(row.total ?? row.amount)}
              </span>
              <span className="deposit-regular">
                {personName(row.sourceAccountName || row.senderName)}
              </span>
              <span className="deposit-account">
                {accountNumber}
                {accountNumber !== "—" && (
                  <button
                    onClick={() =>
                      void copyToClipboard(accountNumber, "Account number")
                    }
                    aria-label="Copy account number"
                  >
                    <Copy />
                  </button>
                )}
              </span>
              <time>
                {row.createdAt ? depositDate.format(row.createdAt) : "—"}
              </time>
              <button
                className="deposit-view"
                onClick={() => setSelected(row)}
                aria-label="View deposit receipt"
              >
                <Eye />
              </button>
            </div>
          );
        })}
        {!filtered.length && <div className="empty">No matching deposits</div>}
      </div>
      {selected && (
        <TransactionPreview
          row={selected}
          deposits
          auditLogs={auditLogs}
          runAction={runAction}
          onClose={() => setSelected(null)}
        />
      )}
    </section>
  );
}

function ProfileWalletHistory({
  rows,
  user,
  auditLogs,
  runAction,
}: {
  rows: Transaction[];
  user: AdminUser;
  auditLogs: Record<string, unknown>[];
  runAction: RunAction;
}) {
  const [selected, setSelected] = useState<Transaction | null>(null);
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortAsc, setSortAsc] = useState<boolean>(false);

  const getIsCredit = (tx: Transaction): boolean => {
    const type = String(tx.type || "").toLowerCase();
    const status = String(tx.status || "").toLowerCase();
    const refunded =
      tx.refunded === true ||
      String(tx.adminReviewStatus || "").toLowerCase().includes("refund");
    return (
      refunded ||
      (type === "fund" && status !== "failed") ||
      type === "funding" ||
      type === "deposit" ||
      type === "wallet_funding" ||
      type === "credit" ||
      type === "admin_credit" ||
      ((type === "admin_wallet_adjustment" || type === "wallet_adjustment") &&
        String(tx.adjustmentAction || "").toLowerCase() === "credit")
    );
  };

  const getWalletTitle = (tx: Transaction): string => {
    const type = String(tx.type || "").toLowerCase();
    const isCredit = getIsCredit(tx);
    const isRefunded =
      tx.refunded === true ||
      String(tx.adminReviewStatus || tx.status || "").toLowerCase().includes("refund");

    const rawTitle = String(tx.title || "").trim();
    if (type === "admin_wallet_adjustment" || type === "wallet_adjustment") {
      const label = isCredit ? "Admin Wallet Credit" : "Admin Wallet Debit";
      const reason = String(tx.reason || tx.description || "").trim();
      return reason ? `${label} • ${reason}` : label;
    }
    const source = `${tx.network || ""} ${tx.provider || ""} ${rawTitle}`.toLowerCase();

    let network = "";
    if (source.includes("mtn")) network = "MTN";
    else if (source.includes("airtel")) network = "Airtel";
    else if (source.includes("glo")) network = "Glo";
    else if (source.includes("9mobile") || source.includes("etisalat")) network = "9mobile";

    let service = "";
    if (type === "fund" || type.includes("deposit") || type === "funding") {
      const rawBank = depositSourceName(tx);
      const bank = depositBankName(rawBank);
      const wallet = rawBank.toLowerCase().includes("opay")
        ? "OPay Wallet"
        : bank !== "—" ? `${bank} Wallet` : "Wallet Funding";
      service = wallet;
    } else if (type.includes("virtual") || rawTitle.toLowerCase().includes("virtual")) {
      service = "Virtual Number";
    } else if (type.includes("airtime") || rawTitle.toLowerCase().includes("airtime")) {
      service = `${network ? network + " " : ""}Airtime`;
    } else if (type.includes("data") || rawTitle.toLowerCase().includes("data")) {
      service = `${network ? network + " " : ""}Data`;
    } else if (type.includes("tv")) {
      service = "TV Subscription";
    } else if (type.includes("electric")) {
      service = "Electricity";
    } else if (type.includes("education")) {
      service = "Education";
    } else {
      service = rawTitle || "Transaction";
    }

    if (isRefunded) return `${service} RVSL`;
    return `${service} ${isCredit ? "Credit" : "Debit"}`;
  };

  const getBalanceBefore = (tx: Transaction): number | null => {
    if (tx.balanceBefore != null && !isNaN(tx.balanceBefore)) return tx.balanceBefore;
    if (tx.balanceAfter != null && !isNaN(tx.balanceAfter)) {
      const amt = tx.total ?? tx.amount ?? 0;
      return getIsCredit(tx) ? tx.balanceAfter - amt : tx.balanceAfter + amt;
    }
    return null;
  };

  const getBalanceAfter = (tx: Transaction): number | null => {
    if (tx.balanceAfter != null && !isNaN(tx.balanceAfter)) return tx.balanceAfter;
    if (tx.balanceBefore != null && !isNaN(tx.balanceBefore)) {
      const amt = tx.total ?? tx.amount ?? 0;
      return getIsCredit(tx) ? tx.balanceBefore + amt : tx.balanceBefore - amt;
    }
    return null;
  };

  const sortedRows = [...rows].sort((a, b) => {
    let valA: any = a[sortField as keyof Transaction];
    let valB: any = b[sortField as keyof Transaction];

    if (sortField === "title") {
      valA = getWalletTitle(a);
      valB = getWalletTitle(b);
    } else if (sortField === "balanceBefore") {
      valA = getBalanceBefore(a) ?? 0;
      valB = getBalanceBefore(b) ?? 0;
    } else if (sortField === "balanceAfter") {
      valA = getBalanceAfter(a) ?? 0;
      valB = getBalanceAfter(b) ?? 0;
    } else if (sortField === "amount") {
      valA = a.total ?? a.amount ?? 0;
      valB = b.total ?? b.amount ?? 0;
    } else if (sortField === "type") {
      valA = getIsCredit(a) ? "Credit" : "Debit";
      valB = getIsCredit(b) ? "Credit" : "Debit";
    }

    if (valA < valB) return sortAsc ? -1 : 1;
    if (valA > valB) return sortAsc ? 1 : -1;
    return 0;
  });

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const renderSortIndicator = (field: string) => {
    if (sortField !== field) return <span style={{ opacity: 0.4, marginLeft: 4 }}>⇅</span>;
    return <span style={{ marginLeft: 4 }}>{sortAsc ? "↑" : "↓"}</span>;
  };

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return { dateStr: "—", timeStr: "" };
    const d = new Date(timestamp);
    const dateStr = d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    const timeStr = d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return { dateStr, timeStr };
  };

  return (
    <div className="wallet-history-container">
      <div className="table-wrap">
        <table className="wallet-history-table-custom">
          <thead>
            <tr>
              <th onClick={() => handleSort("id")} style={{ cursor: "pointer" }}>
                ID {renderSortIndicator("id")}
              </th>
              <th onClick={() => handleSort("title")} style={{ cursor: "pointer" }}>
                Title {renderSortIndicator("title")}
              </th>
              <th onClick={() => handleSort("userName")} style={{ cursor: "pointer" }}>
                User {renderSortIndicator("userName")}
              </th>
              <th onClick={() => handleSort("balanceBefore")} style={{ cursor: "pointer" }}>
                Balance Before {renderSortIndicator("balanceBefore")}
              </th>
              <th onClick={() => handleSort("amount")} style={{ cursor: "pointer" }}>
                Amount {renderSortIndicator("amount")}
              </th>
              <th onClick={() => handleSort("balanceAfter")} style={{ cursor: "pointer" }}>
                Balance After {renderSortIndicator("balanceAfter")}
              </th>
              <th onClick={() => handleSort("type")} style={{ cursor: "pointer" }}>
                Type {renderSortIndicator("type")}
              </th>
              <th onClick={() => handleSort("createdAt")} style={{ cursor: "pointer" }}>
                Date {renderSortIndicator("createdAt")}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => {
              const isCredit = getIsCredit(row);
              const title = getWalletTitle(row);
              const balBefore = getBalanceBefore(row);
              const balAfter = getBalanceAfter(row);
              const amount = row.total ?? row.amount ?? 0;
              const { dateStr, timeStr } = formatDate(row.createdAt);
              const userPhoto = userAvatarSrc(
                user.photoUrl,
                user.avatarId,
                user.name || row.userName,
              );
              const userName = user.name || row.userName || "User";
              const userEmail = user.email || row.userEmail || user.phone || row.userPhone || "";

              return (
                <tr key={`${row.uid}-${row.id}`}>
                  <td className="wallet-cell-id">{row.txId ?? row.id}</td>
                  <td className="wallet-cell-title">
                    <strong>{title}</strong>
                  </td>
                  <td className="wallet-cell-user">
                    <div className="wallet-user-info">
                      <span className="wallet-user-avatar">
                        <LoadedAvatar src={userPhoto} fallback={initials(userName)} />
                      </span>
                      <div>
                        <strong>{userName}</strong>
                        <small>{userEmail}</small>
                      </div>
                    </div>
                  </td>
                  <td className="wallet-cell-money">
                    <strong>{balBefore != null ? naira(balBefore) : "—"}</strong>
                  </td>
                  <td className="wallet-cell-money">
                    <strong>{naira(amount)}</strong>
                  </td>
                  <td className="wallet-cell-money">
                    <strong>{balAfter != null ? naira(balAfter) : "—"}</strong>
                  </td>
                  <td>
                    <span className={`wallet-type-pill ${isCredit ? "credit" : "debit"}`}>
                      <span className="pill-circle">{isCredit ? "↓" : "↑"}</span>
                      {isCredit ? "Credit" : "Debit"}
                    </span>
                  </td>
                  <td className="wallet-cell-date">
                    <div>{dateStr}</div>
                    <small>{timeStr}</small>
                  </td>
                  <td>
                    <button
                      className="wallet-action-btn"
                      onClick={() => setSelected(row)}
                      aria-label="View transaction details"
                    >
                      <Eye />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!sortedRows.length && (
          <div className="empty" style={{ padding: 32, textAlign: "center", color: "var(--muted)" }}>
            No wallet history found
          </div>
        )}
      </div>
      {selected && (
        <TransactionPreview
          row={selected}
          auditLogs={auditLogs}
          runAction={runAction}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function UserAuditLog({
  rows,
  userId,
}: {
  rows: Record<string, unknown>[];
  userId: string;
}) {
  const description = (row: Record<string, unknown>) => {
    const fullAdmin = String(row.adminName || row.adminEmail || "Admin").trim();
    const admin = fullAdmin.split(/\s+/)[0] || "Admin";
    const actionCode = String(row.action || "").toLowerCase();
    const amount = Number(row.amount || 0);
    if (actionCode === "user_watchlist_add") {
      return `${admin} added user to watchlist.`;
    }
    if (actionCode === "user_watchlist_remove") {
      return `${admin} removed user from watchlist.`;
    }
    if (actionCode === "user_suspend") return `${admin} suspended user.`;
    if (actionCode === "user_unsuspend") return `${admin} unsuspended user.`;
    if (actionCode.endsWith("wallet_credit") || actionCode === "wallet_credit") {
      return `${admin} credited user${amount ? ` ${naira(Math.abs(amount))}` : ""}.`;
    }
    if (actionCode.endsWith("wallet_debit") || actionCode === "wallet_debit") {
      return `${admin} deducted${amount ? ` ${naira(Math.abs(amount))}` : " funds"} from user.`;
    }
    const action = actionCode
      .replaceAll("_", " ")
      .replace(/^user /, "")
      .replace(/^admin wallet /, "")
      .replace(/^wallet /, "") || "updated user";
    return `${admin} ${action} for user ${userId}.`;
  };
  return (
    <section className="user-audit">
      <h2>Audit Log</h2>
      {rows.length ? (
        rows.map((row, index) => (
          <article key={String(row.id || index)}>
            <time>
              {row.createdAt ? date.format(Number(row.createdAt)) : "—"}
            </time>
            <p>{description(row)}</p>
            {Boolean(row.reason) && (
              <small>Reason: {String(row.reason)}</small>
            )}
          </article>
        ))
      ) : (
        <div className="empty">No user administration audit recorded.</div>
      )}
    </section>
  );
}

function ActivityPage({
  rows,
  admins,
  broadcasts,
  disputes,
  users,
  runAction,
}: {
  rows: Record<string, unknown>[];
  admins: Record<string, unknown>[];
  broadcasts: Record<string, unknown>[];
  disputes: Record<string, unknown>[];
  users: AdminUser[];
  runAction: RunAction;
}) {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const ordered = useMemo(() => [...rows].sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0)), [rows]);
  const actionCategory = (row: Record<string, unknown>) => {
    const action = String(row.action || "").toLowerCase();
    if (action.includes("wallet") || action.includes("fund")) return "wallet";
    if (action.includes("review") || action.includes("transaction")) return "transaction";
    if (action.includes("kyc") || action.includes("aml") || action.includes("watchlist")) return "compliance";
    if (action.includes("broadcast") || action.includes("campaign") || action.includes("banner")) return "communication";
    if (action.includes("user") || action.includes("admin")) return "account";
    return "system";
  };
  const actionLabel = (value: unknown) => String(value || "Administrative action")
    .replace(/^admin_/, "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
  const filtered = useMemo(() => ordered.filter((row) => {
    if (category !== "all" && actionCategory(row) !== category) return false;
    const needle = search.trim().toLowerCase();
    if (!needle) return true;
    return [row.action, row.adminName, row.adminEmail, row.userName, row.userEmail, row.uid, row.txId, row.reason]
      .some((value) => String(value || "").toLowerCase().includes(needle));
  }), [ordered, category, search]);
  const adminSummary = useMemo(() => {
    const grouped = new Map<string, { name: string; email: string; count: number; latest: Record<string, unknown> }>();
    ordered.forEach((row) => {
      const email = String(row.adminEmail || "").trim();
      const name = String(row.adminName || email || "System").trim();
      const key = String(row.adminUid || email || name).toLowerCase();
      const current = grouped.get(key);
      if (current) current.count += 1;
      else grouped.set(key, { name, email, count: 1, latest: row });
    });
    return [...grouped.values()].sort((a, b) => b.count - a.count);
  }, [ordered]);
  const activeAdmins = admins.filter((row) => row.active !== false && row.disabled !== true).length;
  const exportActivity = () => {
    const header = ["Date", "Admin", "Action", "User", "Transaction", "Reason", "Amount"];
    const lines = filtered.map((row) => [
      new Date(Number(row.createdAt || 0)).toISOString(), row.adminName || row.adminEmail || "System",
      row.action || "", row.userName || row.userEmail || row.uid || "", row.txId || "", row.reason || "", Number(row.amount || 0) / 100,
    ].map((value) => `"${String(value).replaceAll('"', '""')}"`).join(","));
    const url = URL.createObjectURL(new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "admin-activity.csv";
    anchor.click();
    URL.revokeObjectURL(url);
  };
  const openUser = (row: Record<string, unknown>) => {
    const uid = String(row.uid || "");
    const email = String(row.userEmail || "").toLowerCase();
    const match = users.find((user) => user.uid === uid || (!!email && user.email?.toLowerCase() === email));
    if (match) setSelectedUser(match);
  };
  return <div className="activity-page">
    <section className="activity-metrics">
      {[
        { label: "Audit events", value: rows.length, Icon: Activity, note: "Actions captured in the current live window" },
        { label: "Active admins", value: activeAdmins, Icon: ShieldCheck, note: "Administrators with enabled access" },
        { label: "Broadcasts", value: broadcasts.length, Icon: Radio, note: "Messages sent from the console" },
        { label: "Disputes", value: disputes.length, Icon: MessageSquare, note: "Customer cases currently loaded" },
      ].map(({ label, value, Icon, note }) => <article key={label}><span><Icon /></span><div><small>{label}</small><strong>{value}</strong><p>{note}</p></div></article>)}
    </section>

    <section className="activity-panel admin-summary-panel">
      <header><div><i /><span><h2>Admin Activity Summary</h2><p>See who is operating the console and their latest action.</p></span></div><em><span /> Live data</em></header>
      <div className="admin-activity-list">
        {adminSummary.length ? adminSummary.map((admin) => <article key={`${admin.name}-${admin.email}`}>
          <span className="admin-activity-avatar">{initials(admin.name)}</span>
          <div><strong>{admin.name}</strong><p>Last action: {actionLabel(admin.latest.action)}</p><small>{formatDate(admin.latest.createdAt)}</small></div>
          <span className="admin-action-count"><small>Actions</small><strong>{admin.count}</strong></span>
        </article>) : <div className="activity-empty-state"><Activity /><strong>No admin activity yet</strong></div>}
      </div>
    </section>

    <section className="activity-panel recent-activity-panel">
      <header><div><i /><span><h2>Recent Activity</h2><p>A chronological, searchable audit trail of administrative changes.</p></span></div><button onClick={exportActivity}><FileDown /> Export CSV</button></header>
      <div className="activity-controls">
        <label><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search admin, user, transaction or reason" /></label>
        <select value={category} onChange={(event) => setCategory(event.target.value)}>
          <option value="all">All activity</option><option value="wallet">Wallet & funding</option><option value="transaction">Transactions</option><option value="compliance">Compliance</option><option value="communication">Communication</option><option value="account">Users & admins</option><option value="system">System</option>
        </select>
        <span>{filtered.length} results</span>
      </div>
      <div className="activity-timeline">
        {filtered.length ? filtered.map((row, index) => {
          const amount = Number(row.amount || 0);
          const categoryName = actionCategory(row);
          const hasUser = Boolean(row.uid || row.userEmail);
          return <article key={String(row.id || index)}>
            <span className={`activity-event-icon ${categoryName}`}><Activity /></span>
            <div className="activity-event-copy"><div><strong>{actionLabel(row.action)}</strong><span className={`activity-category ${categoryName}`}>{actionLabel(categoryName)}</span></div><p>
              <b>{String(row.adminName || row.adminEmail || "System")}</b>
              {hasUser && <> acted on <button onClick={() => openUser(row)}>{String(row.userName || row.userEmail || row.uid)}</button></>}
              {row.txId ? <> · Transaction <code>{String(row.txId)}</code></> : null}
            </p>{row.reason ? <small>Reason: {String(row.reason)}</small> : <small>{String(row.statusBefore || "")} {row.statusAfter ? `→ ${String(row.statusAfter)}` : ""}</small>}</div>
            <div className="activity-event-meta">{amount !== 0 && <strong className={amount < 0 ? "debit" : "credit"}>{naira(Math.abs(amount))}</strong>}<time>{formatDate(row.createdAt)}</time></div>
          </article>;
        }) : <div className="activity-empty-state"><Search /><strong>No matching activity</strong><p>Try another search or category.</p></div>}
      </div>
    </section>
    {selectedUser && <UserProfileModal initialUser={selectedUser} runAction={runAction} onClose={() => setSelectedUser(null)} />}
  </div>;
}

function RecordPage({
  title,
  subtitle,
  rows,
}: {
  title: string;
  subtitle: string;
  rows: Record<string, unknown>[];
}) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(
    () =>
      rows.filter((row) =>
        JSON.stringify(row).toLowerCase().includes(query.toLowerCase()),
      ),
    [rows, query],
  );
  const columns = useMemo(() => {
    const preferred = [
      "id",
      "name",
      "userName",
      "email",
      "userEmail",
      "status",
      "type",
      "provider",
      "amount",
      "createdAt",
    ];
    const keys = new Set(
      filtered
        .slice(0, 10)
        .flatMap((row) =>
          Object.keys(row).filter((key) => typeof row[key] !== "object"),
        ),
    );
    return [
      ...preferred.filter((key) => keys.has(key)),
      ...[...keys].filter((key) => !preferred.includes(key)),
    ].slice(0, 7);
  }, [filtered]);
  return (
    <div className="panel">
      <div className="panel-title">
        <div>
          <h2>{title}</h2>
          <p>
            {subtitle} · {filtered.length} loaded
          </p>
        </div>
      </div>
      <div className="toolbar">
        <div className="search">
          <Search />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${title.toLowerCase()}`}
          />
        </div>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column}>{humanize(column)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((row, index) => (
              <tr key={String(row.id ?? index)}>
                {columns.map((column) => (
                  <td key={column}>{displayValue(row[column], column)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {!filtered.length && <div className="empty">No matching records</div>}
      </div>
    </div>
  );
}

function KycReviewPage({ rows, runAction }: { rows: Record<string, unknown>[]; runAction: RunAction }) {
  const pending = rows.filter((row) => String(row.status ?? "pending").toLowerCase() === "pending");
  const recent = rows.filter((row) => String(row.status ?? "pending").toLowerCase() !== "pending").slice(0, 80);

  const review = async (row: Record<string, unknown>, action: "approve" | "reject") => {
    const uid = String(row.uid ?? row.id ?? "").trim();
    if (!uid) return;
    if (action === "approve") {
      if (!await confirmAction({ title: "Approve KYC submission?", message: `Confirm that you have reviewed the identity documents for ${String(row.name ?? row.email ?? "this user")}.`, confirmLabel: "Approve KYC", tone: "primary" })) return;
      void runAction("adminReviewKyc", { uid, action, reason: "" }, "KYC approved");
      return;
    }
    const reason = window.prompt("KYC rejection reason", "");
    if (!reason?.trim()) return;
    void runAction("adminReviewKyc", { uid, action, reason: reason.trim() }, "KYC rejected");
  };

  const card = (row: Record<string, unknown>, index: number) => {
    const status = String(row.status ?? "pending").toLowerCase();
    const isPending = status === "pending";
    const isApproved = status === "approved";
    const photo = String(row.photoUrl ?? row.profilePhotoUrl ?? row.avatarUrl ?? "").trim();
    const StatusIcon = isApproved ? CheckCircle2 : isPending ? Clock3 : XCircle;
    const fields = [
      ["User ID", row.userId ?? row.uid ?? row.id ?? "—"],
      ["Phone", row.phone ?? "—"],
      ["BVN", row.bvnMasked ?? "—"],
      ["NIN", row.ninMasked ?? "—"],
      ["Submitted", formatDate(row.submittedAt)],
      ...(!isPending ? [["Reviewed by", row.reviewedByName ?? row.reviewedByEmail ?? "—"]] : []),
    ];
    return <article className={`kyc-card ${status}`} key={String(row.id ?? row.uid ?? index)}>
      <div className="kyc-card-head">
        <div className="kyc-person">
          <span className="kyc-avatar">{photo ? <img src={photo} alt="" /> : <Users />}</span>
          <span><strong>{String(row.name ?? row.email ?? "User")}</strong><small>{String(row.email ?? "")}</small></span>
        </div>
        <span className={`kyc-status ${status}`}><StatusIcon /> {humanize(status)}</span>
      </div>
      <div className="kyc-fields">
        {fields.map(([label, value]) => <div key={String(label)}><small>{String(label)}</small><strong>{String(value)}</strong></div>)}
      </div>
      {String(row.rejectionReason ?? "").trim() && <div className="kyc-reason"><strong>Reason:</strong> {String(row.rejectionReason)}</div>}
      {isPending && <div className="kyc-actions">
        <button className="approve" onClick={() => review(row, "approve")}><CheckCircle2 /> Approve KYC</button>
        <button className="reject" onClick={() => review(row, "reject")}><Ban /> Reject</button>
      </div>}
    </article>;
  };

  return <div className="kyc-review-page">
    <section className="kyc-panel">
      <header><div><i /><h2>Pending KYC</h2></div><span><i /> Live updates</span></header>
      <div className="kyc-list">{pending.length ? pending.map(card) : <p className="kyc-empty">No pending KYC submissions.</p>}</div>
    </section>
    <section className="kyc-panel">
      <header><div><i /><h2>Recent KYC Decisions</h2></div><span><i /> Live updates</span></header>
      <div className="kyc-list">{recent.length ? recent.map(card) : <p className="kyc-empty">No reviewed KYC submissions yet.</p>}</div>
    </section>
  </div>;
}

function AmlReviewPage({ reviews, settings, auditLogs, users, runAction }: {
  reviews: Record<string, unknown>[];
  settings: Record<string, unknown>;
  auditLogs: Record<string, unknown>[];
  users: AdminUser[];
  runAction: RunAction;
}) {
  const [localSettings, setLocalSettings] = useState(settings);
  const [auditSearch, setAuditSearch] = useState("");
  const [auditFilter, setAuditFilter] = useState("all");
  useEffect(() => setLocalSettings(settings), [settings]);
  const auditUsers = useMemo(() => new Map(users.map((user) => [String(user.uid ?? (user as Record<string, unknown>).id ?? ""), user])), [users]);
  const auditUserLabel = useCallback((log: Record<string, unknown>) => {
    const uid = String(log.uid ?? "").trim();
    const profile = auditUsers.get(uid);
    const loggedName = String(log.userName ?? "").trim();
    const resolvedName = String(profile?.name ?? (profile as Record<string, unknown> | undefined)?.displayName ?? "").trim();
    const email = String(profile?.email ?? log.userEmail ?? "").trim();
    const name = resolvedName || (loggedName && loggedName !== uid ? loggedName : "") || email || uid || "—";
    return { name, email, uid };
  }, [auditUsers]);
  const amlRef = doc(db, "admin_provider_config", "aml");
  const moneyFields = new Set(["singleDepositReviewLimitKobo", "unverifiedDailyLimitKobo", "unverifiedMonthlyLimitKobo", "newAccountLargeDepositLimitKobo"]);
  const numberSettings = [
    ["singleDepositReviewLimitKobo", "Single review limit", 2000000],
    ["unverifiedDailyLimitKobo", "Daily unverified limit", 5000000],
    ["unverifiedMonthlyLimitKobo", "Monthly unverified limit", 20000000],
    ["newAccountLargeDepositLimitKobo", "New account limit", 1000000],
    ["rapidDepositCount", "Rapid deposits", 3],
    ["rapidDepositWindowMinutes", "Rapid window", 10],
    ["senderFanoutUserLimit", "Sender fanout", 3],
    ["uniqueSenderDailyLimit", "Unique senders", 3],
    ["holdRiskScore", "Hold score", 35],
    ["highRiskScore", "High score", 70],
  ] as const;
  const switches = [
    ["requireReviewForNameMismatch", "Name mismatch review"],
    ["requireReviewForMissingReference", "Missing ref review"],
    ["autoSuspendHighRisk", "Auto suspend high risk"],
  ] as const;

  const saveSetting = async (field: string, value: number | boolean) => {
    try {
      await setDoc(amlRef, { [field]: value, updatedAt: serverTimestamp(), updatedBy: auth.currentUser?.email ?? auth.currentUser?.uid ?? "admin" }, { merge: true });
      setLocalSettings((current) => ({ ...current, [field]: value }));
      showFlushbar("AML setting updated");
    } catch (failure) {
      showFlushbar(failure instanceof Error ? failure.message : "Could not update AML setting", true);
    }
  };
  const editSetting = (field: string, label: string, fallback: number) => {
    const isMoney = moneyFields.has(field);
    const current = Number(localSettings[field] ?? fallback);
    const entered = window.prompt(`${label}${isMoney ? " (amount in naira)" : ""}`, String(isMoney ? current / 100 : current));
    if (entered === null) return;
    const parsed = Number(entered.replaceAll(",", ""));
    if (!Number.isFinite(parsed) || parsed < 0) {
      showFlushbar("Enter a valid non-negative number", true);
      return;
    }
    void saveSetting(field, isMoney ? Math.round(parsed * 100) : parsed);
  };
  const displaySetting = (field: string, fallback: number) => {
    const value = Number(localSettings[field] ?? fallback);
    if (moneyFields.has(field)) return naira(value);
    if (field === "rapidDepositCount") return `${value} in ${Number(localSettings.rapidDepositWindowMinutes ?? 10)} min`;
    if (field === "rapidDepositWindowMinutes") return `${value} min`;
    if (field === "senderFanoutUserLimit") return `${value} users`;
    if (field === "uniqueSenderDailyLimit") return `${value} daily`;
    return String(value);
  };
  const resolveReview = async (review: Record<string, unknown>, action: "approve" | "reject") => {
    let reason = "Approved by admin";
    let suspendUser = false;
    if (action === "reject") {
      const entered = window.prompt("Deposit rejection reason", "");
      if (!entered?.trim()) return;
      reason = entered.trim();
      suspendUser = await confirmAction({ title: "Suspend this user too?", message: "The deposit will be rejected either way. Choose whether this user's account should also be suspended.", confirmLabel: "Reject & suspend", cancelLabel: "Reject only", tone: "danger" });
    } else if (!await confirmAction({ title: "Approve held deposit?", message: `This will credit ${String(review.userName ?? "this user")}'s wallet with the held deposit.`, confirmLabel: "Approve & credit", tone: "primary" })) return;
    void runAction("adminResolveHeldFunding", { uid: review.uid, id: review.id, action, reason, suspendUser }, action === "approve" ? "Deposit approved and credited" : "Deposit rejected");
  };

  const visibleLogs = useMemo(() => auditLogs.filter((log) => {
    const action = String(log.action ?? "").toLowerCase();
    const wallet = action === "wallet_credit" || action === "wallet_debit" || action.includes("wallet_adjust");
    if (auditFilter === "wallet" && !wallet) return false;
    if (auditFilter === "aml" && !/(fund|aml|review)/.test(action)) return false;
    const resolvedUser = auditUserLabel(log);
    const haystack = `${log.action} ${resolvedUser.name} ${resolvedUser.email} ${resolvedUser.uid} ${log.txId} ${log.adminName} ${log.adminEmail} ${log.reason}`.toLowerCase();
    return haystack.includes(auditSearch.toLowerCase());
  }), [auditLogs, auditFilter, auditSearch, auditUserLabel]);

  return <div className="aml-page">
    <section className="aml-panel">
      <header><div><i /><h2>AML Settings</h2></div><span><i /> Live configuration</span></header>
      <div className="aml-settings-grid">
        {numberSettings.map(([field, label, fallback]) => <button key={field} onClick={() => editSetting(field, label, fallback)}>
          <span><small>{label}</small><strong>{displaySetting(field, fallback)}</strong></span><Pencil />
        </button>)}
      </div>
      <div className="aml-switches">
        {switches.map(([field, label]) => <label key={field}><strong>{label}</strong><input type="checkbox" checked={localSettings[field] === true} onChange={(event) => void saveSetting(field, event.target.checked)} /><span /></label>)}
      </div>
    </section>

    <section className="aml-panel">
      <header><div><i /><h2>Held Funding Review Queue</h2></div><span><i /> {reviews.length} held</span></header>
      <div className="aml-review-list">{reviews.length ? reviews.map((review, index) => {
        const risk = String(review.riskLevel ?? "medium").toLowerCase();
        const reasons = Array.isArray(review.reasons) ? review.reasons : [];
        return <article className={`aml-review-card ${risk}`} key={String(review.id ?? index)}>
          <div className="aml-review-head"><div><strong>{String(review.userName ?? "User")}</strong><small>{String(review.userEmail ?? "")}</small></div><span><CircleAlert /> {humanize(risk)} risk · {String(review.riskScore ?? 0)}</span></div>
          <div className="aml-review-fields">{[
            ["Amount", naira(Number(review.amount ?? 0))], ["Gross", naira(Number(review.gross ?? 0))], ["Sender", review.senderName ?? "—"], ["Phone", review.userPhone ?? "—"], ["Provider", review.provider ?? "—"], ["Provider Ref", review.providerRef ?? "—"], ["Received", formatDate(review.createdAt)],
          ].map(([label, value]) => <div key={String(label)}><small>{String(label)}</small><strong>{String(value)}</strong></div>)}</div>
          {reasons.length > 0 && <div className="aml-reasons">{reasons.map((reason) => <span key={String(reason)}>{String(reason)}</span>)}</div>}
          <div className="aml-review-actions"><button className="approve" onClick={() => resolveReview(review, "approve")}><CheckCircle2 /> Approve & credit</button><button className="reject" onClick={() => resolveReview(review, "reject")}><Ban /> Reject</button></div>
        </article>;
      }) : <p className="aml-empty">No held deposits right now.</p>}</div>
    </section>

    <section className="aml-panel audit-panel">
      <header><div><i /><h2>Audit Logs</h2></div><span><i /> {visibleLogs.length} records</span></header>
      <div className="audit-toolbar"><div className="search"><Search /><input value={auditSearch} onChange={(event) => setAuditSearch(event.target.value)} placeholder="Search user, admin, action, transaction or reason" /></div><select value={auditFilter} onChange={(event) => setAuditFilter(event.target.value)}><option value="all">All activity</option><option value="wallet">Wallet adjustments</option><option value="aml">AML & funding</option></select></div>
      <div className="audit-list">{visibleLogs.map((log, index) => {
        const action = String(log.action ?? "activity").toLowerCase();
        const isCredit = action === "wallet_credit";
        const isDebit = action === "wallet_debit";
        const isWallet = isCredit || isDebit || action.includes("wallet_adjust");
        const resolvedUser = auditUserLabel(log);
        const beforeValue = isWallet && Number.isFinite(Number(log.statusBefore)) ? naira(Number(log.statusBefore)) : String(log.statusBefore ?? "—");
        const afterValue = isWallet && Number.isFinite(Number(log.statusAfter)) ? naira(Number(log.statusAfter)) : String(log.statusAfter ?? "—");
        return <article className={`audit-card ${isCredit ? "credit" : isDebit ? "debit" : ""}`} key={String(log.id ?? index)}>
          <span className="audit-icon">{isWallet ? <WalletCards /> : <Clock3 />}</span>
          <div className="audit-main"><div><strong>{humanize(action)}</strong>{isWallet && <span className="audit-kind">Wallet adjustment</span>}</div><p><b>User:</b> {resolvedUser.name}{resolvedUser.email && resolvedUser.email !== resolvedUser.name ? ` · ${resolvedUser.email}` : ""}</p>{resolvedUser.uid && <p className="audit-uid"><b>User ID:</b> {resolvedUser.uid}</p>}<p><b>Admin:</b> {String(log.adminName ?? log.adminEmail ?? log.adminUid ?? "—")}</p>{String(log.reason ?? "").trim() && <p><b>Reason:</b> {String(log.reason)}</p>}<small>{formatDate(log.createdAt)}{log.txId ? ` · Transaction ${String(log.txId)}` : ""}</small></div>
          {Number(log.amount ?? 0) !== 0 && <strong className="audit-amount">{isWallet ? (Number(log.amount) > 0 ? "+" : "−") : ""}{naira(Math.abs(Number(log.amount)))}</strong>}
          {Boolean(log.statusBefore || log.statusAfter) && <div className="audit-change"><small>Before</small><strong>{beforeValue}</strong><i /><small>After</small><strong>{afterValue}</strong></div>}
        </article>;
      })}{!visibleLogs.length && <p className="aml-empty">No audit records match this filter.</p>}</div>
    </section>
  </div>;
}

function DisputesPage({ rows, users, runAction }: { rows: Record<string, unknown>[]; users: AdminUser[]; runAction: RunAction }) {
  const openStatuses = new Set(["open", "pending", "in_review"]);
  const open = rows.filter((row) => openStatuses.has(String(row.status ?? "open").toLowerCase()));
  const closed = rows.filter((row) => !openStatuses.has(String(row.status ?? "open").toLowerCase())).slice(0, 80);
  const userMap = useMemo(() => new Map(users.map((user) => [user.uid, user])), [users]);
  const userEmailMap = useMemo(() => new Map(users.filter((user) => user.email).map((user) => [String(user.email).trim().toLowerCase(), user])), [users]);

  const update = (row: Record<string, unknown>, status: "in_review" | "resolved" | "rejected") => {
    const note = window.prompt(status === "resolved" ? "Resolution note for the user" : status === "rejected" ? "Reason for rejecting this dispute" : "Internal review note (optional)", String(row.adminNote ?? ""));
    if (note === null && status !== "in_review") return;
    void runAction("adminResolveDispute", { id: row.id, status, adminNote: note ?? "" }, status === "resolved" ? "Dispute resolved" : status === "rejected" ? "Dispute rejected" : "Dispute moved to review");
  };

  const card = (row: Record<string, unknown>, index: number) => {
    const status = String(row.status ?? "open").toLowerCase();
    const priority = String(row.priority ?? "normal").toLowerCase();
    const isOpen = openStatuses.has(status);
    const uid = String(row.uid ?? "");
    const savedEmail = String(row.userEmail ?? "").trim().toLowerCase();
    const profile = userMap.get(uid) ?? userEmailMap.get(savedEmail);
    const userName = String(row.userName ?? profile?.name ?? row.userEmail ?? profile?.email ?? "User");
    const userEmail = String(row.userEmail ?? profile?.email ?? "");
    const displayUserId = String(row.userId ?? profile?.userId ?? profile?.customerNo ?? uid ?? "").trim() || "—";
    const StatusIcon = status === "resolved" ? CheckCircle2 : status === "rejected" ? XCircle : status === "in_review" ? Search : Clock3;
    const proofUrl = String(row.proofUrl ?? "").trim();
    return <article className={`dispute-card ${status}`} key={String(row.id ?? index)}>
      <div className="dispute-card-head">
        <div><strong>{String(row.subject ?? "Dispute")}</strong><small>{userName}{userEmail && userEmail !== userName ? ` · ${userEmail}` : ""}</small></div>
        <div className="dispute-badges"><span className={`dispute-priority ${priority}`}>{humanize(priority)} priority</span><span className={`dispute-status ${status}`}><StatusIcon /> {humanize(status)}</span></div>
      </div>
      <p className="dispute-message">{String(row.message ?? "No description supplied.")}</p>
      <div className="dispute-fields">{[
        ["User ID", displayUserId], ["Transaction ID", row.txId ?? "—"], ["Category", row.category ?? "—"], ["Created", formatDate(row.createdAt)], ...(row.resolvedAt ? [["Closed", formatDate(row.resolvedAt)]] : []),
      ].map(([label, value]) => <div key={String(label)}><small>{String(label)}</small><strong>{String(value || "—")}</strong></div>)}</div>
      {String(row.adminNote ?? "").trim() && <div className="dispute-note"><strong>Admin note</strong><p>{String(row.adminNote)}</p>{String(row.resolvedByName ?? "").trim() && <small>Updated by {String(row.resolvedByName)}</small>}</div>}
      {proofUrl && <div className="dispute-proof"><span className="dispute-proof-preview"><img src={proofUrl} alt="Uploaded dispute proof" /></span><div><strong>Uploaded proof</strong><small>{String(row.proofName ?? "Screenshot")}</small></div><a href={proofUrl} target="_blank" rel="noreferrer"><Eye /> Open proof</a></div>}
      {isOpen && <div className="dispute-actions"><button className="resolve" onClick={() => update(row, "resolved")}><CheckCircle2 /> Resolve</button><button className="review" onClick={() => update(row, "in_review")}><Search /> In review</button><button className="reject" onClick={() => update(row, "rejected")}><Ban /> Reject</button></div>}
    </article>;
  };

  return <div className="disputes-page">
    <div className="dispute-metrics"><article><span><Mail /></span><div><small>Open Disputes</small><strong>{open.length}</strong></div></article><article><span><CheckCircle2 /></span><div><small>Closed</small><strong>{closed.length}</strong></div></article><article><span><CircleAlert /></span><div><small>High Priority</small><strong>{open.filter((row) => String(row.priority ?? "").toLowerCase() === "high").length}</strong></div></article></div>
    <section className="dispute-panel"><header><div><i /><h2>Open Disputes</h2></div><span><i /> {open.length} active</span></header><div className="dispute-list">{open.length ? open.map(card) : <p className="dispute-empty">No open disputes right now.</p>}</div></section>
    <section className="dispute-panel"><header><div><i /><h2>Recent Closed Disputes</h2></div><span><i /> {closed.length} records</span></header><div className="dispute-list">{closed.length ? closed.map(card) : <p className="dispute-empty">No closed disputes yet.</p>}</div></section>
  </div>;
}

function ReviewPage({
  kind,
  rows,
  runAction,
}: {
  kind: "kyc" | "aml" | "disputes";
  rows: Record<string, unknown>[];
  runAction: RunAction;
}) {
  const title =
    kind === "kyc" ? "KYC Review" : kind === "aml" ? "AML Review" : "Disputes";
  const subtitle =
    kind === "kyc"
      ? "Identity submissions requiring review"
      : kind === "aml"
        ? "Held deposits and risk decisions"
        : "Customer transaction disputes";
  const act = async (row: Record<string, unknown>, action: string) => {
    if (kind === "kyc") {
      const reason =
        action === "reject" ? window.prompt("KYC rejection reason") : "";
      if (action === "reject" && !reason) return;
      if (
        action === "approve" &&
        !await confirmAction({ title: "Approve KYC submission?", message: "Confirm that the submitted identity details and documents have been reviewed.", confirmLabel: "Approve KYC", tone: "primary" })
      )
        return;
      void runAction(
        "adminReviewKyc",
        { uid: row.uid ?? row.id, action, reason },
        action === "approve" ? "KYC approved" : "KYC rejected",
      );
    } else if (kind === "aml") {
      const reason =
        action === "reject"
          ? window.prompt("Deposit rejection reason")
          : "Approved by admin";
      if (reason == null) return;
      const suspendUser = action === "reject" && await confirmAction({ title: "Suspend this user too?", message: "The held deposit will be rejected. You can also prevent this user from accessing the account.", confirmLabel: "Reject & suspend", cancelLabel: "Reject only", tone: "danger" });
      void runAction(
        "adminResolveHeldFunding",
        { uid: row.uid, id: row.id, action, reason, suspendUser },
        action === "approve"
          ? "Deposit approved and credited"
          : "Deposit rejected",
      );
    } else {
      const note = window.prompt("Admin note", String(row.adminNote ?? ""));
      if (note == null) return;
      void runAction(
        "adminResolveDispute",
        { id: row.id, status: action, adminNote: note },
        "Dispute updated",
      );
    }
  };
  const columns =
    kind === "kyc"
      ? ["name", "email", "phone", "documentType", "status", "submittedAt"]
      : kind === "aml"
        ? [
            "userName",
            "userEmail",
            "provider",
            "amount",
            "riskScore",
            "riskLevel",
            "status",
            "createdAt",
          ]
        : ["userName", "type", "reason", "status", "createdAt"];
  return (
    <div className="panel">
      <div className="panel-title">
        <div>
          <h2>{title}</h2>
          <p>
            {subtitle} · {rows.length} loaded
          </p>
        </div>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column}>{humanize(column)}</th>
              ))}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={String(row.id ?? index)}>
                {columns.map((column) => (
                  <td key={column}>{displayValue(row[column], column)}</td>
                ))}
                <td>
                  <div className="row-actions">
                    {kind === "disputes" ? (
                      <>
                        <button onClick={() => act(row, "in_review")}>
                          In Review
                        </button>
                        <button onClick={() => act(row, "resolved")}>
                          Resolve
                        </button>
                        <button
                          className="danger"
                          onClick={() => act(row, "rejected")}
                        >
                          Reject
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => act(row, "approve")}>
                          Approve
                        </button>
                        <button
                          className="danger"
                          onClick={() => act(row, "reject")}
                        >
                          Reject
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && <div className="empty">No records require review</div>}
      </div>
    </div>
  );
}

function HealthPage({
  providers,
  events,
  runAction,
}: {
  providers: Record<string, unknown>[];
  events: Record<string, unknown>[];
  runAction: RunAction;
}) {
  const [providerFilter, setProviderFilter] = useState("all");
  const [eventFilter, setEventFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Record<string, unknown> | null>(null);
  const [draftStatus, setDraftStatus] = useState("operational");
  const [draftNote, setDraftNote] = useState("");
  const healthy = providers.filter((row) => String(row.status || "operational") === "operational").length;
  const failures = events.filter((row) => String(row.status || "").toLowerCase() === "failed").length;
  const successes = events.filter((row) => String(row.status || "").toLowerCase() === "success").length;
  const successRate = successes + failures ? Math.round(successes / (successes + failures) * 100) : 100;
  const visibleProviders = providerFilter === "all" ? providers : providers.filter((row) => String(row.status || "operational") === providerFilter);
  const visibleEvents = useMemo(() => events.filter((row) => {
    if (eventFilter !== "all" && String(row.status || "").toLowerCase() !== eventFilter) return false;
    const needle = search.trim().toLowerCase();
    return !needle || [row.provider, row.event, row.message, row.reference, row.uid, row.httpStatus].some((value) => String(value || "").toLowerCase().includes(needle));
  }).sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0)), [events, eventFilter, search]);
  const openEditor = (provider: Record<string, unknown>) => {
    setEditing(provider);
    setDraftStatus(String(provider.status || "operational"));
    setDraftNote(String(provider.note || ""));
  };
  const saveProvider = async () => {
    if (!editing) return;
    const saved = await runAction("adminUpdateProviderHealth", { provider: editing.provider, status: draftStatus, note: draftNote.trim() }, "Provider health updated");
    if (saved) setEditing(null);
  };
  const statusIcon = (status: string) => status === "operational" ? <CheckCircle2 /> : status === "down" ? <XCircle /> : status === "maintenance" ? <Settings2 /> : <CircleAlert />;
  return <div className="health-page">
    <section className="health-metrics">
      {[
        { label: "Healthy providers", value: `${healthy}/${providers.length}`, note: "Providers marked operational", Icon: HeartPulse, tone: healthy === providers.length ? "good" : "warn" },
        { label: "Webhook events", value: events.length, note: "Callbacks in the live event window", Icon: Zap, tone: "neutral" },
        { label: "Webhook failures", value: failures, note: failures ? "Events requiring investigation" : "No failures currently loaded", Icon: CircleAlert, tone: failures ? "bad" : "good" },
        { label: "Delivery success", value: `${successRate}%`, note: "Successful callbacks versus failures", Icon: CheckCircle2, tone: successRate >= 95 ? "good" : "warn" },
      ].map(({ label, value, note, Icon, tone }) => <article className={tone} key={label}><span><Icon /></span><div><small>{label}</small><strong>{value}</strong><p>{note}</p></div></article>)}
    </section>

    <section className="health-panel provider-health-panel">
      <header><div><i /><span><h2>Provider Health</h2><p>Monitor live callback performance and set operational overrides.</p></span></div><label><Filter /><select value={providerFilter} onChange={(event) => setProviderFilter(event.target.value)}><option value="all">All providers</option><option value="operational">Operational</option><option value="degraded">Degraded</option><option value="down">Down</option><option value="maintenance">Maintenance</option></select></label></header>
      <div className="provider-health-grid">{visibleProviders.map((provider, index) => {
        const providerStatus = String(provider.status || "operational").toLowerCase();
        const success = Number(provider.successCount || 0);
        const failed = Number(provider.failureCount || 0);
        const total = success + failed;
        const rate = total ? Math.round(success / total * 100) : 100;
        return <article className={providerStatus} key={String(provider.provider || index)}>
          <div className="provider-health-head"><span>{statusIcon(providerStatus)}</span><div><strong>{String(provider.label || provider.provider || "Provider")}</strong><small>{String(provider.provider || "").toUpperCase()}</small></div><button onClick={() => openEditor(provider)}><Pencil /> Edit</button></div>
          <span className={`provider-health-status ${providerStatus}`}><i /> {humanize(providerStatus)}</span>
          <p>{String(provider.note || "No manual operational note has been added.")}</p>
          <div className="provider-rate"><span><small>Callback success</small><strong>{rate}%</strong></span><div><i style={{ width: `${rate}%` }} /></div></div>
          <footer><span><small>Success</small><strong>{success}</strong></span><span><small>Failed</small><strong>{failed}</strong></span><span><small>Latest event</small><strong>{provider.latestAt ? formatDate(provider.latestAt) : "No event"}</strong></span></footer>
        </article>;
      })}{!visibleProviders.length && <div className="health-empty">No providers match this status.</div>}</div>
    </section>

    <section className="health-panel webhook-health-panel">
      <header><div><i /><span><h2>Webhook Event Monitor</h2><p>Inspect provider callbacks, failures and ignored notifications.</p></span></div><em><span /> Live stream</em></header>
      <div className="webhook-health-toolbar"><label><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search provider, reference, user or message" /></label><select value={eventFilter} onChange={(event) => setEventFilter(event.target.value)}><option value="all">All events</option><option value="success">Successful</option><option value="failed">Failed</option><option value="pending">Pending</option><option value="ignored">Ignored</option></select><span>{visibleEvents.length} events</span></div>
      <div className="webhook-event-list">{visibleEvents.slice(0, 100).map((row, index) => {
        const eventStatus = String(row.status || "pending").toLowerCase();
        return <article className={eventStatus} key={String(row.id || index)}><span className="webhook-event-status">{eventStatus === "success" ? <CheckCircle2 /> : eventStatus === "failed" ? <XCircle /> : <Clock3 />}</span><div><div><strong>{String(row.provider || "Unknown provider").toUpperCase()}</strong><span>{humanize(String(row.event || "callback"))}</span></div><p>{String(row.message || "Provider callback received")}</p><small>{row.reference ? `Reference ${String(row.reference)} · ` : ""}{row.uid ? `User ${String(row.uid)} · ` : ""}HTTP {String(row.httpStatus || "—")}</small></div><time>{formatDate(row.createdAt)}</time></article>;
      })}{!visibleEvents.length && <div className="health-empty"><Search /> No webhook events match your filters.</div>}</div>
    </section>

    {editing && <div className="confirm-modal-overlay" onMouseDown={() => setEditing(null)}><div className="health-editor" onMouseDown={(event) => event.stopPropagation()}><header><span><HeartPulse /></span><div><h2>Update {String(editing.label || editing.provider)} health</h2><p>Set a manual operational status and note for administrators.</p></div><button onClick={() => setEditing(null)}><X /></button></header><div><label>Status<select value={draftStatus} onChange={(event) => setDraftStatus(event.target.value)}><option value="operational">Operational</option><option value="degraded">Degraded</option><option value="down">Down</option><option value="maintenance">Maintenance</option></select></label><label>Operational note<textarea rows={4} value={draftNote} onChange={(event) => setDraftNote(event.target.value)} placeholder="Explain the incident, maintenance window or recovery status" /></label></div><footer><button onClick={() => setEditing(null)}>Cancel</button><button className="save" onClick={() => void saveProvider()}><Check /> Save health status</button></footer></div></div>}
  </div>;
}

function BroadcastPage({
  rows,
  runAction,
}: {
  rows: Record<string, unknown>[];
  runAction: RunAction;
}) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("all");
  const [target, setTarget] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [actionType, setActionType] = useState("none");
  const [actionValue, setActionValue] = useState("");
  const [actionButtonText, setActionButtonText] = useState("");
  const [inApp, setInApp] = useState(false);
  const [sendPush, setSendPush] = useState(true);
  const [uploading, setUploading] = useState(false);
  const composerRef = useRef<HTMLElement | null>(null);
  const actionOptions = [
    ["none", "No action"], ["fund", "Open Add Fund"], ["airtime", "Open Airtime"], ["data", "Open Data"], ["tv", "Open Cable TV"], ["electricity", "Open Electricity"], ["education", "Open Education"], ["payBills", "Open Pay Bills"], ["url", "Open URL"],
  ];
  const uploadImage = async (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return showFlushbar("Choose an image file", true);
    if (file.size > 8 * 1024 * 1024) return showFlushbar("Broadcast images must be smaller than 8 MB", true);
    setUploading(true);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const imageRef = ref(storage, `admin_broadcasts/${auth.currentUser?.uid ?? "admin"}_${Date.now()}_${safeName}`);
      await uploadBytes(imageRef, file, { contentType: file.type });
      setImageUrl(await getDownloadURL(imageRef));
      showFlushbar("Broadcast image uploaded");
    } catch (failure) {
      showFlushbar(failure instanceof Error ? failure.message : "Could not upload broadcast image", true);
    } finally {
      setUploading(false);
    }
  };
  const prepareRepush = (row: Record<string, unknown>) => {
    const previousAudience = String(row.audience ?? "all").toLowerCase();
    const safeAudience = ["all", "active", "single"].includes(previousAudience)
      ? previousAudience
      : "all";
    const previousAction = String(row.actionType ?? "none");

    setTitle(String(row.title ?? "").slice(0, 100));
    setBody(String(row.body ?? "").slice(0, 500));
    setAudience(safeAudience);
    setTarget(
      safeAudience === "single"
        ? String(row.targetQuery ?? row.targetUid ?? "")
        : "",
    );
    setImageUrl(String(row.imageUrl ?? ""));
    setActionType(
      actionOptions.some(([value]) => value === previousAction)
        ? previousAction
        : "none",
    );
    setActionValue(String(row.actionValue ?? ""));
    setActionButtonText(String(row.actionButtonText ?? ""));
    setInApp(row.inApp === true);
    setSendPush(row.sendPush !== false);

    composerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    window.setTimeout(() => {
      composerRef.current?.querySelector<HTMLInputElement>('input[placeholder="Notification title"]')?.focus();
    }, 450);
    showFlushbar("Notification details loaded. Review them, then send when ready.");
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!title.trim() || !body.trim()) return showFlushbar("Enter the broadcast title and message", true);
    if (audience === "single" && !target.trim())
      return showFlushbar("Enter the user email, phone, user ID, or UID", true);
    if (actionType === "url" && !/^https?:\/\//.test(actionValue))
      return showFlushbar("Enter a valid https:// URL", true);
    if (!inApp && !sendPush) return showFlushbar("Select push notification, in-app notification, or both", true);
    const sent = await runAction(
      "adminSendBroadcast",
      {
        title: title.trim(),
        body: body.trim(),
        audience,
        targetQuery: target,
        imageUrl,
        inApp,
        sendPush,
        actionType,
        actionValue,
        actionButtonText: actionButtonText.trim(),
      },
      "Broadcast sent",
    );
    if (!sent) return;

    setTitle("");
    setBody("");
    setAudience("all");
    setTarget("");
    setImageUrl("");
    setActionType("none");
    setActionValue("");
    setActionButtonText("");
    setInApp(false);
    setSendPush(true);
  };
  return (
    <div className="broadcast-page">
      <section className="broadcast-panel composer" ref={composerRef}>
        <header><div><i /><h2>Send Broadcast</h2></div><span><i /> Customer messaging</span></header>
        <form className="broadcast-form" onSubmit={submit}>
          <div className="broadcast-fields">
            <label><span>Title</span><input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Notification title" maxLength={100} required /><small>{title.length}/100</small></label>
            <label><span>Audience</span><select value={audience} onChange={(event) => setAudience(event.target.value)}><option value="all">All users</option><option value="active">Active users</option><option value="single">Single user</option></select></label>
            {audience === "single" && <label><span>Find user</span><input value={target} onChange={(event) => setTarget(event.target.value)} placeholder="Email, phone, user ID, or UID" required /></label>}
            <label><span>Tap action</span><select value={actionType} onChange={(event) => { setActionType(event.target.value); if (event.target.value !== "url") setActionValue(""); }}>{actionOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
            {actionType === "url" && <label><span>Destination URL</span><input value={actionValue} onChange={(event) => setActionValue(event.target.value)} placeholder="https://example.com" required /></label>}
            {actionType !== "none" && <label><span>Action button text</span><input value={actionButtonText} onChange={(event) => setActionButtonText(event.target.value)} placeholder="Example: View offer" maxLength={30} /><small>{actionButtonText.length}/30</small></label>}
          </div>
          <label className="broadcast-message"><span>Message</span><textarea value={body} onChange={(event) => setBody(event.target.value)} placeholder="Example: Scheduled maintenance starts by 11 PM." maxLength={500} required /><small>{body.length}/500</small></label>
          <div className="broadcast-image-picker">
            <div><Image /><span><strong>Broadcast image</strong><small>Optional JPG, PNG or WebP, up to 8 MB.</small></span></div>
            <label className="broadcast-file-button"><input type="file" accept="image/*" disabled={uploading} onChange={(event) => void uploadImage(event.target.files?.[0])} />{uploading ? <><Loader2 className="spin" /> Uploading…</> : <><ArrowDownToLine /> Pick image</>}</label>
            {imageUrl && <div className="broadcast-image-preview"><img src={imageUrl} alt="Broadcast preview" /><button type="button" onClick={() => setImageUrl("")}><X /> Remove image</button></div>}
          </div>
          <div className="broadcast-delivery"><label><input type="checkbox" checked={sendPush} onChange={(event) => setSendPush(event.target.checked)} /><span><Radio /><b>Push notification</b><small>Deliver through registered device tokens.</small></span></label><label><input type="checkbox" checked={inApp} onChange={(event) => setInApp(event.target.checked)} /><span><Smartphone /><b>In-app notification</b><small>Show a dismissible message while the customer is using the app.</small></span></label></div>
          <div className="broadcast-submit"><p>{audience === "single" ? "This message will be sent to one exact matched user." : audience === "active" ? "This message will be sent to active customer accounts." : "This message will be sent to all customer accounts."}</p><button type="submit" disabled={uploading}><Radio /> Send Broadcast</button></div>
        </form>
      </section>
      <section className="broadcast-panel history">
        <header><div><i /><h2>Broadcast History</h2></div><span><i /> {rows.length} sent</span></header>
        <div className="broadcast-history-list">{rows.length ? rows.map((row, index) => {
          const action = String(row.actionType ?? "none");
          const actionLabel = actionOptions.find(([value]) => value === action)?.[1] ?? humanize(action);
          return <article className="broadcast-history-card" key={String(row.id ?? index)}>
            {Boolean(row.imageUrl) && <img src={String(row.imageUrl)} alt="" />}
            <div className="broadcast-history-copy"><div><strong>{String(row.title ?? "Untitled broadcast")}</strong><span className={`status ${String(row.status ?? "sent")}`}>{humanize(String(row.status ?? "sent"))}</span><button type="button" className="broadcast-repush" onClick={() => prepareRepush(row)}><RotateCw /> Repush</button></div><p>{String(row.body ?? "")}</p><small>Sent by {String(row.sentByName ?? row.sentByEmail ?? "Admin")} · {formatDate(row.createdAt)}</small><small>Tap action: {actionLabel}{action === "url" && row.actionValue ? ` · ${String(row.actionValue)}` : ""}{action !== "none" ? ` · Button: ${String(row.actionButtonText || "Open")}` : ""}</small></div>
            <div className="broadcast-stats"><div><small>Audience</small><strong>{humanize(String(row.audience ?? "all"))}</strong></div><div><small>Users</small><strong>{String(row.deliveredCount ?? 0)}</strong></div><div><small>Tokens</small><strong>{String(row.tokenCount ?? 0)}</strong></div><div className="success"><small>Push OK</small><strong>{String(row.pushSuccessCount ?? 0)}</strong></div><div className="failed"><small>Push Failed</small><strong>{String(row.pushFailureCount ?? 0)}</strong></div></div>
          </article>;
        }) : <p className="broadcast-empty">No broadcasts sent yet.</p>}</div>
      </section>
    </div>
  );
}

function CashbackPage({
  settings,
  rows,
  runAction,
}: {
  settings: Record<string, unknown>;
  rows: Record<string, unknown>[];
  runAction: RunAction;
}) {
  const [percent, setPercent] = useState(
    String(settings.airtimePercent ?? "0.5"),
  );
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedPercent, setSavedPercent] = useState(Number(settings.airtimePercent ?? 0));
  const totalCashback = rows.reduce((sum, row) => sum + Number(row.cashbackAmount ?? 0), 0);
  const totalPurchases = rows.reduce((sum, row) => sum + Number(row.transactionAmount ?? 0), 0);
  const visibleRows = rows.filter((row) => `${row.network ?? ""} ${row.phone ?? ""} ${row.uid ?? ""} ${row.txRef ?? ""} ${row.status ?? ""}`.toLowerCase().includes(search.trim().toLowerCase()));
  const save = async (event: FormEvent) => {
    event.preventDefault();
    const value = Number(percent);
    if (value < 0 || value > 100)
      return window.alert("Enter a value between 0 and 100");
    setSaving(true);
    const saved = await runAction(
      "adminUpdateCashbackSettings",
      { airtimePercent: value, enabled: value > 0 },
      "Airtime cashback updated",
      false,
    );
    if (saved) setSavedPercent(value);
    setSaving(false);
  };
  return (
    <div className="cashback-page">
      <div className="cashback-metrics">
        <article><span><CircleDollarSign /></span><div><small>Total cashback credited</small><strong>{naira(totalCashback)}</strong><p>Across {rows.length.toLocaleString("en-NG")} rewards</p></div></article>
        <article><span><Percent /></span><div><small>Current airtime rate</small><strong>{savedPercent.toLocaleString("en-NG", { maximumFractionDigits: 2 })}%</strong><p>{savedPercent > 0 ? "Cashback is active" : "Cashback is disabled"}</p></div></article>
        <article><span><ReceiptText /></span><div><small>Qualifying purchases</small><strong>{naira(totalPurchases)}</strong><p>Purchase value represented here</p></div></article>
      </div>

      <section className="cashback-panel settings">
        <header><div><i /><span><h2>Airtime Cashback Settings</h2><p>Reward customers automatically after successful airtime purchases.</p></span></div><em className={Number(percent) > 0 ? "active" : "disabled"}><CheckCircle2 /> {Number(percent) > 0 ? "Active" : "Disabled"}</em></header>
        <form className="cashback-settings-form" onSubmit={save}>
          <label><span>Cashback percentage</span><div><Percent /><input type="number" step="0.01" min="0" max="100" value={percent} onChange={(event) => setPercent(event.target.value)} /><b>%</b></div><small>Set this to 0% to disable airtime cashback.</small></label>
          <div className="cashback-preview"><small>Reward preview</small><strong>{naira(Math.round(100000 * (Number(percent) || 0) / 100))}</strong><span>Cashback on a ₦1,000 airtime purchase</span></div>
          <button type="submit" disabled={saving}>{saving ? <Loader2 className="spin" /> : <Check />} {saving ? "Saving…" : "Save Cashback Rate"}</button>
        </form>
      </section>

      <section className="cashback-panel ledger">
        <header><div><i /><span><h2>Recent Cashback Credits</h2><p>Successful rewards credited directly to customer wallets.</p></span></div><em>{visibleRows.length} records</em></header>
        <div className="cashback-toolbar"><div><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search network, phone, user or reference" /></div></div>
        <div className="cashback-list">{visibleRows.length ? visibleRows.map((row, index) => {
          const network = String(row.network ?? "Airtime");
          const status = String(row.status ?? "credited").toLowerCase();
          return <article className="cashback-credit" key={String(row.id ?? index)}>
            <span className="cashback-network">{network.slice(0, 1).toUpperCase()}</span>
            <div className="cashback-credit-main"><div><strong>{humanize(network)} Airtime Cashback</strong><span className={`cashback-status ${status}`}><CheckCircle2 /> {humanize(status)}</span></div><p>{String(row.phone ?? "No phone number")}</p><small>{formatDate(row.createdAt)} · Ref: {String(row.txRef ?? row.id ?? "—")}</small>{Boolean(row.uid) && <small>User UID: {String(row.uid)}</small>}</div>
            <div className="cashback-credit-values"><div><small>Purchase</small><strong>{naira(Number(row.transactionAmount ?? 0))}</strong></div><div className="reward"><small>Cashback</small><strong>+{naira(Number(row.cashbackAmount ?? 0))}</strong></div><div><small>Rate</small><strong>{Number(row.cashbackPercent ?? 0)}%</strong></div><div><small>Balance after</small><strong>{naira(Number(row.balanceAfter ?? 0))}</strong></div></div>
          </article>;
        }) : <p className="cashback-empty">No cashback credits match your search.</p>}</div>
      </section>
    </div>
  );
}

function ReferralsPage({
  settings,
  entries,
  withdrawals,
  runAction,
}: {
  settings: Record<string, unknown>;
  entries: Record<string, unknown>[];
  withdrawals: Record<string, unknown>[];
  runAction: RunAction;
}) {
  const [referrer, setReferrer] = useState(
    String(Number(settings.referrerBonusKobo ?? 10000) / 100),
  );
  const [referee, setReferee] = useState(
    String(Number(settings.refereeBonusKobo ?? 5000) / 100),
  );
  const [minimum, setMinimum] = useState(
    String(Number(settings.minimumTransactionKobo ?? 100000) / 100),
  );
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const totalRewards = entries.reduce((sum, row) => sum + Number(row.referrerBonusKobo ?? 0) + Number(row.refereeBonusKobo ?? 0), 0);
  const paidRewards = entries.filter((row) => String(row.status ?? "credited").toLowerCase() === "credited").length;
  const pendingWithdrawals = withdrawals.filter((row) => String(row.status ?? "pending").toLowerCase() === "pending");
  const visibleEntries = entries.filter((row) => `${row.referrerName ?? ""} ${row.referrerEmail ?? ""} ${row.referrerUid ?? ""} ${row.refereeName ?? ""} ${row.refereeEmail ?? ""} ${row.refereeUid ?? ""} ${row.referredByCode ?? ""} ${row.transactionTitle ?? ""} ${row.transactionNetwork ?? ""} ${row.transactionRecipient ?? ""} ${row.txRef ?? ""}`.toLowerCase().includes(search.trim().toLowerCase()));
  const save = async (event: FormEvent) => {
    event.preventDefault();
    const referrerValue = Number(referrer);
    const refereeValue = Number(referee);
    const minimumValue = Number(minimum);
    if (![referrerValue, refereeValue, minimumValue].every((value) => Number.isFinite(value) && value >= 0)) {
      return showFlushbar("Enter valid referral amounts", true);
    }
    setSaving(true);
    await runAction(
      "adminUpdateReferralSettings",
      {
        referrerBonusKobo: Math.round(referrerValue * 100),
        refereeBonusKobo: Math.round(refereeValue * 100),
        minimumTransactionKobo: Math.round(minimumValue * 100),
        enabled: referrerValue > 0 || refereeValue > 0,
      },
      "Referral settings updated",
      false,
    );
    setSaving(false);
  };
  const resolve = (
    row: Record<string, unknown>,
    status: "approved" | "rejected",
  ) => {
    const decision = status === "approved" ? "paid" : "rejected";
    const note = window.prompt("Admin note") ?? "";
    void runAction(
      "adminResolveReferralWithdrawal",
      { id: row.id, decision, note },
      decision === "paid" ? "Withdrawal marked paid" : "Withdrawal rejected",
    );
  };
  return (
    <div className="referrals-page">
      <div className="referral-metrics">
        <article><span><CircleDollarSign /></span><div><small>Total rewards credited</small><strong>{naira(totalRewards)}</strong><p>Combined referrer and new-user bonuses</p></div></article>
        <article><span><Gift /></span><div><small>Successful referrals</small><strong>{paidRewards.toLocaleString("en-NG")}</strong><p>Qualified reward events</p></div></article>
        <article><span><WalletCards /></span><div><small>Pending withdrawals</small><strong>{pendingWithdrawals.length.toLocaleString("en-NG")}</strong><p>{pendingWithdrawals.length ? "Awaiting administrator review" : "Nothing requires attention"}</p></div></article>
      </div>

      <section className="referral-panel settings">
        <header><div><i /><span><h2>Referral Reward Settings</h2><p>Control qualification and the reward received by both customers.</p></span></div><em className={Number(referrer) > 0 || Number(referee) > 0 ? "active" : "disabled"}><CheckCircle2 /> {Number(referrer) > 0 || Number(referee) > 0 ? "Active" : "Disabled"}</em></header>
        <form className="referral-settings-form" onSubmit={save}>
          {[["Referrer earns", referrer, setReferrer], ["New user earns", referee, setReferee], ["Minimum qualifying purchase", minimum, setMinimum]].map(([label, value, setter]) => <label key={String(label)}><span>{String(label)}</span><div><b>₦</b><input type="number" min="0" step="1" value={String(value)} onChange={(event) => (setter as (value: string) => void)(event.target.value)} /></div></label>)}
          <div className="referral-preview"><small>Reward preview</small><strong>{naira((Number(referrer) + Number(referee)) * 100)}</strong><span>Combined reward after a {naira(Number(minimum) * 100)} qualifying purchase</span></div>
          <button type="submit" disabled={saving}>{saving ? <Loader2 className="spin" /> : <Check />} {saving ? "Saving…" : "Save Referral Settings"}</button>
        </form>
      </section>

      <section className="referral-panel withdrawals">
        <header><div><i /><span><h2>Withdrawal Requests</h2><p>Review referral earnings requested for bank payout.</p></span></div><em>{pendingWithdrawals.length} pending</em></header>
        <div className="referral-withdrawal-list">{withdrawals.length ? withdrawals.map((row, index) => {
          const status = String(row.status ?? "pending").toLowerCase();
          return <article key={String(row.id ?? index)}>
            <div className="referral-person"><span><UserPlus /></span><div><strong>{String(row.userName ?? row.accountName ?? "Referral customer")}</strong><small>{String(row.userEmail ?? row.userPhone ?? `UID: ${row.uid ?? "—"}`)}</small><small>Requested {formatDate(row.createdAt)}</small></div></div>
            <div className="referral-bank"><small>Verified destination</small><strong>{String(row.bankName ?? "Bank not provided")}</strong><span>{String(row.accountName ?? "—")}</span><span>{String(row.accountNumber ?? "—")}</span></div>
            <div className="referral-bank"><small>Audit reference</small><strong>{String(row.transactionId ?? row.id ?? "—")}</strong><span>Before: {naira(Number(row.balanceBefore ?? 0))}</span><span>After: {naira(Number(row.balanceAfter ?? 0))}</span></div>
            <div className="referral-amount"><small>Amount</small><strong>{naira(Number(row.amountKobo ?? 0))}</strong><span className={`referral-status ${status}`}>{humanize(status)}</span>{row.resolvedByName ? <small>By {String(row.resolvedByName)}</small> : null}</div>
            {status === "pending" && <div className="referral-actions"><button onClick={() => resolve(row, "approved")}><CheckCircle2 /> Approve & Pay</button><button className="reject" onClick={() => resolve(row, "rejected")}><XCircle /> Reject & Refund</button></div>}
          </article>;
        }) : <p className="referral-empty">No referral withdrawals have been requested.</p>}</div>
      </section>

      <section className="referral-panel ledger">
        <header><div><i /><span><h2>Recent Referral Rewards</h2><p>Qualification events and bonuses issued to both customers.</p></span></div><em>{visibleEntries.length} records</em></header>
        <div className="referral-toolbar"><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search customer, transaction, phone or referral code" /></div>
        <div className="referral-ledger-list">{visibleEntries.length ? visibleEntries.map((row, index) => {
          const transactionTitle = String(row.transactionTitle ?? "Qualifying transaction");
          const referrerName = String(row.referrerName ?? row.referrerEmail ?? row.referrerUid ?? "Unknown customer");
          const refereeName = String(row.refereeName ?? row.refereeEmail ?? row.refereeUid ?? "Unknown customer");
          const recipient = String(row.transactionRecipient ?? "").trim();
          const planName = String(row.transactionPlanName ?? "").trim();
          return <article key={String(row.id ?? index)}><span className="referral-ledger-icon"><UserPlus /></span><div className="referral-ledger-main"><div><strong>{transactionTitle} Referral Reward</strong><span className="referral-status credited"><CheckCircle2 /> Credited</span></div><div className="referral-transaction"><b>{transactionTitle}</b><span>{naira(Number(row.transactionAmount ?? 0))}{recipient ? ` · ${recipient}` : ""}{planName ? ` · ${planName}` : ""}</span></div><div className="referral-people"><span><small>Referrer</small><b>{referrerName}</b>{row.referrerEmail && row.referrerName ? <em>{String(row.referrerEmail)}</em> : null}</span><span><small>New customer</small><b>{refereeName}</b>{row.refereeEmail && row.refereeName ? <em>{String(row.refereeEmail)}</em> : null}</span></div><small>{formatDate(row.createdAt)} · Referral code: {String(row.referredByCode ?? "—")}</small>{Boolean(row.transactionId) && <small>Transaction ID: {String(row.transactionId)}</small>}</div><div className="referral-ledger-values"><div><small>Purchase</small><strong>{naira(Number(row.transactionAmount ?? 0))}</strong></div><div><small>Referrer</small><strong>+{naira(Number(row.referrerBonusKobo ?? 0))}</strong></div><div><small>New user</small><strong>+{naira(Number(row.refereeBonusKobo ?? 0))}</strong></div><div><small>Total reward</small><strong>{naira(Number(row.referrerBonusKobo ?? 0) + Number(row.refereeBonusKobo ?? 0))}</strong></div></div></article>;
        }) : <p className="referral-empty">No referral rewards match your search.</p>}</div>
      </section>
    </div>
  );
}

function PromosPage({
  rows,
  redemptions,
  totalSpendKobo,
  runAction,
}: {
  rows: Record<string, unknown>[];
  redemptions: Record<string, unknown>[];
  totalSpendKobo: number;
  runAction: RunAction;
}) {
  const [code, setCode] = useState("");
  const [reward, setReward] = useState("100");
  const [maxUses, setMaxUses] = useState("0");
  const [expiry, setExpiry] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [togglingCode, setTogglingCode] = useState("");
  const activeCodes = rows.filter((row) => row.active !== false).length;
  const computedSpend = redemptions.reduce((sum, row) => sum + Number(row.rewardKobo || 0), 0);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    const saved = await runAction(
      "adminCreateOrUpdatePromoCode",
      {
        code,
        rewardKobo: Math.round(Number(reward) * 100),
        maxRedemptions: Number(maxUses),
        expiresAt: expiry ? new Date(expiry).getTime() : 0,
        description,
        active: true,
      },
      "Promo code saved",
    );
    if (saved) {
      setCode("");
      setReward("100");
      setMaxUses("0");
      setExpiry("");
      setDescription("");
    }
    setSaving(false);
  };
  const toggle = async (row: Record<string, unknown>) => {
    const promoCode = String(row.code ?? row.id ?? "");
    setTogglingCode(promoCode);
    await runAction(
      "adminSetPromoCodeActive",
      { code: promoCode, active: row.active === false },
      row.active === false ? "Promo enabled" : "Promo paused",
    );
    setTogglingCode("");
  };

  return (
    <div className="promo-page">
      <section className="promo-metrics">
        <article><span><Gift /></span><div><small>Total Promo Spend</small><strong>{naira(totalSpendKobo || computedSpend)}</strong><p>Rewards credited to customers</p></div></article>
        <article><span><Percent /></span><div><small>Active Codes</small><strong>{activeCodes}</strong><p>Available for redemption</p></div></article>
        <article><span><Gift /></span><div><small>Redemptions</small><strong>{redemptions.length}</strong><p>Successful promo claims</p></div></article>
      </section>

      <section className="promo-panel promo-create-panel">
        <header><div><i /><span><h2>Create Promo Code</h2><p>Users can redeem each code once. Set maximum uses to 0 for unlimited redemptions.</p></span></div><em><RefreshCw /> Live data</em></header>
        <form className="promo-form" onSubmit={save}>
          <section className="promo-form-group promo-form-details">
            <div className="promo-form-group-title"><span><Percent /></span><div><strong>Promo details</strong><small>Define the code and customer reward.</small></div></div>
            <div className="promo-form-pair">
              <label><span>Promo code</span><div><Percent /><input value={code} onChange={(event) => setCode(event.target.value.toUpperCase().replace(/\s+/g, ""))} placeholder="e.g. WELCOME100" maxLength={40} required /></div></label>
              <label><span>Reward amount</span><div><b>₦</b><input type="number" min="1" step="0.01" value={reward} onChange={(event) => setReward(event.target.value)} required /></div></label>
            </div>
            <label><span>Description</span><div><Pencil /><input value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Internal note or campaign description" maxLength={240} /></div><small>Optional internal note to help identify this campaign.</small></label>
          </section>
          <section className="promo-form-group promo-form-rules">
            <div className="promo-form-group-title"><span><Settings2 /></span><div><strong>Redemption rules</strong><small>Control availability and campaign limits.</small></div></div>
            <div className="promo-form-pair">
              <label><span>Maximum uses</span><div><Users /><input type="number" min="0" step="1" value={maxUses} onChange={(event) => setMaxUses(event.target.value)} /></div><small>Enter 0 for unlimited redemptions.</small></label>
              <label><span>Expiry date</span><div><Clock3 /><input type="date" value={expiry} onChange={(event) => setExpiry(event.target.value)} min={new Date().toISOString().slice(0, 10)} /></div><small>Leave empty when the code should not expire.</small></label>
            </div>
            <div className="promo-form-submit"><p><CheckCircle2 /> The code becomes active immediately after saving.</p><button className="promo-save" disabled={saving}>{saving ? <Loader2 className="animate-spin" /> : <Gift />} {saving ? "Saving…" : "Save Promo"}</button></div>
          </section>
        </form>
      </section>

      <section className="promo-panel">
        <header><div><i /><span><h2>Promo Codes</h2><p>{rows.length} code{rows.length === 1 ? "" : "s"} configured</p></span></div><em><RefreshCw /> Live data</em></header>
        <div className="promo-code-list">
          {rows.length ? rows.map((row, index) => {
            const active = row.active !== false;
            const promoCode = String(row.code ?? row.id ?? "");
            const max = Number(row.maxRedemptions || 0);
            const used = Number(row.redeemedCount || 0);
            const expiresAt = Number(row.expiresAt || 0);
            return <article className="promo-code-card" key={String(row.id ?? index)}>
              <div className="promo-code-identity"><span><Percent /></span><div><strong>{promoCode}</strong><p>{String(row.description || "").trim() || `Updated ${formatDate(row.updatedAt)}`}</p></div></div>
              <div className="promo-code-stat"><small>Reward</small><strong>{naira(Number(row.rewardKobo || 0))}</strong></div>
              <div className="promo-code-stat"><small>Used</small><strong>{used.toLocaleString()} / {max ? max.toLocaleString() : "unlimited"}</strong></div>
              <div className="promo-code-stat"><small>Expires</small><strong>{expiresAt ? formatDate(expiresAt) : "No expiry"}</strong></div>
              <span className={`promo-state ${active ? "active" : "paused"}`}>{active ? <CheckCircle2 /> : <PauseCircle />}{active ? "Active" : "Paused"}</span>
              <button className="promo-toggle" disabled={togglingCode === promoCode} onClick={() => void toggle(row)}>{togglingCode === promoCode ? <Loader2 className="animate-spin" /> : active ? <PauseCircle /> : <PlayCircle />}{active ? "Pause" : "Enable"}</button>
            </article>;
          }) : <p className="promo-empty">No promo codes yet. Create the first one above.</p>}
        </div>
      </section>

      <section className="promo-panel">
        <header><div><i /><span><h2>Recent Redemptions</h2><p>Latest rewards credited through promo codes</p></span></div><em>{redemptions.length} records</em></header>
        <div className="promo-redemption-list">
          {redemptions.length ? redemptions.map((row, index) => <article key={String(row.id ?? index)}>
            <span className="promo-redemption-icon"><Gift /></span>
            <div className="promo-redemption-main"><strong>Promo Redemption</strong><small>{formatDate(row.createdAt)}</small><p>User: {String(row.uid || "—")}</p></div>
            <div><small>Code</small><strong>{String(row.code || "—")}</strong></div>
            <div><small>Reward</small><strong className="promo-credit">+{naira(Number(row.rewardKobo || 0))}</strong></div>
            <div><small>Balance After</small><strong>{naira(Number(row.balanceAfter || 0))}</strong></div>
            <span className="promo-state active"><CheckCircle2 />{String(row.status || "Credited")}</span>
          </article>) : <p className="promo-empty">No promo redemptions yet.</p>}
        </div>
      </section>
    </div>
  );
}

function WatchlistPage({ users, runAction }: { users: AdminUser[]; runAction: RunAction }) {
  const [search, setSearch] = useState("");
  const [removing, setRemoving] = useState<string | null>(null);
  const [removed, setRemoved] = useState<string[]>([]);
  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return users
      .filter((user) => user.watchlisted === true && !removed.includes(user.uid))
      .filter((user) => !needle || [user.name, user.email, user.phone, user.watchlistReason, user.watchlistSource]
        .some((value) => String(value || "").toLowerCase().includes(needle)))
      .sort((a, b) => Number(b.watchlistedAt || 0) - Number(a.watchlistedAt || 0));
  }, [users, removed, search]);

  const removeUser = async (user: AdminUser) => {
    if (!await confirmAction({
      title: "Remove user from watchlist?",
      message: `${user.name || user.email || "This user"} will no longer be flagged for watchlist review.`,
      confirmLabel: "Remove from watchlist",
      tone: "warning",
    })) return;
    setRemoving(user.uid);
    const ok = await runAction("adminSetUserWatchlisted", {
      uid: user.uid,
      watchlisted: false,
      reason: "Removed from watchlist",
    }, "User removed from watchlist");
    if (ok) setRemoved((current) => [...current, user.uid]);
    setRemoving(null);
  };

  const total = users.filter((user) => user.watchlisted === true && !removed.includes(user.uid)).length;
  const adminFlags = users.filter((user) => user.watchlisted === true && String(user.watchlistSource || "").toLowerCase() === "admin" && !removed.includes(user.uid)).length;

  return <div className="watchlist-page">
    <section className="watchlist-hero">
      <div><span><Ban /></span><div><small>Risk monitoring</small><h2>Customer Watchlist</h2><p>Review flagged accounts, understand why they were added and safely clear resolved cases.</p></div></div>
      <aside><strong>{total}</strong><small>currently watched</small></aside>
    </section>
    <section className="watchlist-metrics">
      <article><span><Users /></span><div><small>Watchlisted users</small><strong>{total}</strong></div></article>
      <article><span><Shield /></span><div><small>Admin flags</small><strong>{adminFlags}</strong></div></article>
      <article><span><Activity /></span><div><small>Automated / other</small><strong>{Math.max(0, total - adminFlags)}</strong></div></article>
    </section>
    <section className="watchlist-panel">
      <header><div><i /><span><h2>Watchlist</h2><p>Only admins with Watchlist permission can open this area.</p></span></div><label><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search name, email, phone or reason" /></label></header>
      <div className="watchlist-table-wrap">
        <table className="watchlist-table">
          <colgroup><col className="watchlist-user-column" /><col className="watchlist-reason-column" /><col className="watchlist-source-column" /><col className="watchlist-added-column" /><col className="watchlist-action-column" /></colgroup>
          <thead><tr><th>User</th><th>Reason</th><th>Source</th><th>Added</th><th>Action</th></tr></thead>
          <tbody>{rows.map((user) => <tr key={user.uid}>
            <td><div className="watchlist-user"><span><LoadedAvatar src={userAvatarSrc(user.photoUrl, user.avatarId, user.name || user.email)} fallback={initials(user.name || user.email || "User")} /></span><div><strong>{user.name || "Unnamed user"}</strong><small>{user.email || user.phone || user.uid}</small>{user.phone && user.email && <em>{user.phone}</em>}</div></div></td>
            <td><p className="watchlist-reason" title={user.watchlistReason || "No reason recorded"}>{user.watchlistReason || "No reason recorded"}</p></td>
            <td><span className="watchlist-source">{String(user.watchlistSource || "system").replace(/_/g, " ")}</span>{user.watchlistedByName && <small className="watchlist-by">by {user.watchlistedByName}</small>}</td>
            <td><time>{user.watchlistedAt ? formatDate(user.watchlistedAt) : "Not recorded"}</time></td>
            <td><button className="watchlist-remove" disabled={removing === user.uid} onClick={() => void removeUser(user)}>{removing === user.uid ? <Loader2 className="spin" /> : <XCircle />} Remove</button></td>
          </tr>)}</tbody>
        </table>
        {!rows.length && <div className="watchlist-empty"><ShieldCheck /><strong>{search ? "No matching watchlisted users" : "The watchlist is clear"}</strong><p>{search ? "Try a different search term." : "Flagged users will appear here with their recorded reason."}</p></div>}
      </div>
    </section>
  </div>;
}

function AdminsPage({
  admins,
  invites,
  runAction,
}: {
  admins: Record<string, unknown>[];
  invites: Record<string, unknown>[];
  runAction: RunAction;
}) {
  const availableSections = nav.filter((item) => !item.adminOnly);
  const allSections = availableSections.map((item) => item.key);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("admin");
  const [sections, setSections] = useState<string[]>(allSections);
  const [canManageTransactions, setCanManageTransactions] = useState(false);
  const [canAdjustWallet, setCanAdjustWallet] = useState(false);
  const [canViewTransactionProviders, setCanViewTransactionProviders] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [removedEmails, setRemovedEmails] = useState<string[]>([]);
  const [editing, setEditing] = useState<Record<string, unknown> | null>(null);
  const [editRole, setEditRole] = useState("admin");
  const [editSections, setEditSections] = useState<string[]>([]);
  const [editManageTransactions, setEditManageTransactions] = useState(false);
  const [editAdjustWallet, setEditAdjustWallet] = useState(false);
  const [editViewTransactionProviders, setEditViewTransactionProviders] = useState(false);

  const toggleSection = (
    key: string,
    current: string[],
    update: (value: string[]) => void,
  ) => update(current.includes(key) ? current.filter((item) => item !== key) : [...current, key]);

  const invite = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim() || !email.trim() || !sections.length) {
      showFlushbar("Enter a name, valid email and select at least one section", true);
      return;
    }
    setSaving(true);
    const ok = await runAction("adminInviteAdmin", {
      email: email.trim(),
      name: name.trim(),
      role,
      sections,
      canManageTransactions,
      canAdjustWallet,
      canViewTransactionProviders,
    }, "Admin created and login sent");
    setSaving(false);
    if (ok) {
      setName("");
      setEmail("");
      setRole("admin");
      setSections(allSections);
      setCanManageTransactions(false);
      setCanAdjustWallet(false);
      setCanViewTransactionProviders(false);
    }
  };

  const openEditor = (row: Record<string, unknown>) => {
    setEditing(row);
    setEditRole(String(row.role || "admin"));
    setEditSections(Array.isArray(row.sections) ? row.sections.map(String) : []);
    setEditManageTransactions(row.canManageTransactions === true);
    setEditAdjustWallet(row.canAdjustWallet === true);
    setEditViewTransactionProviders(row.canViewTransactionProviders === true);
  };

  const saveEditor = async () => {
    if (!editing || !editSections.length) {
      showFlushbar("Select at least one section", true);
      return;
    }
    setSaving(true);
    const ok = await runAction("adminUpdateAdminAccess", {
      email: editing.email,
      uid: editing.uid,
      role: editRole,
      sections: editSections,
      canManageTransactions: editManageTransactions,
      canAdjustWallet: editAdjustWallet,
      canViewTransactionProviders: editViewTransactionProviders,
      active: editing.active !== false,
    }, "Admin permissions updated");
    setSaving(false);
    if (ok) setEditing(null);
  };

  const toggleActive = async (row: Record<string, unknown>) => {
    const active = row.active !== false;
    if (!await confirmAction({ title: `${active ? "Disable" : "Enable"} admin access?`, message: active ? `${row.email} will be signed out and unable to use the console until enabled again.` : `${row.email} will regain access to their assigned console sections.`, confirmLabel: active ? "Disable access" : "Enable access", tone: active ? "warning" : "primary" })) return;
    await runAction("adminUpdateAdminAccess", {
      email: row.email,
      uid: row.uid,
      role: row.role || "admin",
      sections: Array.isArray(row.sections) && row.sections.length ? row.sections : ["overview"],
      canManageTransactions: row.canManageTransactions === true,
      canAdjustWallet: row.canAdjustWallet === true,
      canViewTransactionProviders: row.canViewTransactionProviders === true,
      active: !active,
    }, active ? "Admin access disabled" : "Admin access enabled");
  };
  const remove = async (row: Record<string, unknown>) => {
    const rowEmail = String(row.email || "").toLowerCase();
    if (!await confirmAction({ title: "Remove admin permanently?", message: `${row.email} will lose all console access and permissions. This account will disappear from the active admin directory.`, confirmLabel: "Remove admin", tone: "danger" })) return;
    setRemovedEmails((current) => current.includes(rowEmail) ? current : [...current, rowEmail]);
    const ok = await runAction("adminRemoveAdminAccess", { email: row.email }, "Admin access removed");
    if (!ok) setRemovedEmails((current) => current.filter((item) => item !== rowEmail));
  };

  const visibleAdmins = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return admins.filter((row) => {
      const rowEmail = String(row.email || "").toLowerCase();
      const removed = String(row.status || "").toLowerCase() === "removed" || removedEmails.includes(rowEmail);
      if (removed) return false;
      const active = row.active !== false && String(row.status || "active").toLowerCase() !== "removed";
      if (statusFilter === "active" && !active) return false;
      if (statusFilter === "inactive" && active) return false;
      if (!needle) return true;
      return [row.name, row.email, row.role, ...(Array.isArray(row.sections) ? row.sections : [])]
        .some((value) => String(value || "").toLowerCase().includes(needle));
    });
  }, [admins, removedEmails, search, statusFilter]);
  const directoryAdmins = admins.filter((row) => String(row.status || "").toLowerCase() !== "removed" && !removedEmails.includes(String(row.email || "").toLowerCase()));
  const activeCount = directoryAdmins.filter((row) => row.active !== false).length;
  const actionCount = directoryAdmins.filter((row) => row.active !== false && row.canManageTransactions === true).length;
  const recentInvites = invites.slice(0, 4);

  return (
    <div className="admins-page">
      <section className="admin-metrics">
        <article><span><Users /></span><div><small>Admin accounts</small><strong>{directoryAdmins.length}</strong><p>Configured console operators</p></div></article>
        <article><span><ShieldCheck /></span><div><small>Active access</small><strong>{activeCount}</strong><p>Admins who can sign in now</p></div></article>
        <article><span><Zap /></span><div><small>Transaction access</small><strong>{actionCount}</strong><p>Can approve, requery and refund</p></div></article>
        <article><span><Mail /></span><div><small>Invitations issued</small><strong>{invites.length}</strong><p>Login credentials sent by email</p></div></article>
      </section>

      <section className="admin-access-panel">
        <header><div><i /><span><h2>Invite Admin</h2><p>Create secure console access with precise permissions.</p></span></div><em><Shield /> Superadmin only</em></header>
        <form className="admin-invite-form" onSubmit={invite}>
          <div className="admin-invite-fields">
            <label><span>Admin name</span><div><UserCog /><input value={name} onChange={(event) => setName(event.target.value)} placeholder="e.g. Chizuru John" /></div></label>
            <label><span>Email address</span><div><Mail /><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="admin@example.com" /></div></label>
            <label><span>Role</span><div><Shield /><select value={role} onChange={(event) => setRole(event.target.value)}><option value="admin">Admin</option><option value="analyst">Analyst</option><option value="support">Support</option><option value="viewer">Viewer</option></select></div></label>
          </div>
          <div className="admin-permission-heading"><div><strong>Allowed sections</strong><small>Select the areas this admin can open.</small></div><button type="button" onClick={() => setSections(sections.length === allSections.length ? [] : allSections)}>{sections.length === allSections.length ? "Clear all" : "Select all"}</button></div>
          <div className="admin-permission-grid">
            {availableSections.map((item) => <button type="button" className={sections.includes(item.key) ? "selected" : ""} key={item.key} onClick={() => toggleSection(item.key, sections, setSections)}><item.icon /> <span>{item.label}</span>{sections.includes(item.key) && <Check />}</button>)}
          </div>
          <footer>
            <div className="admin-sensitive-permissions">
              <label className="admin-action-toggle"><input type="checkbox" checked={canManageTransactions} onChange={(event) => setCanManageTransactions(event.target.checked)} /><span><Zap /></span><div><strong>Allow transaction actions</strong><small>Approve pending requests, requery providers, mark failed and trigger refunds.</small></div></label>
              <label className="admin-action-toggle"><input type="checkbox" checked={canAdjustWallet} onChange={(event) => setCanAdjustWallet(event.target.checked)} /><span><WalletCards /></span><div><strong>Allow wallet adjustments</strong><small>Show Add / Deduct Wallet and permit customer balance changes.</small></div></label>
              <label className="admin-action-toggle"><input type="checkbox" checked={canViewTransactionProviders} onChange={(event) => setCanViewTransactionProviders(event.target.checked)} /><span><Eye /></span><div><strong>Show transaction providers</strong><small>Reveal internal provider routing such as Lizzysub, Paypoint and VTPass.</small></div></label>
            </div>
            <div><p>A temporary password is generated and securely emailed to the new admin.</p><button disabled={saving} type="submit">{saving ? <Loader2 className="spin" /> : <Send />} Create &amp; Send Login</button></div>
          </footer>
        </form>
      </section>

      <section className="admin-access-panel admin-directory">
        <header><div><i /><span><h2>Admin Directory</h2><p>Review access, roles and account status.</p></span></div><em><span /> Live data</em></header>
        <div className="admin-directory-tools">
          <label><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search name, email, role or section" /></label>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="all">All accounts</option><option value="active">Active only</option><option value="inactive">Disabled or removed</option></select>
          <span>{visibleAdmins.length} result{visibleAdmins.length === 1 ? "" : "s"}</span>
        </div>
        <div className="admin-card-list">
          {visibleAdmins.map((row, index) => {
            const rowSections = Array.isArray(row.sections) ? row.sections.map(String) : [];
            const active = row.active !== false && String(row.status || "active").toLowerCase() !== "removed";
            const protectedAdmin = String(row.role || "").toLowerCase() === "superadmin";
            return <article key={String(row.id ?? index)} className={!active ? "inactive" : ""}>
              <span className="admin-card-avatar">{initials(String(row.name || row.email || "Admin"))}</span>
              <div className="admin-card-identity"><strong>{displayValue(row.name, "name")}</strong><small>{displayValue(row.email, "email")}</small><p>{rowSections.slice(0, 5).map((key) => availableSections.find((item) => item.key === key)?.label || key).join(" · ") || "No sections assigned"}{rowSections.length > 5 ? ` · +${rowSections.length - 5}` : ""}</p></div>
              <div className="admin-card-badges"><span className={active ? "active" : "disabled"}>{active ? <CheckCircle2 /> : <Ban />}{active ? "Active" : "Inactive"}</span><span><Shield />{String(row.role || "admin")}</span><span className={row.canManageTransactions === true ? "actions" : "view"}>{row.canManageTransactions === true ? <Zap /> : <Eye />}{row.canManageTransactions === true ? "Actions allowed" : "View only"}</span>{row.canAdjustWallet === true && <span className="actions"><WalletCards /> Wallet access</span>}{row.canViewTransactionProviders === true && <span className="actions"><Eye /> Provider access</span>}</div>
              <div className="admin-card-actions">{protectedAdmin ? <span className="protected"><ShieldCheck /> Protected</span> : <><button onClick={() => openEditor(row)}><Pencil /> Edit</button><button onClick={() => void toggleActive(row)}>{active ? <Ban /> : <PlayCircle />}{active ? "Disable" : "Enable"}</button><button className="remove" onClick={() => void remove(row)}><XCircle /> Remove</button></>}</div>
            </article>;
          })}
          {!visibleAdmins.length && <div className="admin-empty"><UserCog /><strong>No admin accounts found</strong><p>Try another search or create a new admin above.</p></div>}
        </div>
      </section>

      {!!recentInvites.length && <section className="admin-access-panel admin-invite-history"><header><div><i /><span><h2>Recent Invitations</h2><p>Latest credentials issued from the console.</p></span></div><em>{invites.length} total</em></header><div>{recentInvites.map((row, index) => <article key={String(row.id ?? index)}><span><Mail /></span><div><strong>{displayValue(row.name, "name")}</strong><small>{displayValue(row.email, "email")}</small></div><b>{displayValue(row.role, "role")}</b><em>{displayValue(row.status, "status")}</em><time>{formatDate(row.createdAt)}</time></article>)}</div></section>}

      {editing && <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) setEditing(null); }}><section className="admin-editor" role="dialog" aria-modal="true"><header><span><UserCog /></span><div><h2>Edit admin access</h2><p>{String(editing.email || "")}</p></div><button onClick={() => setEditing(null)}><X /></button></header><div><label>Role<select value={editRole} onChange={(event) => setEditRole(event.target.value)}><option value="admin">Admin</option><option value="analyst">Analyst</option><option value="support">Support</option><option value="viewer">Viewer</option></select></label><div className="admin-permission-heading"><div><strong>Allowed sections</strong><small>{editSections.length} selected</small></div><button type="button" onClick={() => setEditSections(editSections.length === allSections.length ? [] : allSections)}>{editSections.length === allSections.length ? "Clear all" : "Select all"}</button></div><div className="admin-permission-grid compact">{availableSections.map((item) => <button type="button" className={editSections.includes(item.key) ? "selected" : ""} key={item.key} onClick={() => toggleSection(item.key, editSections, setEditSections)}><item.icon /><span>{item.label}</span>{editSections.includes(item.key) && <Check />}</button>)}</div><div className="admin-sensitive-permissions"><label className="admin-action-toggle"><input type="checkbox" checked={editManageTransactions} onChange={(event) => setEditManageTransactions(event.target.checked)} /><span><Zap /></span><div><strong>Allow transaction actions</strong><small>Grants access to sensitive transaction controls.</small></div></label><label className="admin-action-toggle"><input type="checkbox" checked={editAdjustWallet} onChange={(event) => setEditAdjustWallet(event.target.checked)} /><span><WalletCards /></span><div><strong>Allow wallet adjustments</strong><small>Grants access to Add / Deduct Wallet.</small></div></label><label className="admin-action-toggle"><input type="checkbox" checked={editViewTransactionProviders} onChange={(event) => setEditViewTransactionProviders(event.target.checked)} /><span><Eye /></span><div><strong>Show transaction providers</strong><small>Reveal the provider used for each transaction.</small></div></label></div></div><footer><button onClick={() => setEditing(null)}>Cancel</button><button className="save" disabled={saving} onClick={() => void saveEditor()}>{saving ? <Loader2 className="spin" /> : <Check />} Save permissions</button></footer></section></div>}
    </div>
  );
}

const bannerActions = [
  ["none", "No action"],
  ["fund", "Open Add Fund"],
  ["airtime", "Open Airtime"],
  ["data", "Open Data"],
  ["tv", "Open Cable TV"],
  ["electricity", "Open Electricity"],
  ["education", "Open Education"],
  ["payBills", "Open Pay Bills"],
  ["url", "Open URL"],
] as const;

function bannerActionLabel(value: unknown) {
  return bannerActions.find(([key]) => key === value)?.[1] || "No action";
}

function HomeBannersPage() {
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [order, setOrder] = useState("0");
  const [enabled, setEnabled] = useState(true);
  const [actionType, setActionType] = useState("none");
  const [actionValue, setActionValue] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => onSnapshot(
    query(collection(db, "admin_banners"), orderBy("createdAt", "desc")),
    (snapshot) => setRows(
      snapshot.docs
        .map((item): Record<string, unknown> => ({ id: item.id, ...item.data() }))
        .sort((a, b) => Number(a.order || 0) - Number(b.order || 0)),
    ),
    (failure) => setError(failure.message),
  ), []);

  const reset = () => {
    setEditing(null);
    setTitle("");
    setImageUrl("");
    setOrder("0");
    setEnabled(true);
    setActionType("none");
    setActionValue("");
    setError("");
  };
  const edit = (row: Record<string, unknown>) => {
    setEditing(String(row.id));
    setTitle(String(row.title || ""));
    setImageUrl(String(row.imageUrl || ""));
    setOrder(String(row.order ?? 0));
    setEnabled(row.enabled !== false);
    const action = String(row.actionType || "none");
    setActionType(bannerActions.some(([key]) => key === action) ? action : "none");
    setActionValue(String(row.actionValue || ""));
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const upload = async (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return setError("Choose a JPG, PNG or WebP image.");
    setUploading(true);
    setError("");
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const item = ref(storage, `admin_banners/${auth.currentUser?.uid || "admin"}_${Date.now()}_${safeName}`);
      await uploadBytes(item, file, { contentType: file.type });
      setImageUrl(await getDownloadURL(item));
      showFlushbar("Banner image uploaded");
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Unable to upload banner image.");
    } finally {
      setUploading(false);
    }
  };
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!imageUrl) return setError("Upload a banner image first.");
    if (actionType === "url" && !/^https?:\/\//i.test(actionValue.trim())) return setError("Enter a valid http:// or https:// URL.");
    setSaving(true);
    setError("");
    const user = auth.currentUser;
    const payload: Record<string, unknown> = {
      title: title.trim(), imageUrl, enabled, order: Number(order) || 0, actionType,
      actionValue: actionType === "url" ? actionValue.trim() : "",
      updatedAt: serverTimestamp(), updatedBy: user?.uid || "", updatedByEmail: user?.email || "",
    };
    try {
      if (editing) await setDoc(doc(db, "admin_banners", editing), payload, { merge: true });
      else await addDoc(collection(db, "admin_banners"), { ...payload, createdAt: serverTimestamp(), createdBy: user?.uid || "", createdByEmail: user?.email || "" });
      showFlushbar(editing ? "Banner updated" : "Banner added");
      reset();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Unable to save banner.");
    } finally {
      setSaving(false);
    }
  };
  const toggle = async (row: Record<string, unknown>) => {
    try {
      await updateDoc(doc(db, "admin_banners", String(row.id)), { enabled: row.enabled === false, updatedAt: serverTimestamp() });
      showFlushbar(row.enabled === false ? "Banner enabled" : "Banner disabled");
    } catch (failure) { setError(failure instanceof Error ? failure.message : "Unable to update banner."); }
  };
  const remove = async (row: Record<string, unknown>) => {
    if (!await confirmAction({ title: "Delete home banner?", message: "This banner will be removed from the mobile app carousel and cannot be restored.", confirmLabel: "Delete banner", tone: "danger" })) return;
    try {
      await deleteDoc(doc(db, "admin_banners", String(row.id)));
      if (editing === row.id) reset();
      showFlushbar("Banner deleted");
    } catch (failure) { setError(failure instanceof Error ? failure.message : "Unable to delete banner."); }
  };

  const activeCount = rows.filter((row) => row.enabled !== false).length;
  return <div className="banner-page">
    <section className="banner-metrics">
      <article><span><Image /></span><div><small>Total Banners</small><strong>{rows.length}</strong><p>Configured carousel slides</p></div></article>
      <article><span><CheckCircle2 /></span><div><small>Live Banners</small><strong>{activeCount}</strong><p>Visible in the mobile app</p></div></article>
      <article><span><PauseCircle /></span><div><small>Disabled</small><strong>{rows.length - activeCount}</strong><p>Saved but hidden from users</p></div></article>
    </section>

    <section className="banner-panel banner-editor">
      <header><div><i /><span><h2>{editing ? "Edit Home Banner" : "Add Home Banner"}</h2><p>Create wide artwork for the app carousel and choose what happens when it is tapped.</p></span></div>{editing && <button onClick={reset}><X /> Cancel edit</button>}</header>
      <form onSubmit={save}>
        <section className="banner-image-workspace">
          <div className={`banner-dropzone ${imageUrl ? "has-image" : ""}`}>
            {imageUrl ? <><img src={imageUrl} alt="Banner preview" /><div className="banner-preview-overlay"><label><UploadCloud /> Replace image<input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => void upload(event.target.files?.[0])} /></label><button type="button" onClick={() => setImageUrl("")}><X /> Remove</button></div></> : <label><span><UploadCloud /></span><strong>{uploading ? "Uploading image…" : "Upload banner artwork"}</strong><p>Choose a wide JPG, PNG or WebP image. Recommended ratio: 16:7.</p><em>Browse files</em><input type="file" accept="image/jpeg,image/png,image/webp" disabled={uploading} onChange={(event) => void upload(event.target.files?.[0])} /></label>}
          </div>
          <div className="banner-preview-note"><Image /><span><strong>Live preview</strong><small>The preview uses the same wide crop applied by the home carousel.</small></span></div>
        </section>
        <section className="banner-fields">
          <div className="banner-field-heading"><span><Settings2 /></span><div><strong>Banner settings</strong><small>Internal information, display priority and click behavior.</small></div></div>
          <label><span>Internal title</span><div><Pencil /><input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="e.g. Weekend data offer" maxLength={100} /></div><small>Only administrators see this title.</small></label>
          <div className="banner-field-row"><label><span>Display order</span><div><SortAsc /><input type="number" min="0" step="1" value={order} onChange={(event) => setOrder(event.target.value)} /></div><small>Lower numbers appear first.</small></label><label><span>Click action</span><div><Link2 /><select value={actionType} onChange={(event) => setActionType(event.target.value)}>{bannerActions.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></div><small>Destination when users tap the banner.</small></label></div>
          {actionType === "url" && <label><span>Destination URL</span><div><Link2 /><input type="url" value={actionValue} onChange={(event) => setActionValue(event.target.value)} placeholder="https://example.com/offer" required /></div></label>}
          <label className="banner-enable"><input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} /><span><strong>{enabled ? "Banner enabled" : "Banner disabled"}</strong><small>{enabled ? "It will appear in the home carousel after saving." : "It will remain hidden until enabled."}</small></span></label>
          <div className="banner-submit"><p><CheckCircle2 /> Changes update the mobile carousel in real time.</p><button disabled={saving || uploading}>{saving ? <Loader2 className="animate-spin" /> : <Image />}{saving ? "Saving…" : editing ? "Save Changes" : "Add Banner"}</button></div>
        </section>
      </form>
      {error && <div className="error form-error">{error}</div>}
    </section>

    <section className="banner-panel banner-library">
      <header><div><i /><span><h2>Current Home Banners</h2><p>Drag-free ordering uses the display order number shown on each card.</p></span></div><em>{rows.length} configured</em></header>
      <div className="banner-grid">{rows.length ? rows.map((row) => <article key={String(row.id)} className={row.enabled === false ? "disabled" : ""}>
        <div className="banner-card-image"><img src={String(row.imageUrl || "")} alt="" /><span>Order {String(row.order ?? 0)}</span></div>
        <div className="banner-card-body"><div><strong>{String(row.title || "Untitled banner")}</strong><span className={`banner-status ${row.enabled === false ? "off" : "on"}`}>{row.enabled === false ? <PauseCircle /> : <CheckCircle2 />}{row.enabled === false ? "Disabled" : "Live"}</span></div><p><Link2 /> {bannerActionLabel(row.actionType)}{row.actionType === "url" && row.actionValue ? ` · ${String(row.actionValue)}` : ""}</p><small>Updated {formatDate(row.updatedAt || row.createdAt)}</small><div className="banner-card-actions"><button onClick={() => edit(row)}><Pencil /> Edit</button><button onClick={() => void toggle(row)}>{row.enabled === false ? <PlayCircle /> : <PauseCircle />}{row.enabled === false ? "Enable" : "Disable"}</button><button className="danger" onClick={() => void remove(row)}><X /> Delete</button></div></div>
      </article>) : <div className="banner-empty"><Image /><strong>No home banners yet</strong><p>The mobile app will continue using its default carousel images.</p></div>}</div>
    </section>
  </div>;
}

const campaignAudiences = [
  ["all", "All users", "Everyone with an OPrime account"],
  ["new", "New users", "Customers who joined in the last 7 days"],
  ["noTransactions", "No transactions", "Customers who have not made a purchase"],
  ["lowBalance", "Low wallet balance", "Customers with less than ₦1,000"],
  ["single", "Single user", "One email, phone, User ID or UID"],
] as const;

function campaignAudienceLabel(value: unknown) {
  return campaignAudiences.find(([key]) => key === value)?.[1] || "All users";
}

function CampaignsPage() {
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [buttonText, setButtonText] = useState("Continue");
  const [active, setActive] = useState(true);
  const [audience, setAudience] = useState("all");
  const [targetQuery, setTargetQuery] = useState("");
  const [actionType, setActionType] = useState("none");
  const [actionValue, setActionValue] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => onSnapshot(
    query(collection(db, "admin_campaigns"), orderBy("createdAt", "desc")),
    (snapshot) => setRows(snapshot.docs.map((item): Record<string, unknown> => ({ id: item.id, ...item.data() }))),
    (failure) => setError(failure.message),
  ), []);

  const reset = () => {
    setEditing(null); setTitle(""); setBody(""); setImageUrl(""); setButtonText("Continue");
    setActive(true); setAudience("all"); setTargetQuery(""); setActionType("none"); setActionValue(""); setError("");
  };
  const edit = (row: Record<string, unknown>) => {
    setEditing(String(row.id)); setTitle(String(row.title || "")); setBody(String(row.body || ""));
    setImageUrl(String(row.imageUrl || "")); setButtonText(String(row.buttonText || "Continue"));
    setActive(row.active !== false);
    const nextAudience = String(row.audience || "all");
    setAudience(campaignAudiences.some(([key]) => key === nextAudience) ? nextAudience : "all");
    setTargetQuery(String(row.targetQuery || ""));
    const nextAction = String(row.actionType || "none");
    setActionType(bannerActions.some(([key]) => key === nextAction) ? nextAction : "none");
    setActionValue(String(row.actionValue || "")); setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const upload = async (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return setError("Choose a JPG, PNG or WebP image.");
    setUploading(true); setError("");
    try {
      const item = ref(storage, `admin_campaigns/${auth.currentUser?.uid || "admin"}_${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`);
      await uploadBytes(item, file, { contentType: file.type });
      setImageUrl(await getDownloadURL(item)); showFlushbar("Campaign image uploaded");
    } catch (failure) { setError(failure instanceof Error ? failure.message : "Unable to upload campaign image."); }
    finally { setUploading(false); }
  };
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!title.trim() || !body.trim()) return setError("Enter a campaign title and message.");
    if (audience === "single" && !targetQuery.trim()) return setError("Enter the user email, phone, User ID or UID.");
    if (actionType === "url" && !/^https?:\/\//i.test(actionValue.trim())) return setError("Enter a valid http:// or https:// URL.");
    setSaving(true); setError("");
    const user = auth.currentUser;
    const payload: Record<string, unknown> = {
      title: title.trim(), body: body.trim(), imageUrl, buttonText: buttonText.trim() || "Continue", active,
      audience, targetQuery: audience === "single" ? targetQuery.trim() : "", actionType,
      actionValue: actionType === "url" ? actionValue.trim() : "", updatedAt: serverTimestamp(),
      updatedBy: user?.uid || "", updatedByEmail: user?.email || "",
    };
    try {
      if (editing) await setDoc(doc(db, "admin_campaigns", editing), payload, { merge: true });
      else await addDoc(collection(db, "admin_campaigns"), { ...payload, createdAt: serverTimestamp(), createdBy: user?.uid || "", createdByEmail: user?.email || "" });
      showFlushbar(editing ? "Campaign saved" : "Campaign created"); reset();
    } catch (failure) { setError(failure instanceof Error ? failure.message : "Unable to save campaign."); }
    finally { setSaving(false); }
  };
  const toggle = async (row: Record<string, unknown>) => {
    try { await updateDoc(doc(db, "admin_campaigns", String(row.id)), { active: row.active === false, updatedAt: serverTimestamp() }); showFlushbar(row.active === false ? "Campaign activated" : "Campaign paused"); }
    catch (failure) { setError(failure instanceof Error ? failure.message : "Unable to update campaign."); }
  };
  const remove = async (row: Record<string, unknown>) => {
    if (!await confirmAction({ title: "Delete campaign?", message: "Users will no longer see this campaign and the record cannot be restored.", confirmLabel: "Delete campaign", tone: "danger" })) return;
    try { await deleteDoc(doc(db, "admin_campaigns", String(row.id))); if (editing === row.id) reset(); showFlushbar("Campaign deleted"); }
    catch (failure) { setError(failure instanceof Error ? failure.message : "Unable to delete campaign."); }
  };

  const activeCount = rows.filter((row) => row.active !== false).length;
  const targetedCount = rows.filter((row) => row.audience !== "all").length;
  return <div className="campaign-page">
    <section className="campaign-metrics"><article><span><Megaphone /></span><div><small>Total Campaigns</small><strong>{rows.length}</strong><p>In-app messages configured</p></div></article><article><span><CheckCircle2 /></span><div><small>Active</small><strong>{activeCount}</strong><p>Eligible to appear in the app</p></div></article><article><span><Target /></span><div><small>Targeted</small><strong>{targetedCount}</strong><p>Campaigns using audience rules</p></div></article></section>
    <section className="campaign-panel campaign-editor">
      <header><div><i /><span><h2>{editing ? "Edit In-App Campaign" : "Create In-App Campaign"}</h2><p>Design a focused popup, select its audience and configure the call to action.</p></span></div>{editing && <button onClick={reset}><X /> Cancel edit</button>}</header>
      <form onSubmit={save}>
        <section className="campaign-compose">
          <div className="campaign-section-title"><span><MessageSquare /></span><div><strong>Campaign content</strong><small>Write the message customers will see.</small></div></div>
          <label><span>Campaign title</span><div><Megaphone /><input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="e.g. Weekend bonus is live" maxLength={100} required /></div></label>
          <label><span>Message</span><div className="campaign-message"><MessageSquare /><textarea value={body} onChange={(event) => setBody(event.target.value)} placeholder="Tell customers what is available and why they should act." maxLength={500} required /></div><small>{body.length}/500 characters</small></label>
          <div className={`campaign-image-picker ${imageUrl ? "has-image" : ""}`}>{imageUrl ? <><img src={imageUrl} alt="Campaign" /><div><label><UploadCloud /> Replace<input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => void upload(event.target.files?.[0])} /></label><button type="button" onClick={() => setImageUrl("")}><X /> Remove</button></div></> : <label><UploadCloud /><span><strong>{uploading ? "Uploading…" : "Add campaign image"}</strong><small>Optional artwork displayed in the popup.</small></span><em>Browse</em><input type="file" accept="image/jpeg,image/png,image/webp" disabled={uploading} onChange={(event) => void upload(event.target.files?.[0])} /></label>}</div>
        </section>
        <section className="campaign-settings">
          <div className="campaign-section-title"><span><Target /></span><div><strong>Delivery and action</strong><small>Choose who sees it and what the button does.</small></div></div>
          <label><span>Audience</span><div><Users /><select value={audience} onChange={(event) => setAudience(event.target.value)}>{campaignAudiences.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div><small>{campaignAudiences.find(([value]) => value === audience)?.[2]}</small></label>
          {audience === "single" && <label><span>Target customer</span><div><Search /><input value={targetQuery} onChange={(event) => setTargetQuery(event.target.value)} placeholder="Email, phone, User ID or UID" required /></div></label>}
          <div className="campaign-field-row"><label><span>Button text</span><div><Pencil /><input value={buttonText} onChange={(event) => setButtonText(event.target.value)} placeholder="Continue" maxLength={30} /></div></label><label><span>Button action</span><div><Link2 /><select value={actionType} onChange={(event) => setActionType(event.target.value)}>{bannerActions.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></div></label></div>
          {actionType === "url" && <label><span>Destination URL</span><div><Link2 /><input type="url" value={actionValue} onChange={(event) => setActionValue(event.target.value)} placeholder="https://example.com/offer" required /></div></label>}
          <label className="campaign-active"><input type="checkbox" checked={active} onChange={(event) => setActive(event.target.checked)} /><span><strong>{active ? "Campaign active" : "Campaign paused"}</strong><small>{active ? "Eligible customers can see it after saving." : "The campaign remains saved but hidden."}</small></span></label>
          <div className="campaign-submit"><p><CheckCircle2 /> Customers dismiss each campaign after viewing or acting.</p><button disabled={saving || uploading}>{saving ? <Loader2 className="animate-spin" /> : <Megaphone />}{saving ? "Saving…" : editing ? "Save Campaign" : "Create Campaign"}</button></div>
        </section>
        <aside className="campaign-phone-preview"><div className="campaign-preview-label"><Eye /> Live popup preview</div><div className="campaign-phone"><div className="campaign-phone-top" /><div className="campaign-popup">{imageUrl && <img src={imageUrl} alt="" />}<strong>{title.trim() || "Campaign title"}</strong><p>{body.trim() || "Your campaign message will appear here exactly as customers will see it."}</p><div><button type="button">Not now</button><button type="button">{buttonText.trim() || "Continue"}</button></div></div></div></aside>
      </form>{error && <div className="error form-error">{error}</div>}
    </section>
    <section className="campaign-panel campaign-library"><header><div><i /><span><h2>Current Campaigns</h2><p>Manage active messages and review their audience and destination.</p></span></div><em>{rows.length} configured</em></header><div className="campaign-list">{rows.length ? rows.map((row) => <article key={String(row.id)} className={row.active === false ? "paused" : ""}>{row.imageUrl ? <img src={String(row.imageUrl)} alt="" /> : <span className="campaign-list-icon"><Megaphone /></span>}<div className="campaign-list-copy"><div><strong>{String(row.title || "Untitled campaign")}</strong><span className={`campaign-status ${row.active === false ? "off" : "on"}`}>{row.active === false ? <PauseCircle /> : <CheckCircle2 />}{row.active === false ? "Paused" : "Active"}</span></div><p>{String(row.body || "")}</p><small><Users /> {campaignAudienceLabel(row.audience)} <b>·</b> <Link2 /> {bannerActionLabel(row.actionType)}</small></div><div className="campaign-list-actions"><button onClick={() => edit(row)}><Pencil /> Edit</button><button onClick={() => void toggle(row)}>{row.active === false ? <PlayCircle /> : <PauseCircle />}{row.active === false ? "Activate" : "Pause"}</button><button className="danger" onClick={() => void remove(row)}><X /> Delete</button></div></article>) : <div className="campaign-empty"><Megaphone /><strong>No campaigns yet</strong><p>Create your first in-app campaign above.</p></div>}</div></section>
  </div>;
}

function ContentManager({ kind }: { kind: "banner" | "campaign" }) {
  const collectionName =
    kind === "banner" ? "admin_banners" : "admin_campaigns";
  const enabledKey = kind === "banner" ? "enabled" : "active";
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [order, setOrder] = useState("0");
  const [enabled, setEnabled] = useState(true);
  const [audience, setAudience] = useState("all");
  const [target, setTarget] = useState("");
  const [buttonText, setButtonText] = useState("Continue");
  const [actionType, setActionType] = useState("none");
  const [actionValue, setActionValue] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  useEffect(
    () =>
      onSnapshot(
        query(
          collection(db, collectionName),
          orderBy(kind === "banner" ? "order" : "updatedAt", "desc"),
        ),
        (snapshot) =>
          setRows(
            snapshot.docs.map((item) => ({ id: item.id, ...item.data() })),
          ),
        (failure) => setError(failure.message),
      ),
    [collectionName, kind],
  );
  const reset = () => {
    setEditing(null);
    setTitle("");
    setBody("");
    setImageUrl("");
    setOrder("0");
    setEnabled(true);
    setAudience("all");
    setTarget("");
    setButtonText("Continue");
    setActionType("none");
    setActionValue("");
  };
  const edit = (row: Record<string, unknown>) => {
    setEditing(String(row.id));
    setTitle(String(row.title ?? ""));
    setBody(String(row.body ?? ""));
    setImageUrl(String(row.imageUrl ?? ""));
    setOrder(String(row.order ?? 0));
    setEnabled(row[enabledKey] !== false);
    setAudience(String(row.audience ?? "all"));
    setTarget(String(row.targetQuery ?? ""));
    setButtonText(String(row.buttonText ?? "Continue"));
    setActionType(String(row.actionType ?? "none"));
    setActionValue(String(row.actionValue ?? ""));
  };
  const upload = async (file?: File) => {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const path = `admin_${kind === "banner" ? "banners" : "campaigns"}/${auth.currentUser?.uid ?? "admin"}_${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
      const item = ref(storage, path);
      await uploadBytes(item, file, { contentType: file.type });
      setImageUrl(await getDownloadURL(item));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (kind === "banner" && !imageUrl)
      return setError("Upload a banner image first.");
    if (kind === "campaign" && (!title || !body))
      return setError("Enter a campaign title and message.");
    if (audience === "single" && !target)
      return setError("Enter a target user.");
    if (actionType === "url" && !/^https?:\/\//.test(actionValue))
      return setError("Enter a valid URL.");
    setError("");
    const payload: Record<string, unknown> = {
      title,
      imageUrl,
      [enabledKey]: enabled,
      actionType,
      actionValue,
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.uid ?? "",
      updatedByEmail: auth.currentUser?.email ?? "",
    };
    if (kind === "banner") payload.order = Number(order) || 0;
    else
      Object.assign(payload, {
        body,
        buttonText: buttonText || "Continue",
        audience,
        targetQuery: target,
      });
    try {
      if (editing)
        await setDoc(doc(db, collectionName, editing), payload, {
          merge: true,
        });
      else
        await addDoc(collection(db, collectionName), {
          ...payload,
          createdAt: serverTimestamp(),
          createdBy: auth.currentUser?.uid ?? "",
          createdByEmail: auth.currentUser?.email ?? "",
        });
      reset();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to save");
    }
  };
  const toggle = async (row: Record<string, unknown>) => {
    try {
      await updateDoc(doc(db, collectionName, String(row.id)), {
        [enabledKey]: row[enabledKey] === false,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to update");
    }
  };
  const remove = async (row: Record<string, unknown>) => {
    if (!await confirmAction({ title: `Delete this ${kind}?`, message: `This ${kind} will be permanently removed from the admin panel.`, confirmLabel: `Delete ${kind}`, tone: "danger" })) return;
    try {
      await deleteDoc(doc(db, collectionName, String(row.id)));
      if (editing === row.id) reset();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to delete");
    }
  };
  const label = kind === "banner" ? "Home Banners" : "Campaigns";
  return (
    <>
      <FormPanel
        title={editing ? `Edit ${kind}` : `Add ${kind}`}
        subtitle={
          kind === "banner"
            ? "Manage home carousel artwork, ordering and actions"
            : "Manage in-app messages and audience targeting"
        }
      >
        <form className="admin-form inline" onSubmit={save}>
          <label>
            Title
            <input value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>
          {kind === "campaign" && (
            <label>
              Message
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                required
              />
            </label>
          )}
          <label>
            Image
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => void upload(e.target.files?.[0])}
            />
            <small>
              {uploading
                ? "Uploading…"
                : imageUrl
                  ? "Image ready"
                  : "Choose JPG, PNG or WebP"}
            </small>
          </label>
          {kind === "banner" ? (
            <label>
              Order
              <input
                type="number"
                value={order}
                onChange={(e) => setOrder(e.target.value)}
              />
            </label>
          ) : (
            <>
              <label>
                Button text
                <input
                  value={buttonText}
                  onChange={(e) => setButtonText(e.target.value)}
                />
              </label>
              <label>
                Audience
                <select
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                >
                  <option value="all">All users</option>
                  <option value="active">Active users</option>
                  <option value="single">Single user</option>
                </select>
              </label>
              {audience === "single" && (
                <label>
                  Target
                  <input
                    value={target}
                    onChange={(e) => setTarget(e.target.value)}
                  />
                </label>
              )}
            </>
          )}
          <label>
            Action
            <select
              value={actionType}
              onChange={(e) => setActionType(e.target.value)}
            >
              <option value="none">None</option>
              <option value="url">URL</option>
              <option value="screen">App screen</option>
            </select>
          </label>
          {actionType !== "none" && (
            <label>
              Action value
              <input
                value={actionValue}
                onChange={(e) => setActionValue(e.target.value)}
              />
            </label>
          )}
          <label className="check">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            {enabled ? "Enabled" : "Disabled"}
          </label>
          <button className="primary" disabled={uploading}>
            {editing ? "Save Changes" : `Add ${kind}`}
          </button>
          {editing && (
            <button type="button" className="action-button" onClick={reset}>
              Cancel
            </button>
          )}
        </form>
        {error && <div className="error form-error">{error}</div>}
      </FormPanel>
      <div className="section-gap">
        <div className="panel">
          <div className="panel-title">
            <div>
              <h2>{label}</h2>
              <p>{rows.length} configured</p>
            </div>
          </div>
          <div className="content-cards">
            {rows.map((row) => (
              <article className="content-card" key={String(row.id)}>
                {Boolean(row.imageUrl) && (
                  <img src={String(row.imageUrl)} alt="" />
                )}
                <div>
                  <h3>{String(row.title || `Untitled ${kind}`)}</h3>
                  {kind === "campaign" && <p>{String(row.body ?? "")}</p>}
                  <small>
                    {row[enabledKey] === false ? "Disabled" : "Active"}
                    {kind === "banner"
                      ? ` · Order ${row.order ?? 0}`
                      : ` · ${row.audience ?? "all"} audience`}
                  </small>
                  <div className="row-actions">
                    <button onClick={() => edit(row)}>Edit</button>
                    <button onClick={() => void toggle(row)}>
                      {row[enabledKey] === false ? "Enable" : "Disable"}
                    </button>
                    <button className="danger" onClick={() => void remove(row)}>
                      Delete
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function FormPanel({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
}) {
  return (
    <div className="panel">
      <div className="panel-title">
        <div>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
      </div>
      <div className="form-body">{children}</div>
    </div>
  );
}

const serviceAvailabilityOptions = [
  ["funding", "Wallet Funding"],
  ["airtime", "Airtime"],
  ["data", "Data"],
  ["tv", "Cable TV"],
  ["electricity", "Electricity"],
  ["education", "Education"],
  ["virtualNumbers", "Virtual Numbers"],
  ["referrals", "Referral Earnings"],
] as const;

const fallbackNetworkPrefixes: Record<string, string[]> = {
  mtn: ["0703", "0704", "0706", "0707", "0803", "0806", "0810", "0813", "0814", "0815", "0816", "0903", "0906", "0913", "0916"],
  airtel: ["0701", "0708", "0802", "0808", "0812", "0901", "0902", "0904", "0907", "0912"],
  glo: ["0705", "0805", "0807", "0811", "0815", "0905", "0914", "0915"],
  "9mobile": ["0809", "0817", "0818", "0908", "0909"],
};

const dataNetworkOptions = [
  ["mtn", "MTN"],
  ["airtel", "Airtel"],
  ["glo", "Glo"],
  ["9mobile", "9mobile / T2"],
] as const;

function ServicesPage({ data: _data }: { data: DashboardData }) {
  const [availability, setAvailability] = useState<Record<string, unknown>>({});
  const [prefixes, setPrefixes] = useState<Record<string, string[]>>(fallbackNetworkPrefixes);
  const [virtualConfig, setVirtualConfig] = useState<Record<string, unknown>>({});
  const [electricityConfig, setElectricityConfig] = useState<Record<string, unknown>>({});
  const [editingNetwork, setEditingNetwork] = useState<string | null>(null);
  const [prefixDraft, setPrefixDraft] = useState("");
  const [editingVirtual, setEditingVirtual] = useState(false);
  const [virtualDraft, setVirtualDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const unsubscribers = [
      onSnapshot(doc(db, "admin_service_config", "availability"), (snapshot) =>
        setAvailability(snapshot.data() || {}),
      ),
      onSnapshot(doc(db, "admin_provider_config", "network_prefixes"), (snapshot) => {
        const raw = snapshot.data()?.prefixes as Record<string, unknown> | undefined;
        const parsed: Record<string, string[]> = {};
        Object.entries(fallbackNetworkPrefixes).forEach(([network, fallback]) => {
          const values = Array.isArray(raw?.[network])
            ? (raw?.[network] as unknown[]).map(String).filter((value) => /^0\d{3}$/.test(value))
            : [];
          parsed[network] = values.length ? [...new Set(values)].sort() : fallback;
        });
        setPrefixes(parsed);
      }),
      onSnapshot(doc(db, "admin_provider_config", "virtual_numbers"), (snapshot) =>
        setVirtualConfig(snapshot.data() || {}),
      ),
      onSnapshot(doc(db, "admin_provider_config", "electricity"), (snapshot) =>
        setElectricityConfig(snapshot.data() || {}),
      ),
    ];
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, []);

  const saveDocument = async (
    collectionName: string,
    documentName: string,
    values: Record<string, unknown>,
    success: string,
  ) => {
    setSaving(true);
    try {
      await setDoc(doc(db, collectionName, documentName), {
        ...values,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.email || auth.currentUser?.uid || "admin",
      }, { merge: true });
      showFlushbar(success);
      return true;
    } catch (error) {
      showFlushbar(error instanceof Error ? error.message : "Could not save service settings", true);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const toggleService = (key: string, enabled: boolean) => {
    setAvailability((current) => ({ ...current, [key]: enabled }));
    void saveDocument("admin_service_config", "availability", { [key]: enabled }, enabled ? "Service enabled" : "Service disabled");
  };

  const toggleDataNetwork = (network: string, enabled: boolean) => {
    const current = availability.dataNetworks && typeof availability.dataNetworks === "object"
      ? availability.dataNetworks as Record<string, unknown>
      : {};
    const dataNetworks = { ...current, [network]: enabled };
    setAvailability((value) => ({ ...value, dataNetworks }));
    void saveDocument(
      "admin_service_config",
      "availability",
      { dataNetworks },
      enabled ? `${humanize(network)} data enabled` : `${humanize(network)} data disabled`,
    );
  };

  const openPrefixEditor = (network: string) => {
    setEditingNetwork(network);
    setPrefixDraft((prefixes[network] || []).join(", "));
  };

  const savePrefixes = async () => {
    if (!editingNetwork) return;
    const values = [...new Set(prefixDraft.split(/[\s,;]+/).map((value) => value.replace(/\D/g, "")).filter(Boolean))].sort();
    if (!values.length || values.some((value) => !/^0\d{3}$/.test(value))) {
      showFlushbar("Use 4-digit prefixes such as 0803", true);
      return;
    }
    const saved = await saveDocument("admin_provider_config", "network_prefixes", { prefixes: { [editingNetwork]: values } }, `${humanize(editingNetwork)} prefixes updated`);
    if (saved) setEditingNetwork(null);
  };

  const openVirtualEditor = () => {
    const legacyUsd = String(virtualConfig.exchangeRateNgnPerUsd ?? "1600");
    setVirtualDraft({
      provider: String(virtualConfig.provider ?? "SMSPool"),
      markupPercent: String(virtualConfig.markupPercent ?? "20"),
      exchangeRateNgnPerRub: String(virtualConfig.exchangeRateNgnPerRub ?? "25"),
      smsPoolExchangeRateNgnPerUsd: String(virtualConfig.smsPoolExchangeRateNgnPerUsd ?? legacyUsd),
      grizzlyExchangeRateNgnPerUsd: String(virtualConfig.grizzlyExchangeRateNgnPerUsd ?? legacyUsd),
      virtualSmsExchangeRateNgnPerUsd: String(virtualConfig.virtualSmsExchangeRateNgnPerUsd ?? legacyUsd),
      maxWhatsAppPriceNaira: String(virtualConfig.maxWhatsAppPriceNaira ?? "6000"),
      maxSmsPoolPriceNaira: String(virtualConfig.maxSmsPoolPriceNaira ?? "5000"),
      maxVirtualSmsPriceNaira: String(virtualConfig.maxVirtualSmsPriceNaira ?? "10000"),
    });
    setEditingVirtual(true);
  };

  const saveVirtual = async () => {
    const numericKeys = ["markupPercent", "exchangeRateNgnPerRub", "smsPoolExchangeRateNgnPerUsd", "grizzlyExchangeRateNgnPerUsd", "virtualSmsExchangeRateNgnPerUsd", "maxWhatsAppPriceNaira", "maxSmsPoolPriceNaira", "maxVirtualSmsPriceNaira"];
    const values = Object.fromEntries(numericKeys.map((key) => [key, Number(virtualDraft[key])]));
    if (Object.values(values).some((value) => !Number.isFinite(value) || value < 0)) {
      showFlushbar("Enter valid provider rates and prices", true);
      return;
    }
    const saved = await saveDocument("admin_provider_config", "virtual_numbers", { provider: virtualDraft.provider, ...values, exchangeRateNgnPerUsd: values.smsPoolExchangeRateNgnPerUsd }, "Virtual number settings updated");
    if (saved) setEditingVirtual(false);
  };

  const electricityProvider = String(electricityConfig.provider ?? "vtpass").toLowerCase();
  const virtualProvider = String(virtualConfig.provider ?? "SMSPool");
  const virtualStats = [
    ["Provider", virtualProvider],
    ["Markup", `${virtualConfig.markupPercent ?? 20}%`],
    ["SMS-Man ₦/RUB", String(virtualConfig.exchangeRateNgnPerRub ?? 25)],
    ["SMSPool ₦/$", String(virtualConfig.smsPoolExchangeRateNgnPerUsd ?? virtualConfig.exchangeRateNgnPerUsd ?? 1600)],
    ["Grizzly ₦/$", String(virtualConfig.grizzlyExchangeRateNgnPerUsd ?? virtualConfig.exchangeRateNgnPerUsd ?? 1600)],
    ["VirtualSMS ₦/$", String(virtualConfig.virtualSmsExchangeRateNgnPerUsd ?? virtualConfig.exchangeRateNgnPerUsd ?? 1600)],
    ["Max WhatsApp", `₦${virtualConfig.maxWhatsAppPriceNaira ?? 6000}`],
    ["Max SMSPool", `₦${virtualConfig.maxSmsPoolPriceNaira ?? 5000}`],
    ["Max VirtualSMS", `₦${virtualConfig.maxVirtualSmsPriceNaira ?? 10000}`],
    ["Catalog", "Live"],
  ];

  return (
    <div className="legacy-services-page">
      <section className="panel legacy-service-panel">
        <div className="panel-title"><div><h2>Service Availability</h2></div></div>
        <div className="legacy-availability-grid">
          {serviceAvailabilityOptions.map(([key, label]) => {
            const enabled = availability[key] !== false;
            return <article className="legacy-availability-card" key={key}>
              <span className={`legacy-service-state ${enabled ? "enabled" : "disabled"}`}>{enabled ? <CheckCircle2 /> : <Ban />}</span>
              <div><strong>{label}</strong><small>{enabled ? "Available to users" : "Hidden from users"}</small></div>
              <label className="service-switch"><input type="checkbox" checked={enabled} onChange={(event) => toggleService(key, event.target.checked)} /><span /></label>
            </article>;
          })}
        </div>
        <div className="data-network-availability">
          <div><strong>Data networks</strong><small>Choose which mobile networks users can select and purchase data from.</small></div>
          <div className="data-network-toggle-grid">
            {dataNetworkOptions.map(([network, label]) => {
              const configured = availability.dataNetworks && typeof availability.dataNetworks === "object"
                ? availability.dataNetworks as Record<string, unknown>
                : {};
              const enabled = configured[network] !== false;
              return <article key={network}>
                <span className={`legacy-service-state ${enabled ? "enabled" : "disabled"}`}>{enabled ? <CheckCircle2 /> : <Ban />}</span>
                <div><strong>{label}</strong><small>{enabled ? "Available for data purchases" : "Unavailable to users"}</small></div>
                <label className="service-switch"><input type="checkbox" checked={enabled} onChange={(event) => toggleDataNetwork(network, event.target.checked)} /><span /></label>
              </article>;
            })}
          </div>
        </div>
      </section>

      <section className="panel legacy-service-panel">
        <div className="panel-title"><div><h2>Network Number Prefixes</h2><p>Add or remove prefixes used by the app and backend.</p></div></div>
        <div className="network-prefix-grid">
          {Object.entries(prefixes).map(([network, values]) => <article className="network-prefix-card" key={network}>
            <div className="network-prefix-heading"><span><Smartphone /></span><strong>{network === "9mobile" ? "9mobile" : humanize(network)}</strong><button onClick={() => openPrefixEditor(network)}><Pencil /> Edit</button></div>
            <div className="prefix-chips">{values.map((value) => <span key={value}>{value}</span>)}</div>
          </article>)}
        </div>
      </section>

      <section className="panel legacy-service-panel">
        <div className="panel-title"><div><h2>Virtual Numbers</h2><p>Configure the temporary virtual number product. Countries and services load live from the provider.</p></div></div>
        <div className="virtual-settings-summary">
          {virtualStats.map(([label, value]) => <div key={label}><small>{label}</small><strong>{value}</strong></div>)}
          <button className="primary legacy-edit-settings" onClick={openVirtualEditor}><Pencil /> Edit settings</button>
        </div>
      </section>

      <section className="panel legacy-service-panel">
        <div className="panel-title"><div><h2>Electricity Provider</h2></div></div>
        <div className="electricity-current"><div><small>Current electricity provider</small><strong>{electricityProvider.toUpperCase()}</strong></div><span>{electricityProvider === "lizzysub" ? "LizzySub active" : "VTpass active"}</span></div>
        <div className="electricity-options">
          {[
            ["vtpass", "VTpass", "Uses VTpass for meter verification and electricity purchase."],
            ["lizzysub", "LizzySub", "Uses LizzySub for meter verification and electricity purchase."],
          ].map(([id, title, subtitle]) => {
            const active = electricityProvider === id;
            return <button className={`electricity-option ${active ? "active" : ""}`} key={id} disabled={saving || active} onClick={() => void saveDocument("admin_provider_config", "electricity", { provider: id }, `Electricity provider set to ${title}`)}><span><Zap /></span><div><strong>{title}</strong><small>{subtitle}</small><em>{active ? "Currently active" : "Set as active"}</em></div>{active && <CheckCircle2 />}</button>;
          })}
        </div>
        <p className="service-updated">Last updated: {formatDate(electricityConfig.updatedAt)} · Updated by: {String(electricityConfig.updatedBy ?? "—")}</p>
      </section>

      {editingNetwork && <div className="confirm-modal-overlay" onMouseDown={() => !saving && setEditingNetwork(null)}><div className="service-editor-modal" onMouseDown={(event) => event.stopPropagation()}><div className="service-editor-header"><div><span className="service-editor-icon"><Smartphone /></span><div><h2>Edit {humanize(editingNetwork)} prefixes</h2><p>Update the prefixes used to identify this network.</p></div></div><button onClick={() => setEditingNetwork(null)} aria-label="Close"><X /></button></div><div className="service-editor-body"><label className="service-editor-field">Number prefixes<textarea value={prefixDraft} onChange={(event) => setPrefixDraft(event.target.value)} rows={6} autoFocus /><small>Separate entries with commas, spaces, or new lines. Use four digits such as 0803.</small></label></div><div className="service-editor-footer"><button className="confirm-btn-cancel" onClick={() => setEditingNetwork(null)}>Cancel</button><button className="service-save-button" disabled={saving} onClick={() => void savePrefixes()}>{saving ? <><Loader2 className="spin" /> Saving…</> : "Save changes"}</button></div></div></div>}

      {editingVirtual && (
        <div className="confirm-modal-overlay" onMouseDown={() => !saving && setEditingVirtual(false)}>
          <div className="service-editor-modal virtual-editor" onMouseDown={(event) => event.stopPropagation()}>
            <div className="service-editor-header">
              <div><span className="service-editor-icon"><Smartphone /></span><div><h2>Virtual number settings</h2><p>Manage provider pricing, exchange rates and selling limits.</p></div></div>
              <button onClick={() => setEditingVirtual(false)} aria-label="Close"><X /></button>
            </div>
            <div className="service-editor-body">
              <div className="service-editor-fields">
                <label className="provider-field">Provider<select value={virtualDraft.provider} onChange={(event) => setVirtualDraft((current) => ({ ...current, provider: event.target.value }))}><option value="VirtualSMS">VirtualSMS</option><option value="SMSMan">SMS-Man</option><option value="SMSPool">SMSPool</option><option value="GrizzlySMS">GrizzlySMS</option></select><small>The active provider used for new virtual-number purchases. When VirtualSMS is active, only USA WhatsApp is automatically routed to GrizzlySMS.</small></label>
                {[
                  ["markupPercent", "Markup percent", "Percentage added to the provider cost."],
                  ["smsPoolExchangeRateNgnPerUsd", "SMSPool rate", "Naira value of one US dollar."],
                  ["grizzlyExchangeRateNgnPerUsd", "Grizzly rate", "Naira value of one US dollar."],
                  ["virtualSmsExchangeRateNgnPerUsd", "VirtualSMS rate", "Naira value of one US dollar."],
                  ["maxWhatsAppPriceNaira", "Max WhatsApp price", "Maximum customer selling price in naira."],
                  ["maxSmsPoolPriceNaira", "Max SMSPool price", "Maximum customer selling price in naira."],
                  ["maxVirtualSmsPriceNaira", "Max VirtualSMS price", "Maximum customer selling price in naira."],
                  ["exchangeRateNgnPerRub", "SMS-Man / Grizzly rate", "Naira value of one Russian ruble."],
                ].map(([key, label, help]) => <label key={key}>{label}<div className="service-number-input"><span>{key === "markupPercent" ? "%" : "₦"}</span><input type="number" min="0" value={virtualDraft[key] || ""} onChange={(event) => setVirtualDraft((current) => ({ ...current, [key]: event.target.value }))} /></div><small>{help}</small></label>)}
              </div>
            </div>
            <div className="service-editor-footer"><button className="confirm-btn-cancel" onClick={() => setEditingVirtual(false)}>Cancel</button><button className="service-save-button" disabled={saving} onClick={() => void saveVirtual()}>{saving ? <><Loader2 className="spin" /> Saving…</> : "Save changes"}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

function FundingPage({ data }: { data: DashboardData }) {
  void data;
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configError, setConfigError] = useState("");
  const fundingRef = doc(db, "admin_provider_config", "funding");
  const provider = String(config.provider ?? "xixapay").toLowerCase() === "sqaud"
    ? "squad"
    : String(config.provider ?? "xixapay").toLowerCase();
  const bankCode = String(config.xixapayBankCode ?? "20987");
  const bankNames: Record<string, string> = { "20867": "Palmpay", "20987": "Kolomoni", "29007": "Safehaven", "100004": "OPay" };
  const bankName = String(config.xixapayBankName ?? bankNames[bankCode] ?? "Xixapay Bank");
  const paymentPointBankCode = String(config.paymentpointBankCode ?? "20946");
  const paymentPointBankNames: Record<string, string> = { "20946": "PalmPay", "20897": "OPay" };
  const paymentPointBankName = String(config.paymentpointBankName ?? paymentPointBankNames[paymentPointBankCode] ?? "PaymentPoint Bank");

  useEffect(() => onSnapshot(
    fundingRef,
    (snapshot) => {
      setConfig(snapshot.data() ?? {});
      setConfigError("");
      setLoading(false);
    },
    (failure) => {
      setConfigError(failure.message);
      setLoading(false);
    },
  ), []);

  const saveConfig = async (values: Record<string, unknown>, success: string) => {
    setSaving(true);
    setConfigError("");
    try {
      await setDoc(fundingRef, {
        ...values,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.email ?? auth.currentUser?.uid ?? "admin",
      }, { merge: true });
      showFlushbar(success);
    } catch (failure) {
      const message = failure instanceof Error ? failure.message : "Could not update funding settings";
      setConfigError(message);
      showFlushbar(message, true);
    } finally {
      setSaving(false);
    }
  };

  const setSquadAccount = () => {
    const current = String(config.squadBeneficiaryAccount ?? "");
    const entered = window.prompt("Enter the 10-digit GTBank beneficiary account Squad settles collections into.", current);
    if (entered === null) return;
    const normalized = entered.replace(/\D/g, "");
    if (!/^\d{10}$/.test(normalized)) {
      showFlushbar("Enter a valid 10-digit beneficiary account", true);
      return;
    }
    void saveConfig({ squadBeneficiaryAccount: normalized }, "Squad beneficiary account saved");
  };

  const providers = [
    { id: "xixapay", title: "Xixapay", description: "Users see their virtual account details for bank transfer funding.", icon: Database },
    { id: "paystack", title: "Paystack", description: "Users enter an amount and complete payment through Paystack checkout.", icon: WalletCards },
    { id: "squad", title: "Squad", description: "Users get a Squad GTBank virtual account for automatic wallet funding.", icon: Database },
    { id: "seerbit", title: "SeerBit", description: "Users get a SeerBit virtual account for automatic bank transfer funding.", icon: WalletCards },
    { id: "paymentpoint", title: "PaymentPoint", description: "Users get a persistent PalmPay or OPay virtual account powered by PaymentPoint.", icon: Database },
  ];
  const banks = [
    { code: "20867", name: "Palmpay", description: "Uses Xixapay Palmpay virtual account bank code 20867." },
    { code: "20987", name: "Kolomoni", description: "Uses Xixapay Kolomoni virtual account bank code 20987." },
    { code: "29007", name: "Safehaven", description: "Uses Xixapay Safehaven Dynamic & Static bank code 29007." },
    { code: "100004", name: "OPay", description: "Uses Xixapay OPay Dynamic Account bank code 100004." },
  ];
  const paymentPointBanks = [
    { code: "20946", name: "PalmPay", description: "Creates a PaymentPoint PalmPay virtual account." },
    { code: "20897", name: "OPay", description: "Creates a PaymentPoint OPay virtual account." },
  ];
  const providerTitle = providers.find((item) => item.id === provider)?.title ?? provider;

  return (
    <section className="funding-manager">
      <header className="funding-heading">
        <div className="funding-title"><i /><h2>Bank Funding Provider</h2></div>
        <span className="funding-live"><i /> Refresh on demand</span>
      </header>
      <div className="funding-body">
        {configError && <div className="form-error">{configError}</div>}
        <div className="funding-current">
          <div><small>Current provider</small><strong>{providerTitle.toUpperCase()}</strong></div>
          <span><CheckCircle2 /> {providerTitle} Active</span>
        </div>

        <div className="funding-provider-grid">
          {providers.map((item) => {
            const active = provider === item.id;
            const Icon = item.icon;
            return <button key={item.id} className={`funding-option ${active ? "active" : ""}`} disabled={saving || active} onClick={() => void saveConfig({ provider: item.id }, `Funding provider set to ${item.title.toUpperCase()}`)}>
              <span className="funding-option-icon"><Icon /></span>
              <span className="funding-option-copy"><strong>{item.title}</strong><small>{item.description}</small><b>{active ? "Currently active" : "Set as active"}</b></span>
              {active && <CheckCircle2 className="funding-check" />}
            </button>;
          })}
        </div>

        {provider === "xixapay" && <section className="funding-subpanel">
          <header><div><WalletCards /><strong>Xixapay Bank Account Type</strong></div><span><Clock3 /> {bankName} {bankCode}</span></header>
          <div className="funding-bank-grid">
            {banks.map((bank) => {
              const active = bank.code === bankCode;
              return <button key={bank.code} className={`funding-bank ${active ? "active" : ""}`} disabled={saving || active} onClick={() => void saveConfig({ xixapayBankCode: bank.code, xixapayBankName: bank.name }, `Xixapay bank set to ${bank.name}`)}>
                <strong>{bank.name}</strong>{active && <CheckCircle2 />}
                <span>Bank code: {bank.code}</span><small>{bank.description}</small>
              </button>;
            })}
          </div>
          <p>When this changes, users without the selected Xixapay account will generate the selected bank account when they tap Add Fund.</p>
        </section>}

        {provider === "paymentpoint" && <section className="funding-subpanel">
          <header><div><WalletCards /><strong>PaymentPoint Bank</strong></div><span><Clock3 /> {paymentPointBankName} {paymentPointBankCode}</span></header>
          <div className="funding-bank-grid">
            {paymentPointBanks.map((bank) => {
              const active = bank.code === paymentPointBankCode;
              return <button key={bank.code} className={`funding-bank ${active ? "active" : ""}`} disabled={saving || active} onClick={() => void saveConfig({ paymentpointBankCode: bank.code, paymentpointBankName: bank.name }, `PaymentPoint bank set to ${bank.name}`)}>
                <strong>{bank.name}</strong>{active && <CheckCircle2 />}
                <span>Bank code: {bank.code}</span><small>{bank.description}</small>
              </button>;
            })}
          </div>
          <p>Users without a PaymentPoint account will receive the selected bank account when they tap Add Fund.</p>
        </section>}

        {provider === "squad" && <section className="funding-subpanel squad-account">
          <div><CheckCircle2 /><span><strong>Squad Beneficiary Account</strong><small>{/^\d{10}$/.test(String(config.squadBeneficiaryAccount ?? "")) ? `Settlement account: ${config.squadBeneficiaryAccount}` : "Required before Squad can create virtual accounts."}</small></span></div>
          <button disabled={saving} onClick={setSquadAccount}>{config.squadBeneficiaryAccount ? "Change" : "Add"}</button>
        </section>}

        <footer className="funding-meta">
          <small>Firestore path</small>
          <strong>admin_provider_config/funding.provider, xixapayBankCode, paymentpointBankCode, squadBeneficiaryAccount</strong>
          <p>Last updated: {formatDate(config.updatedAt)} · Updated by: {String(config.updatedBy ?? "—")}</p>
        </footer>
        {loading && <div className="funding-loading"><Loader2 className="spin" /> Loading funding settings…</div>}
      </div>
    </section>
  );
}

function ReportsPage({ data }: { data: DashboardData }) {
  const [range, setRange] = useState("30");
  const [status, setStatus] = useState("all");
  const deposits = data.transactions.filter(isDepositTransaction);
  const transactions = data.transactions.filter((row) => !isDepositTransaction(row));
  const successful = transactions.filter((row) => ["success", "successful", "completed", "delivered"].includes(String(row.status || "").toLowerCase()));
  const volume = successful.reduce((sum, row) => sum + Number(row.total ?? row.amount ?? 0), 0);
  const successRate = transactions.length ? Math.round(successful.length / transactions.length * 100) : 0;
  const timestamp = (row: Record<string, unknown>) => Number(row.createdAt || row.updatedAt || row.submittedAt || row.sentAt || 0);
  const applyFilters = (rows: Record<string, unknown>[]) => rows.filter((row) => {
    const time = timestamp(row);
    if (range !== "all" && time && time < Date.now() - Number(range) * 86400000) return false;
    const rowStatus = String(row.status || "").toLowerCase();
    if (status !== "all" && rowStatus && rowStatus !== status && !(status === "success" && ["successful", "completed", "delivered", "resolved", "sent"].includes(rowStatus))) return false;
    return true;
  });
  const exportCsv = (name: string, source: Record<string, unknown>[], preferred: string[]) => {
    const rows = applyFilters(source);
    const discovered = new Set(rows.slice(0, 100).flatMap((row) => Object.keys(row).filter((key) => typeof row[key] !== "object")));
    const columns = [...preferred.filter((key) => discovered.has(key)), ...[...discovered].filter((key) => !preferred.includes(key))].slice(0, 16);
    const lines = rows.map((row) => columns.map((column) => {
      const value = row[column];
      if (/amount|total|balance|gross|fee/i.test(column) && typeof value === "number") return csvCell(value / 100);
      if (/at$|date/i.test(column) && typeof value === "number") return csvCell(new Date(value).toISOString());
      return csvCell(value);
    }).join(","));
    const url = URL.createObjectURL(new Blob([[columns.map(humanize).join(","), ...lines].join("\n")], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `oprime-${name}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  const reportCards = [
    { key: "users", title: "Users", Icon: Users, note: "Customer identity, contact, account and KYC records", rows: data.users as unknown as Record<string, unknown>[], columns: ["uid", "userId", "name", "email", "phone", "balance", "kycStatus", "deactivated", "createdAt"] },
    { key: "transactions", title: "Transactions", Icon: ReceiptText, note: "Service purchases, provider status and reconciliation data", rows: transactions as unknown as Record<string, unknown>[], columns: ["txId", "type", "title", "status", "userName", "userEmail", "userPhone", "amount", "provider", "recipient", "createdAt"] },
    { key: "deposits", title: "Deposits", Icon: WalletCards, note: "Wallet funding, sender bank, account and credited amount", rows: deposits as unknown as Record<string, unknown>[], columns: ["depositId", "txId", "status", "userName", "userEmail", "amount", "bankName", "senderName", "senderAccountNumber", "provider", "createdAt"] },
    { key: "audit-logs", title: "Audit Logs", Icon: Activity, note: "Admin actions, reasons and before-to-after changes", rows: data.auditLogs, columns: ["id", "action", "adminName", "adminEmail", "userName", "userEmail", "uid", "txId", "reason", "amount", "statusBefore", "statusAfter", "createdAt"] },
    { key: "disputes", title: "Disputes", Icon: MessageSquare, note: "Customer cases, evidence, resolution and ownership", rows: data.disputes, columns: ["id", "subject", "status", "priority", "userName", "userEmail", "txId", "amount", "adminNote", "createdAt"] },
    { key: "broadcasts", title: "Broadcasts", Icon: Radio, note: "Messages, audiences, delivery results and senders", rows: data.broadcasts, columns: ["id", "title", "message", "status", "audience", "sentByName", "sentByEmail", "deliveredCount", "pushSuccessCount", "pushFailureCount", "createdAt"] },
  ];
  return <div className="reports-page">
    <section className="reports-overview">
      <div><span><ReceiptText /></span><small>Loaded transactions</small><strong>{transactions.length}</strong><p>Service transactions available to report</p></div>
      <div><span><WalletCards /></span><small>Deposit records</small><strong>{deposits.length}</strong><p>Funding entries ready for reconciliation</p></div>
      <div><span><CheckCircle2 /></span><small>Success rate</small><strong>{successRate}%</strong><p>Across the currently loaded transactions</p></div>
      <div><span><Database /></span><small>Successful volume</small><strong>{naira(volume)}</strong><p>Total completed service value</p></div>
    </section>
    <section className="reports-panel">
      <header><div><i /><span><h2>Export Reports</h2><p>Download clean, permission-filtered operational records as CSV.</p></span></div><em><span /> Live data</em></header>
      <div className="reports-filterbar">
        <div><Filter /><span><small>Date range</small><select value={range} onChange={(event) => setRange(event.target.value)}><option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="90">Last 90 days</option><option value="all">All loaded records</option></select></span></div>
        <div><SlidersHorizontal /><span><small>Status</small><select value={status} onChange={(event) => setStatus(event.target.value)}><option value="all">All statuses</option><option value="success">Successful</option><option value="pending">Pending</option><option value="failed">Failed</option><option value="rejected">Rejected</option></select></span></div>
        <p><ShieldCheck /> Exports only include records your admin account is authorized to view.</p>
      </div>
      <div className="report-card-grid">{reportCards.map(({ key, title, Icon, note, rows, columns }) => {
        const count = applyFilters(rows).length;
        return <article key={key}><div className="report-card-icon"><Icon /></div><div className="report-card-copy"><small>CSV report</small><strong>{title}</strong><p>{note}</p><span>{count} matching records</span></div><button disabled={!count} onClick={() => exportCsv(key, rows, columns)}><FileDown /> Export CSV</button></article>;
      })}</div>
    </section>
  </div>;
}

function storedPlanCategory(plan: Record<string, unknown>): string {
  const text = [
    plan.name,
    plan.detailName,
    plan.validity,
    plan.raw_line,
    (plan.raw as Record<string, unknown> | undefined)?.name,
    (plan.raw as Record<string, unknown> | undefined)?.validity,
    (plan.providerRaw as Record<string, unknown> | undefined)?.name,
    (plan.providerRaw as Record<string, unknown> | undefined)?.validity,
  ].filter(Boolean).join(" ").toLowerCase().replaceAll(",", " ");

  if (/\b(?:night|nightlife|midnight|11\s*pm|12\s*am)\b/.test(text)) return "night";
  if (/\b(?:weekend|saturday|sunday)\b/.test(text)) return "weekend";
  if (/\b(?:social|whatsapp|facebook|instagram|tiktok|youtube|snapchat)\b/.test(text)) return "social";

  const durations: number[] = [];
  const durationPattern = /(\d+(?:\.\d+)?)\s*(years?|yrs?|months?|mos?|weeks?|wks?|days?|hours?|hrs?)/gi;
  for (const match of text.matchAll(durationPattern)) {
    const value = Number(match[1]);
    const unit = match[2].toLowerCase();
    if (!Number.isFinite(value) || value <= 0) continue;
    if (unit.startsWith("year") || unit.startsWith("yr")) durations.push(value * 365);
    else if (unit.startsWith("month") || unit.startsWith("mo")) durations.push(value * 30);
    else if (unit.startsWith("week") || unit.startsWith("wk")) durations.push(value * 7);
    else if (unit.startsWith("hour") || unit.startsWith("hr")) durations.push(value / 24);
    else durations.push(value);
  }

  if (/\b(?:annual|yearly)\b/.test(text)) return "yearly";
  if (/\bmonthly\b/.test(text)) return "monthly";
  if (/\bweekly\b/.test(text)) return "weekly";
  if (/\bdaily\b/.test(text)) return "daily";
  if (!durations.length) return "others";

  const days = Math.max(...durations);
  if (days >= 365) return "yearly";
  if (days >= 30) return "monthly";
  if (days >= 7) return "weekly";
  return "daily";
}

function DataPlansManager() {
  const networks = ["mtn", "airtel", "glo", "9mobile"];
  const categories = [
    "hot",
    "daily",
    "weekly",
    "monthly",
    "night",
    "social",
    "weekend",
    "yearly",
    "others",
  ];
  const providers = ["vtpass", "lizzysub", "datareloaded", "paypoint", "gladtidings", "misvtu", "combined"];
  const combinableProviders = ["vtpass", "lizzysub", "datareloaded", "paypoint", "gladtidings", "misvtu"];
  const [network, setNetwork] = useState("mtn");
  const [category, setCategory] = useState("hot");
  const [provider, setProvider] = useState("vtpass");
  const [enabledOnly, setEnabledOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Record<string, unknown> | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importProgress, setImportProgress] = useState("");
  const [categoryImportBusy, setCategoryImportBusy] = useState(false);
  const [combineOpen, setCombineOpen] = useState(false);
  const [combineCandidates, setCombineCandidates] = useState<Record<string, unknown>[]>([]);
  const [combineLoading, setCombineLoading] = useState(false);
  const [combineForm, setCombineForm] = useState({ name: "", detailName: "", price: "", order: "0", category: "monthly", isHot: false });
  const [combineComponents, setCombineComponents] = useState<Array<{ provider: string; planKey: string }>>([
    { provider: "", planKey: "" },
    { provider: "", planKey: "" },
  ]);
  const [editForm, setEditForm] = useState({
    name: "",
    detailName: "",
    price: "",
    order: "0",
    category: "hot",
    isHot: false,
  });

  useEffect(() => {
    const sourceCategories = category === "hot"
      ? categories.filter((item) => item !== "hot")
      : [category];
    const rowsByCategory = new Map<string, Record<string, unknown>[]>();
    const publishRows = () => {
      const merged = sourceCategories.flatMap((sourceCategory) => rowsByCategory.get(sourceCategory) || []);
      setRows(category === "hot"
        ? merged.filter((row) => row.isHot === true || row.is_hot === true || row.hot === true)
        : merged);
      setError("");
    };
    const unsubscribers = sourceCategories.map((sourceCategory) =>
      onSnapshot(
        collection(db, "admin_dataplans", network, "categories", sourceCategory, "plans"),
        (snapshot) => {
          rowsByCategory.set(
            sourceCategory,
            snapshot.docs.map((item) => ({
              id: item.id,
              ...item.data(),
              _sourceCategory: sourceCategory,
            })),
          );
          publishRows();
        },
        (failure) => setError(failure.message),
      ),
    );
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [network, category]);

  const visible = useMemo(
    () =>
      rows
        .filter((row) => row.provider === provider)
        .filter((row) => !enabledOnly || row.enabled !== false)
        .filter((row) =>
          `${row.name} ${row.detailName} ${row.code} ${row.id}`
            .toLowerCase()
            .includes(search.toLowerCase()),
        )
        .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0)),
    [rows, provider, enabledOnly, search],
  );

  const planCategory = (row: Record<string, unknown>) =>
    String(row._sourceCategory ?? row.category ?? (category === "hot" ? "others" : category));
  const planRef = (row: Record<string, unknown>) =>
    doc(
      db,
      "admin_dataplans",
      network,
      "categories",
      planCategory(row),
      "plans",
      String(row.id),
    );
  const edit = (row: Record<string, unknown>) => {
    setEditingPlan(row);
    setEditForm({
      name: String(row.name ?? ""),
      detailName: String(row.detailName ?? row.raw_line ?? ""),
      price: String(row.price ?? row.amount ?? 0),
      order: String(row.order ?? 0),
      category: planCategory(row),
      isHot: row.isHot === true || row.is_hot === true || row.hot === true,
    });
  };
  const saveEdit = async () => {
    if (!editingPlan) return;
    const price = Number(editForm.price.replace(/,/g, ""));
    const order = Number(editForm.order);
    if (!editForm.name.trim() || !Number.isFinite(price)) {
      setError("Enter a plan name and a valid selling price.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
          name: editForm.name.trim(),
          detailName: editForm.detailName.trim(),
          raw_line: editForm.detailName.trim(),
          price,
          order: Number.isFinite(order) ? order : 0,
          category: editForm.category,
          vtCategory: editForm.category,
          isHot: editForm.isHot,
          updatedAt: serverTimestamp(),
      };
      const sourceCategory = planCategory(editingPlan);
      if (editForm.category === sourceCategory) {
        await setDoc(planRef(editingPlan), payload, { merge: true });
      } else {
        const destination = doc(db, "admin_dataplans", network, "categories", editForm.category, "plans", String(editingPlan.id));
        const existingData = { ...editingPlan };
        delete existingData.id;
        await setDoc(destination, { ...existingData, ...payload }, { merge: true });
        await deleteDoc(planRef(editingPlan));
      }
      setEditingPlan(null);
      if (category !== "hot") setCategory(editForm.category);
      setError("");
    } catch (failure) {
      setError(
        failure instanceof Error ? failure.message : "Could not update plan",
      );
    } finally {
      setBusy(false);
    }
  };
  const toggle = async (row: Record<string, unknown>) => {
    try {
      await updateDoc(planRef(row), {
        enabled: row.enabled === false,
        updatedAt: serverTimestamp(),
      });
    } catch (failure) {
      setError(
        failure instanceof Error ? failure.message : "Could not update plan",
      );
    }
  };
  const toggleHot = async (row: Record<string, unknown>) => {
    try {
      await updateDoc(planRef(row), {
        isHot: !(row.isHot === true || row.is_hot === true || row.hot === true),
        updatedAt: serverTimestamp(),
      });
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not update plan");
    }
  };
  const importLizzysubPlans = async () => {
    const sourceLines = importText
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    const logicalLines: string[] = [];
    for (let index = 0; index < sourceLines.length; index += 1) {
      const line = sourceLines[index];
      // LizzySub's copied table can place the numeric ID on its own line.
      // Join it to the following row so users can paste the table unchanged.
      if (/^\d+$/.test(line) && sourceLines[index + 1]) {
        logicalLines.push(`${line}\t${sourceLines[index + 1]}`);
        index += 1;
      } else {
        logicalLines.push(line);
      }
    }
    const parsed = logicalLines
      .map((line) => {
        const raw = line.trim();
        const columns = raw.split(/\t+/).map((value) => value.trim()).filter(Boolean);
        let code = "";
        let rawNetwork = "";
        let rawType = "";
        let planName = "";
        let rawAmount = "";
        let validity = "";
        if (columns.length >= 6 && /^(MTN|AIRTEL|GLO|9MOBILE)$/i.test(columns[1])) {
          [code, rawNetwork, rawType, planName] = columns;
          rawAmount = columns[4].replace(/^₦\s*/, "");
          validity = columns.slice(5).join(" ");
        } else {
          const match = raw.match(/^(\S+)\s+(MTN|AIRTEL|GLO|9MOBILE)\s+(\S+)\s+(.+?)\s+₦\s*([\d,]+(?:\.\d+)?)\s*(.*)$/i);
          if (!match) return null;
          [, code, rawNetwork, rawType, planName, rawAmount, validity] = match;
        }
        const planNetwork = rawNetwork.toLowerCase();
        const planType = rawType.replaceAll("_", " ");
        const description = `${planName} ${planType} ${validity}`.trim();
        const lowered = description.toLowerCase();
        const guessedCategory = lowered.includes("night") || lowered.includes("midnight")
          ? "night"
          : lowered.includes("weekend")
            ? "weekend"
            : /social|whatsapp|facebook|instagram|tiktok/.test(lowered)
              ? "social"
              : /year|365/.test(lowered)
                ? "yearly"
                : /week|7\s*days?/.test(lowered)
                  ? "weekly"
                  : /month|30\s*days?/.test(lowered)
                    ? "monthly"
                    : /day|24\s*(?:hr|hour)/.test(lowered)
                      ? "daily"
                      : "others";
        return {
          code,
          network: planNetwork,
          planType,
          name: `${planName} ${planType}`.trim(),
          detailName: validity.trim(),
          validity: validity.trim(),
          amount: Number(rawAmount.replaceAll(",", "")),
          category: guessedCategory,
          raw_line: raw,
        };
      })
      .filter((plan): plan is NonNullable<typeof plan> => Boolean(plan));

    if (!parsed.length) {
      setError("No valid LizzySub plans were found. Paste the same line format used in lizzysub.dart.");
      return;
    }

    setBusy(true);
    setError("");
    setImportProgress(`Checking ${parsed.length} parsed plans…`);
    try {
      // Keep the unedited provider catalogue as the source for the floating
      // category import button. This is separate from the admin-managed plan
      // documents, whose names, prices and placement may have been changed.
      await setDoc(doc(db, "admin_dataplans", "_lizzysub_import_source"), {
        provider: "lizzysub",
        plans: parsed,
        count: parsed.length,
        updatedAt: serverTimestamp(),
      }, { merge: true });

      const existing = new Map<string, { category: string; data: Record<string, unknown> }>();
      const catalogueSnapshots = await Promise.all(
        networks.flatMap((planNetwork) =>
          categories.map(async (planCategory) => ({
            planNetwork,
            planCategory,
            snapshot: await getDocs(collection(db, "admin_dataplans", planNetwork, "categories", planCategory, "plans")),
          })),
        ),
      );
      catalogueSnapshots.forEach(({ planNetwork, planCategory, snapshot }) => {
        snapshot.docs.forEach((item) => {
            if (item.data().provider === "lizzysub") {
              existing.set(`${planNetwork}:${item.id}`, {
                category: planCategory,
                data: item.data(),
              });
            }
        });
      });

      let batch = writeBatch(db);
      let operations = 0;
      let updated = 0;
      let created = 0;
      let moved = 0;
      const commit = async () => {
        if (!operations) return;
        setImportProgress(`Saving plans… ${updated} updated, ${created} added`);
        await batch.commit();
        batch = writeBatch(db);
        operations = 0;
      };
      for (const plan of parsed) {
        const current = existing.get(`${plan.network}:${plan.code}`);
        // Existing plans stay where the admin placed them. The imported
        // category is only used for genuinely new plans.
        const targetCategory = current?.category || plan.category;
        const target = doc(db, "admin_dataplans", plan.network, "categories", targetCategory, "plans", plan.code);
        const importedFields = {
          ...plan,
          category: targetCategory,
          vtCategory: targetCategory,
          provider: "lizzysub",
          providerPlanCode: plan.code,
          order: Number(plan.code) || 0,
          updatedAt: serverTimestamp(),
        };
        if (current) {
          batch.set(target, {
            ...importedFields,
            // Preserve every admin-managed field on an existing plan.
            name: current.data.name ?? plan.name,
            detailName: current.data.detailName ?? plan.detailName,
            raw_line: current.data.raw_line ?? plan.raw_line,
            price: current.data.price ?? plan.amount,
            order: current.data.order ?? (Number(plan.code) || 0),
            category: current.category,
            vtCategory: current.category,
            enabled: current.data.enabled === true,
            isHot: current.data.isHot === true,
            is_hot: current.data.is_hot === true,
            hot: current.data.hot === true,
          }, { merge: true });
          operations += 1;
          updated += 1;
        } else {
          batch.set(target, {
            ...importedFields,
            price: plan.amount,
            enabled: false,
            createdAt: serverTimestamp(),
          }, { merge: true });
          operations += 1;
          created += 1;
        }
        if (operations >= 398) await commit();
      }
      await commit();
      setProvider("lizzysub");
      setImportOpen(false);
      setImportText("");
      showFlushbar(
        `LizzySub catalogue imported: ${updated} updated, ${created} added${moved ? `, ${moved} moved to updated categories` : ""}. Existing enabled states were preserved.`,
      );
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not import plans");
    } finally {
      setBusy(false);
      setImportProgress("");
    }
  };
  const importPayPointPlans = async () => {
    const sourceLines = importText.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    const parsed = sourceLines.map((line) => {
      const columns = line.split(/\t+/).map((value) => value.trim()).filter(Boolean);
      let planSize = "";
      let planId = "";
      let rawAmount = "";
      let validity = "";
      if (columns.length >= 4 && !/plan.?size/i.test(columns[0])) {
        [planSize, planId, rawAmount] = columns;
        validity = columns.slice(3).join(" ");
      } else {
        const match = line.match(/^(\S+)\s+(\S*(?:MTN|AIRTEL|GLO|9MOBILE)\S*)\s+₦?\s*([\d,]+(?:\.\d+)?)\s+(.+)$/i);
        if (!match) return null;
        [, planSize, planId, rawAmount, validity] = match;
      }
      const upperId = planId.toUpperCase();
      const planNetwork = upperId.includes("9MOBILE")
        ? "9mobile"
        : upperId.includes("AIRTEL")
          ? "airtel"
          : upperId.includes("GLO")
            ? "glo"
            : upperId.includes("MTN")
              ? "mtn"
              : "";
      const amount = Number(rawAmount.replace(/[₦,\s]/g, ""));
      if (!planNetwork || !planId || !planSize || !Number.isFinite(amount) || amount <= 0) return null;
      const typeTokens = upperId
        .split(/[_-]+/)
        .filter((token) => token && token !== planSize.toUpperCase() && !["MTN", "AIRTEL", "GLO", "9MOBILE"].includes(token));
      const planType = typeTokens.join(" ").replace(/\bDG\b/g, "DIRECT GIFTING").replace(/\bCG\b/g, "CORPORATE GIFTING");
      const description = `${validity} ${planType}`.trim();
      return {
        code: `paypoint_${planId}`,
        providerPlanCode: planId,
        network: planNetwork,
        name: `${planSize} ${planType}`.trim(),
        detailName: validity,
        validity,
        amount,
        category: storedPlanCategory({ name: planSize, detailName: description }),
        raw_line: line,
      };
    }).filter((plan): plan is NonNullable<typeof plan> => Boolean(plan));

    if (!parsed.length) {
      setError("No valid PayPoint plans were found. Copy the pricing table with Plan Size, Plan_id, API Price and Validity.");
      return;
    }

    setBusy(true);
    setError("");
    setImportProgress(`Checking ${parsed.length} PayPoint plans…`);
    try {
      await setDoc(doc(db, "admin_dataplans", "_paypoint_import_source"), {
        provider: "paypoint", plans: parsed, count: parsed.length, updatedAt: serverTimestamp(),
      }, { merge: true });
      const catalogueSnapshots = await Promise.all(networks.flatMap((planNetwork) =>
        categories.map(async (planCategory) => ({
          planNetwork, planCategory,
          snapshot: await getDocs(collection(db, "admin_dataplans", planNetwork, "categories", planCategory, "plans")),
        })),
      ));
      const existing = new Map<string, { category: string; data: Record<string, unknown> }>();
      catalogueSnapshots.forEach(({ planNetwork, planCategory, snapshot }) => snapshot.docs.forEach((item) => {
        if (item.data().provider === "paypoint") existing.set(`${planNetwork}:${item.id}`, { category: planCategory, data: item.data() });
      }));

      let batch = writeBatch(db);
      let operations = 0;
      let updated = 0;
      let created = 0;
      const commit = async () => {
        if (!operations) return;
        setImportProgress(`Saving PayPoint plans… ${updated} updated, ${created} added`);
        await batch.commit();
        batch = writeBatch(db);
        operations = 0;
      };
      for (const plan of parsed) {
        const current = existing.get(`${plan.network}:${plan.code}`);
        const targetCategory = current?.category || plan.category;
        const target = doc(db, "admin_dataplans", plan.network, "categories", targetCategory, "plans", plan.code);
        const importedFields = {
          ...plan, category: targetCategory, vtCategory: targetCategory, provider: "paypoint",
          order: 0, updatedAt: serverTimestamp(), providerRaw: { plan_id: plan.providerPlanCode, plan_size: plan.name, api_price: plan.amount, validity: plan.validity },
        };
        if (current) {
          batch.set(target, {
            ...importedFields,
            name: current.data.name ?? plan.name,
            detailName: current.data.detailName ?? plan.detailName,
            raw_line: current.data.raw_line ?? plan.raw_line,
            price: current.data.price ?? plan.amount,
            order: current.data.order ?? 0,
            category: current.category,
            vtCategory: current.category,
            enabled: current.data.enabled === true,
            isHot: current.data.isHot === true,
            is_hot: current.data.is_hot === true,
            hot: current.data.hot === true,
          }, { merge: true });
          updated += 1;
        } else {
          batch.set(target, { ...importedFields, price: plan.amount, enabled: false, createdAt: serverTimestamp() }, { merge: true });
          created += 1;
        }
        operations += 1;
        if (operations >= 398) await commit();
      }
      await commit();
      setProvider("paypoint");
      setImportOpen(false);
      setImportText("");
      showFlushbar(`PayPoint catalogue imported: ${updated} updated, ${created} added. Existing admin edits and enabled states were preserved.`);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not import PayPoint plans");
    } finally {
      setBusy(false);
      setImportProgress("");
    }
  };
  const importMisvtuPlans = async () => {
    const sourceLines = importText.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    const logicalLines: string[] = [];
    for (let index = 0; index < sourceLines.length; index += 1) {
      const line = sourceLines[index];
      const nextLine = sourceLines[index + 1] ?? "";
      // Copying the rendered MISVTU table from Safari puts every cell on its
      // own line: ID, network, type, name, amount and validity. Reassemble
      // those six cells before passing the row through the normal parser.
      if (
        /^\d+$/.test(line) &&
        /^(MTN|AIRTEL|GLO|9MOBILE)$/i.test(nextLine) &&
        sourceLines.length > index + 5
      ) {
        logicalLines.push(sourceLines.slice(index, index + 6).join("\t"));
        index += 5;
      } else if (
        /^\d+$/.test(line) &&
        /^(MTN|AIRTEL|GLO|9MOBILE)(?:\t|\s{2,})/i.test(nextLine)
      ) {
        // Some browsers put the ID on one line and the remaining table cells
        // on the next line. Keep supporting that clipboard layout as well.
        logicalLines.push(`${line}\t${nextLine}`);
        index += 1;
      } else {
        logicalLines.push(line);
      }
    }
    const parsed = logicalLines.map((line) => {
      const columns = line.split(/\t+/).map((value) => value.trim()).filter(Boolean);
      let planId = "";
      let rawNetwork = "";
      let planType = "";
      let planName = "";
      let rawAmount = "";
      let validity = "";
      if (columns.length >= 6 && /^\d+$/.test(columns[0]) && /^(MTN|AIRTEL|GLO|9MOBILE)$/i.test(columns[1])) {
        [planId, rawNetwork, planType, planName, rawAmount] = columns;
        validity = columns.slice(5).join(" ");
      } else {
        const match = line.match(/^(\d+)\s+(MTN|AIRTEL|GLO|9MOBILE)\s+(.+?)\s+(\d+(?:\.\d+)?(?:MB|GB|TB))\s+₦?\s*([\d,]+(?:\.\d+)?)\s+(.+)$/i);
        if (!match) return null;
        [, planId, rawNetwork, planType, planName, rawAmount, validity] = match;
      }
      const amount = Number(rawAmount.replace(/[₦,\s]/g, ""));
      const planNetwork = rawNetwork.toLowerCase();
      if (!planId || !Number.isFinite(amount) || amount <= 0) return null;
      const normalizedType = planType.replaceAll("_", " ").trim();
      const code = `misvtu_${planId}`;
      return {
        code,
        // Keep the provider prefix in the app-facing code so already-installed
        // clients route MISVTU safely through the generic buyData callable.
        providerPlanCode: code,
        nativePlanId: planId,
        network: planNetwork,
        planType: normalizedType,
        name: `${planName} ${normalizedType}`.trim(),
        detailName: validity.trim(),
        validity: validity.trim(),
        amount,
        category: storedPlanCategory({ name: planName, detailName: `${normalizedType} ${validity}` }),
        raw_line: line,
      };
    }).filter((plan): plan is NonNullable<typeof plan> => Boolean(plan));

    if (!parsed.length) {
      setError("No valid MISVTU plans were found. Paste the table containing Plan ID, Network, Plan Type, Plan Name, Amount and Validity.");
      return;
    }

    setBusy(true);
    setError("");
    setImportProgress(`Checking ${parsed.length} MISVTU plans…`);
    try {
      await setDoc(doc(db, "admin_dataplans", "_misvtu_import_source"), {
        provider: "misvtu", plans: parsed, count: parsed.length, updatedAt: serverTimestamp(),
      }, { merge: true });
      const catalogueSnapshots = await Promise.all(networks.flatMap((planNetwork) =>
        categories.filter((item) => item !== "hot").map(async (planCategory) => ({
          planNetwork, planCategory,
          snapshot: await getDocs(collection(db, "admin_dataplans", planNetwork, "categories", planCategory, "plans")),
        })),
      ));
      const existing = new Map<string, { category: string; data: Record<string, unknown> }>();
      catalogueSnapshots.forEach(({ planNetwork, planCategory, snapshot }) => snapshot.docs.forEach((item) => {
        if (item.data().provider === "misvtu") existing.set(`${planNetwork}:${item.id}`, { category: planCategory, data: item.data() });
      }));

      let batch = writeBatch(db);
      let operations = 0;
      let updated = 0;
      let created = 0;
      const commit = async () => {
        if (!operations) return;
        setImportProgress(`Saving MISVTU plans… ${updated} updated, ${created} added`);
        await batch.commit();
        batch = writeBatch(db);
        operations = 0;
      };
      for (const plan of parsed) {
        const current = existing.get(`${plan.network}:${plan.code}`);
        const targetCategory = current?.category || plan.category;
        const target = doc(db, "admin_dataplans", plan.network, "categories", targetCategory, "plans", plan.code);
        const importedFields = {
          ...plan, category: targetCategory, vtCategory: targetCategory, provider: "misvtu",
          order: Number(plan.nativePlanId) || 0, updatedAt: serverTimestamp(),
          providerRaw: { id: plan.nativePlanId, network: plan.network, plan_type: plan.planType, name: plan.name, amount: plan.amount, validity: plan.validity },
        };
        if (current) {
          batch.set(target, {
            ...importedFields,
            name: current.data.name ?? plan.name,
            detailName: current.data.detailName ?? plan.detailName,
            raw_line: current.data.raw_line ?? plan.raw_line,
            price: current.data.price ?? plan.amount,
            order: current.data.order ?? (Number(plan.nativePlanId) || 0),
            category: current.category,
            vtCategory: current.category,
            enabled: current.data.enabled === true,
            isHot: current.data.isHot === true,
            is_hot: current.data.is_hot === true,
            hot: current.data.hot === true,
          }, { merge: true });
          updated += 1;
        } else {
          batch.set(target, { ...importedFields, price: plan.amount, enabled: false, createdAt: serverTimestamp() }, { merge: true });
          created += 1;
        }
        operations += 1;
        if (operations >= 398) await commit();
      }
      await commit();
      setProvider("misvtu");
      setImportOpen(false);
      setImportText("");
      showFlushbar(`MISVTU catalogue imported: ${updated} updated, ${created} added. Existing admin edits and enabled states were preserved.`);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not import MISVTU plans");
    } finally {
      setBusy(false);
      setImportProgress("");
    }
  };

  const importActiveCategory = async () => {
    if (category === "hot" || categoryImportBusy) return;
    if (provider === "lizzysub" || provider === "paypoint" || provider === "misvtu") {
      // Providers without a documented live catalogue endpoint are sourced
      // from the last full catalogue pasted by an admin.
      const sourceProvider = provider;
      const providerLabel = sourceProvider === "lizzysub" ? "LizzySub" : sourceProvider === "misvtu" ? "MISVTU" : "PayPoint";
      setCategoryImportBusy(true);
      setError("");
      try {
        const sourceSnapshot = await getDoc(doc(db, "admin_dataplans", `_${sourceProvider}_import_source`));
        let sourcePlans = sourceSnapshot.exists() && Array.isArray(sourceSnapshot.data().plans)
          ? sourceSnapshot.data().plans as Record<string, unknown>[]
          : [];

        const catalogueSnapshots = await Promise.all(
          categories.map(async (planCategory) => ({
            planCategory,
            snapshot: await getDocs(collection(db, "admin_dataplans", network, "categories", planCategory, "plans")),
          })),
        );

        // Catalogues pasted before this feature was deployed have no saved
        // source document. Existing provider records are a safe fallback.
        if (!sourcePlans.length) {
          sourcePlans = catalogueSnapshots.flatMap(({ planCategory, snapshot }) =>
            snapshot.docs
              .filter((item) => item.data().provider === sourceProvider)
              .map((item) => ({
                ...item.data(),
                code: String(item.data().code ?? item.id),
                network,
                category: String(item.data().category ?? planCategory),
              })),
          );
        }

        const selectedPlans = sourcePlans.filter((plan) =>
          String(plan.network ?? "").toLowerCase() === network
          && String(plan.category ?? "others").toLowerCase() === category,
        );
        if (!selectedPlans.length) {
          throw new Error(
            `No ${network.toUpperCase()} ${humanize(category).toLowerCase()} plans were found in the saved ${providerLabel} catalogue. Import the latest full ${providerLabel} list first.`,
          );
        }

        const existing = new Map<string, { category: string; data: Record<string, unknown> }>();
        catalogueSnapshots.forEach(({ planCategory, snapshot }) => {
          snapshot.docs.forEach((item) => {
            if (item.data().provider === sourceProvider) {
              existing.set(item.id, { category: planCategory, data: item.data() });
            }
          });
        });

        let batch = writeBatch(db);
        let operations = 0;
        let updated = 0;
        let created = 0;
        const commit = async () => {
          if (!operations) return;
          await batch.commit();
          batch = writeBatch(db);
          operations = 0;
        };
        for (const plan of selectedPlans) {
          const code = String(plan.code ?? plan.providerPlanCode ?? "").trim();
          if (!code) continue;
          const current = existing.get(code);
          // Respect an admin's category change for an existing plan. Only new
          // plans are placed in the currently selected source category.
          const targetCategory = current?.category ?? category;
          const target = doc(db, "admin_dataplans", network, "categories", targetCategory, "plans", code);
          const amount = Number(plan.amount ?? 0);
          const importedFields = {
            ...plan,
            code,
            network,
            category: targetCategory,
            vtCategory: targetCategory,
            provider: sourceProvider,
            providerPlanCode: String(plan.providerPlanCode ?? code),
            amount,
            updatedAt: serverTimestamp(),
          };
          if (current) {
            batch.set(target, {
              ...importedFields,
              name: current.data.name ?? plan.name,
              detailName: current.data.detailName ?? plan.detailName,
              raw_line: current.data.raw_line ?? plan.raw_line,
              price: current.data.price ?? amount,
              order: current.data.order ?? (Number(code) || 0),
              category: current.category,
              vtCategory: current.category,
              enabled: current.data.enabled === true,
              isHot: current.data.isHot === true,
              is_hot: current.data.is_hot === true,
              hot: current.data.hot === true,
            }, { merge: true });
            updated += 1;
          } else {
            batch.set(target, {
              ...importedFields,
              price: amount,
              order: Number(plan.order ?? code) || 0,
              enabled: false,
              createdAt: serverTimestamp(),
            }, { merge: true });
            created += 1;
          }
          operations += 1;
          if (operations >= 398) await commit();
        }
        await commit();
        showFlushbar(
          `Imported ${selectedPlans.length} saved ${providerLabel} ${humanize(category).toLowerCase()} plans: ${updated} refreshed, ${created} added. Existing edits and enabled states were preserved.`,
        );
      } catch (failure) {
        setError(failure instanceof Error ? failure.message : `Could not import saved ${providerLabel} plans`);
      } finally {
        setCategoryImportBusy(false);
      }
      return;
    }
    setCategoryImportBusy(true);
    setError("");
    try {
      const importCategory = httpsCallable(functions, "adminImportDataPlanCategory", { timeout: 180000 });
      const importedResult = await importCategory({ network, category, provider });
      const importedData = (importedResult.data || {}) as Record<string, unknown>;
      const importedCount = Number(importedData.imported || 0);
      const createdCount = Number(importedData.created || 0);
      const snapshots = await Promise.all(
        categories.map(async (sourceCategory) => ({
            sourceCategory,
            snapshot: await getDocs(collection(db, "admin_dataplans", network, "categories", sourceCategory, "plans")),
          })),
      );
      const targetIds = new Set(
        snapshots
          .find((entry) => entry.sourceCategory === category)
          ?.snapshot.docs
          .filter((item) => item.data().provider === provider)
          .map((item) => item.id) ?? [],
      );
      const matching = snapshots.flatMap(({ sourceCategory, snapshot }) =>
        snapshot.docs
          .filter((item) => sourceCategory !== category)
          .filter((item) => item.data().provider === provider)
          .filter((item) => storedPlanCategory(item.data()) === category)
          .map((item) => ({ sourceCategory, item })),
      );

      let batch = writeBatch(db);
      let operations = 0;
      let moved = 0;
      let duplicatesRemoved = 0;
      const commit = async () => {
        if (!operations) return;
        await batch.commit();
        batch = writeBatch(db);
        operations = 0;
      };

      for (const { item } of matching) {
        if (targetIds.has(item.id)) {
          // The target copy may contain admin edits, so never overwrite it.
          batch.delete(item.ref);
          operations += 1;
          duplicatesRemoved += 1;
        } else {
          const destination = doc(db, "admin_dataplans", network, "categories", category, "plans", item.id);
          batch.set(destination, {
            ...item.data(),
            category,
            vtCategory: category,
          });
          batch.delete(item.ref);
          operations += 2;
          moved += 1;
          targetIds.add(item.id);
        }
        if (operations >= 398) await commit();
      }
      await commit();

      showFlushbar(
        moved || duplicatesRemoved
          ? `Synced ${importedCount} live plans (${createdCount} new), placed ${moved} in ${humanize(category)}${duplicatesRemoved ? ` and removed ${duplicatesRemoved} duplicate${duplicatesRemoved === 1 ? "" : "s"}` : ""}. Existing enabled states were preserved.`
          : `Synced ${importedCount} live ${provider.toUpperCase()} ${humanize(category).toLowerCase()} plans (${createdCount} new). Existing enabled states were preserved.`,
      );
    } catch (failure) {
      setError(
        failure instanceof Error ? failure.message : "Could not import this category",
      );
    } finally {
      setCategoryImportBusy(false);
    }
  };
  const remove = async (row: Record<string, unknown>) => {
    if (!await confirmAction({ title: "Delete data plan?", message: `${row.name ?? "This plan"} will be permanently removed from the catalogue.`, confirmLabel: "Delete plan", tone: "danger" })) return;
    try {
      await deleteDoc(planRef(row));
    } catch (failure) {
      setError(
        failure instanceof Error ? failure.message : "Could not delete plan",
      );
    }
  };

  const openCombineBuilder = async () => {
    setCombineOpen(true);
    setCombineLoading(true);
    setError("");
    setCombineComponents([
      { provider: "", planKey: "" },
      { provider: "", planKey: "" },
    ]);
    try {
      // Source components may live in any category. The active category only
      // controls where the newly created combined plan is saved.
      const sourceCategories = categories.filter((item) => item !== "hot");
      const snapshots = await Promise.all(sourceCategories.map(async (sourceCategory) => ({
        sourceCategory,
        snapshot: await getDocs(collection(db, "admin_dataplans", network, "categories", sourceCategory, "plans")),
      })));
      const candidates: Record<string, unknown>[] = snapshots.flatMap(({ sourceCategory, snapshot }) => snapshot.docs
        .filter((item) => combinableProviders.includes(String(item.data().provider).toLowerCase()))
        .map((item) => ({ id: item.id, ...item.data(), _sourceCategory: sourceCategory }) as Record<string, unknown>));
      setCombineCandidates(candidates.sort((a, b) => String(a.provider).localeCompare(String(b.provider)) || String(a.name).localeCompare(String(b.name))));
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not load component plans");
      setCombineOpen(false);
    } finally {
      setCombineLoading(false);
    }
  };

  const saveCombinedPlan = async () => {
    const selectedKeys = combineComponents.map((item) => item.planKey).filter(Boolean);
    const selected = selectedKeys.map((key) => combineCandidates.find((item) => `${String(item.provider).toLowerCase()}:${String(item._sourceCategory)}:${String(item.id)}` === key)).filter(Boolean) as Record<string, unknown>[];
    const price = Number(combineForm.price.replace(/,/g, ""));
    const order = Number(combineForm.order);
    if (!combineForm.name.trim() || !combineForm.detailName.trim() || !Number.isFinite(price) || price <= 0 || selected.length < 2) {
      setError("Enter the short name, detail name, a valid selling price, and choose at least two component plans.");
      return;
    }
    setBusy(true);
    try {
      const id = `combined_${Date.now()}`;
      const components = selected.map((item, index) => ({
        order: index + 1,
        planId: String(item.id),
        category: String(item._sourceCategory),
        provider: String(item.provider),
        providerPlanCode: String(item.providerPlanCode ?? item.code ?? item.id),
        name: String(item.name ?? item.id),
        providerAmount: Number(item.amount ?? 0),
      }));
      await setDoc(doc(db, "admin_dataplans", network, "categories", combineForm.category, "plans", id), {
        code: id,
        providerPlanCode: id,
        provider: "combined",
        network,
        name: combineForm.name.trim(),
        detailName: combineForm.detailName.trim(),
        raw_line: combineForm.detailName.trim(),
        price,
        amount: components.reduce((sum, item) => sum + item.providerAmount, 0),
        order: Number.isFinite(order) ? order : 0,
        category: combineForm.category,
        vtCategory: combineForm.category,
        isHot: combineForm.isHot,
        enabled: false,
        components,
        componentCount: components.length,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setProvider("combined");
      setCategory(combineForm.category);
      setCombineOpen(false);
      setCombineForm({ name: "", detailName: "", price: "", order: "0", category: "monthly", isHot: false });
      setCombineComponents([{ provider: "", planKey: "" }, { provider: "", planKey: "" }]);
      showFlushbar("Combined plan created disabled. Review it, then enable it when ready to sell.");
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not create combined plan");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="data-plans-manager">
      <div className="data-plans-heading">
        <div>
          <span className="data-plans-eyebrow">Catalogue management</span>
          <h2>Manage Data Plans</h2>
          <p>Provider pricing, availability and catalogue management</p>
        </div>
        <div className="data-plans-heading-actions">
          <button className="action-button" onClick={() => void openCombineBuilder()}><Link2 /> Join plans</button>
          {(provider === "lizzysub" || provider === "paypoint" || provider === "misvtu") && (
            <button className="action-button" onClick={() => setImportOpen(true)}>
              <ArrowDownToLine /> Import {provider === "paypoint" ? "PayPoint" : provider === "misvtu" ? "MISVTU" : "LizzySub"} list
            </button>
          )}
        </div>
      </div>
      <div className="data-plans-controls">
        <div className="plan-filter-group">
          <small>Network</small>
          <div className="plan-segments">
            {networks.map((item) => <button key={item} className={network === item ? "active" : ""} onClick={() => setNetwork(item)}>{item.toUpperCase()}</button>)}
          </div>
        </div>
        <div className="plan-filter-group">
          <small>Provider</small>
          <div className="plan-segments provider-segments">
            {providers.map((item) => <button key={item} className={provider === item ? "active" : ""} onClick={() => setProvider(item)}>{item.toUpperCase()}</button>)}
          </div>
        </div>
        <div className="search">
          <Search />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search plans"
          />
        </div>
        <label className="only-enabled">
          <input
            type="checkbox"
            checked={enabledOnly}
            onChange={(e) => setEnabledOnly(e.target.checked)}
          />{" "}
          Enabled only
        </label>
      </div>
      <div className="plan-category-tabs" role="tablist">
        {categories.map((item) => <button role="tab" aria-selected={category === item} key={item} className={category === item ? "active" : ""} onClick={() => setCategory(item)}>{humanize(item)}</button>)}
      </div>
      {error && <div className="error banner">{error}</div>}
      <div className="plan-results-heading">
        <div><strong>{humanize(category)} plans</strong><span>{visible.length} plan{visible.length === 1 ? "" : "s"} · {provider.toUpperCase()} · {network.toUpperCase()}</span></div>
      </div>
      <div className="data-plan-grid">
        {visible.map((row) => {
          const enabled = row.enabled !== false;
          const hot = row.isHot === true || row.is_hot === true || row.hot === true;
          return <article className={`data-plan-card ${enabled ? "enabled" : "disabled"}`} key={`${String(row._sourceCategory ?? category)}:${String(row.id)}`}>
            <div className="data-plan-accent" />
            <div className="data-plan-info">
              <div className="data-plan-name"><strong>{String(row.name ?? "Unnamed plan")}</strong>{hot && <span>🔥 HOT</span>}</div>
              <p>{String(row.detailName ?? row.raw_line ?? row.validity ?? "")}</p>
              <div className="data-plan-prices"><span>Provider base <b>{money.format(Number(row.amount ?? 0))}</b></span><span>Your price <b>{money.format(Number(row.price ?? row.amount ?? 0))}</b></span><span className="mono">Code {String(row.code ?? row.id)}</span></div>
            </div>
            <div className="data-plan-actions">
              <button className={`plan-switch ${enabled ? "on" : ""}`} aria-label={enabled ? "Disable plan" : "Enable plan"} onClick={() => void toggle(row)}><i /></button>
              <button onClick={() => edit(row)}>Edit</button>
              <button onClick={() => void toggleHot(row)}>{hot ? "Unmark hot" : "Mark hot"}</button>
              <button className="danger" onClick={() => void remove(row)}>Delete</button>
            </div>
          </article>;
        })}
        {!visible.length && (
          <div className="empty plan-empty"><Search /><strong>No {provider.toUpperCase()} plans found</strong><span>Try another category or clear your search.</span></div>
        )}
      </div>
      {category !== "hot" && (
        <button
          type="button"
          className="plan-category-import"
          disabled={categoryImportBusy}
          onClick={() => void importActiveCategory()}
        >
          {categoryImportBusy ? <Loader2 className="spin" /> : <ArrowDownToLine />}
          {categoryImportBusy ? "Importing…" : `Import ${humanize(category)} · ${provider.toUpperCase()}`}
        </button>
      )}
      {editingPlan && (
        <div className="modal-overlay plan-edit-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) setEditingPlan(null); }}>
          <form className="plan-edit-modal" onSubmit={(event) => { event.preventDefault(); void saveEdit(); }}>
            <div className="plan-edit-header">
              <div><span>Edit catalogue item</span><h2>Edit Plan</h2></div>
              <button type="button" className="icon-button" aria-label="Close" onClick={() => setEditingPlan(null)}><X /></button>
            </div>
            <div className="plan-edit-body">
              <label><span>Short name <small>Shown in the plan grid</small></span><textarea rows={2} value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></label>
              <label><span>Detail name <small>Shown on the confirmation screen</small></span><textarea rows={3} value={editForm.detailName} onChange={(e) => setEditForm({ ...editForm, detailName: e.target.value })} /></label>
              <div className="plan-edit-row">
                <label><span>Your price (₦)</span><input inputMode="decimal" value={editForm.price} onChange={(e) => setEditForm({ ...editForm, price: e.target.value })} /></label>
                <label><span>Order <small>Lower appears first</small></span><input type="number" value={editForm.order} onChange={(e) => setEditForm({ ...editForm, order: e.target.value })} /></label>
              </div>
              <label><span>Category</span><select value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}>{categories.map((item) => <option key={item} value={item}>{humanize(item)}</option>)}</select></label>
              <label className="plan-hot-control"><span className="plan-hot-icon">🔥</span><span><b>Tag as Hot Deal</b><small>Show this plan in Hot Deals as well as its main category.</small></span><button type="button" className={`plan-switch ${editForm.isHot ? "on" : ""}`} aria-pressed={editForm.isHot} onClick={() => setEditForm({ ...editForm, isHot: !editForm.isHot })}><i /></button></label>
            </div>
            <div className="plan-edit-footer"><button type="button" onClick={() => setEditingPlan(null)}>Cancel</button><button className="primary" disabled={busy}>{busy ? "Saving…" : "Save changes"}</button></div>
          </form>
        </div>
      )}
      {combineOpen && (
        <div className="modal-overlay plan-edit-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) setCombineOpen(false); }}>
          <form className="plan-edit-modal combined-plan-modal" onSubmit={(event) => { event.preventDefault(); void saveCombinedPlan(); }}>
            <div className="plan-edit-header"><div><span>Multi-provider bundle</span><h2>Join Data Plans</h2></div><button type="button" className="icon-button" aria-label="Close" onClick={() => setCombineOpen(false)}><X /></button></div>
            <div className="plan-edit-body">
              <p className="plan-import-help">Create one {network.toUpperCase()} product using plans from any supported provider. The new combined plan will be saved under {humanize(combineForm.category)}; components are submitted in order and every provider response is retained.</p>
              <label><span>Short name <small>Shown in the plan grid</small></span><textarea rows={2} autoFocus value={combineForm.name} onChange={(event) => setCombineForm({ ...combineForm, name: event.target.value })} placeholder="64GB 30 Days" /></label>
              <label><span>Detail name <small>Shown on the confirmation screen</small></span><textarea rows={3} value={combineForm.detailName} onChange={(event) => setCombineForm({ ...combineForm, detailName: event.target.value })} placeholder="Glo Data - ₦20,000 - 64GB - 30 Days" /></label>
              <div className="plan-edit-row">
                <label><span>Your price (₦)</span><input inputMode="decimal" value={combineForm.price} onChange={(event) => setCombineForm({ ...combineForm, price: event.target.value })} /></label>
                <label><span>Order <small>Lower appears first</small></span><input type="number" value={combineForm.order} onChange={(event) => setCombineForm({ ...combineForm, order: event.target.value })} /></label>
              </div>
              <label><span>Category</span><select value={combineForm.category} onChange={(event) => setCombineForm({ ...combineForm, category: event.target.value })}>{categories.filter((item) => item !== "hot").map((item) => <option key={item} value={item}>{humanize(item)}</option>)}</select></label>
              <div className="combined-components"><span>Providers and component plans <small>Choose a provider and plan for each component. You can repeat the same provider or the same plan two or more times; execution follows this order.</small></span>
                {combineLoading ? <div className="combined-loading"><Loader2 className="spin" /> Loading enabled plans…</div> : combineComponents.map((component, index) => (
                  <div className="combined-component-row" key={index}>
                    <b>{index + 1}</b>
                    <select aria-label={`Provider ${index + 1}`} value={component.provider} onChange={(event) => { const next = [...combineComponents]; next[index] = { provider: event.target.value, planKey: "" }; setCombineComponents(next); }}>
                      <option value="">Choose provider</option>
                      {combinableProviders.map((candidateProvider) => <option key={candidateProvider} value={candidateProvider}>{candidateProvider === "vtpass" ? "VTpass" : candidateProvider === "lizzysub" ? "LizzySub" : candidateProvider === "datareloaded" ? "DataReloaded" : candidateProvider === "paypoint" ? "PayPoint" : "GladTidings"}</option>)}
                    </select>
                    <select aria-label={`Plan ${index + 1}`} disabled={!component.provider} value={component.planKey} onChange={(event) => { const next = [...combineComponents]; next[index] = { ...next[index], planKey: event.target.value }; setCombineComponents(next); }}>
                      <option value="">Choose plan</option>
                      {combineCandidates.filter((item) => String(item.provider).toLowerCase() === component.provider).map((item) => { const key = `${String(item.provider).toLowerCase()}:${String(item._sourceCategory)}:${String(item.id)}`; return <option key={key} value={key}>{String(item.name)} · {money.format(Number(item.amount ?? 0))}{item.enabled === true ? "" : " · Disabled"}</option>; })}
                    </select>
                    {combineComponents.length > 2 && <button type="button" className="danger" aria-label={`Remove component ${index + 1}`} onClick={() => setCombineComponents(combineComponents.filter((_, itemIndex) => itemIndex !== index))}><X /></button>}
                  </div>
                ))}
                <button type="button" className="combined-add" onClick={() => setCombineComponents([...combineComponents, { provider: "", planKey: "" }])}><Link2 /> Add another provider / plan</button>
              </div>
              <label className="plan-hot-control"><span className="plan-hot-icon">🔥</span><span><b>Tag as Hot Deal</b><small>Also show this joined plan under Hot Deals.</small></span><button type="button" className={`plan-switch ${combineForm.isHot ? "on" : ""}`} onClick={() => setCombineForm({ ...combineForm, isHot: !combineForm.isHot })}><i /></button></label>
            </div>
            <div className="plan-edit-footer"><button type="button" disabled={busy} onClick={() => setCombineOpen(false)}>Cancel</button><button className="primary" disabled={busy || combineLoading}>{busy ? "Creating…" : "Create combined plan"}</button></div>
          </form>
        </div>
      )}
      {importOpen && (
        <div className="modal-overlay plan-edit-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) setImportOpen(false); }}>
          <form className="plan-edit-modal plan-import-modal" onSubmit={(event) => { event.preventDefault(); void (provider === "paypoint" ? importPayPointPlans() : provider === "misvtu" ? importMisvtuPlans() : importLizzysubPlans()); }}>
            <div className="plan-edit-header"><div><span>Catalogue import</span><h2>Paste {provider === "paypoint" ? "PayPoint" : provider === "misvtu" ? "MISVTU" : "LizzySub"} Plans</h2></div><button type="button" className="icon-button" aria-label="Close" onClick={() => setImportOpen(false)}><X /></button></div>
            <div className="plan-edit-body">
              <p className="plan-import-help">{provider === "paypoint" ? "Copy and paste the PayPoint pricing table containing Plan Size, Plan_id, API Price and Validity." : provider === "misvtu" ? "Copy and paste the MISVTU Data Plans table containing Plan ID, Network, Plan Type, Plan Name, Amount and Validity." : <>Paste the plan list directly from LizzySub or in the same format as <code>lizzysub.dart</code>. IDs may be on their own line or on the same line as the plan.</>} Existing enabled states, selling prices, names, categories, ordering and Hot Deal settings are preserved; provider costs are refreshed and new plans are added disabled for review.</p>
              <label><span>Plan list <small>One plan per line, including plan code, network and provider price</small></span><textarea className="plan-import-textarea" rows={14} autoFocus value={importText} onChange={(event) => setImportText(event.target.value)} placeholder={provider === "paypoint" ? "1GB    1GB_MTN-SME    ₦450.0    (30 Days)" : provider === "misvtu" ? "1    MTN    COOPERATE GIFTING    1GB    ₦380.00    1-Week" : "1 MTN COOPERATE_GIFTING 1.0GB ₦776.00 1 week"} /></label>
            </div>
            <div className="plan-edit-footer"><button type="button" disabled={busy} onClick={() => setImportOpen(false)}>Cancel</button><button className="primary" disabled={busy || !importText.trim()}>{busy ? (importProgress || "Importing…") : "Import plans"}</button></div>
          </form>
        </div>
      )}
    </section>
  );
}

function ComingParity({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="panel">
      <div className="panel-title">
        <div>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
      </div>
      <div className="empty parity">
        <Settings2 />
        <strong>Parity implementation in progress</strong>
        <span>
          The live Flutter admin remains the source of truth for this section.
        </span>
      </div>
    </div>
  );
}
function humanize(value: string) {
  return value
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
function displayValue(value: unknown, key: string) {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (key.toLowerCase().includes("amount") && typeof value === "number")
    return naira(value);
  if (key.toLowerCase().includes("at") && typeof value === "number")
    return date.format(value);
  return String(value);
}
function csvCell(value: unknown) {
  const text = String(value ?? "");
  return `"${text.replace(/"/g, '""')}"`;
}

export default App;
